import React, { useState, useEffect } from "react";
import { CommissionAuditDetails, AUDIT_ACTION_LABELS } from "../../components/admin/CommissionAuditDetails";
import axios from "axios";
import { PaginationControls } from "../../components/ui/PaginationControls";
import { formatMsk, formatMskDate } from "../../utils/timeUtils";
import { formatRub } from "../../utils/money";
import { B2BAdminTab } from "./B2BAdminTab";
import { RefundsSummaryBar, AutoRefundOnExpiryToggle } from "./RefundsSummaryBar";
import { ExpiredBadge } from "../../components/task/DeadlineControls";
import { LeadsAdminTab } from "./LeadsAdminTab";
import { BUSINESS_STATUS_LABELS } from "../../components/b2b/B2BBadges";

// Вынесены за пределы AdminPanel чтобы React не перемонтировал их при каждом рендере
// (компонент, определённый внутри родителя, теряет DOM-фокус у input при каждом рендере)
const AdminSearchSort = ({ tab, placeholder, sortOptions, testPrefix, tabState, setSearch, setSort }) => {
  const state = tabState[tab] || { search: '', sort: 'date_desc' };
  return (
    <div className="flex flex-col sm:flex-row gap-2 mb-4">
      <input
        type="text"
        placeholder={placeholder}
        value={state.search || ''}
        onChange={e => setSearch(tab, e.target.value)}
        className="text-sm border border-gray-300 rounded px-3 py-1.5 flex-1"
        data-testid={`${testPrefix}-search-input`}
        style={{ fontFamily: 'IBM Plex Sans,sans-serif' }}
      />
      <select
        value={state.sort}
        onChange={e => setSort(tab, e.target.value)}
        className="text-sm border border-gray-300 rounded px-3 py-1.5"
        data-testid={`${testPrefix}-sort-select`}
        style={{ fontFamily: 'IBM Plex Sans,sans-serif' }}
      >
        {sortOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
};
const AdminPager = ({ tab, total, tabState, setPage, setPerPage }) => {
  const state = tabState[tab] || { page: 1, perPage: 10 };
  return (
    <PaginationControls
      total={total}
      perPage={state.perPage}
      currentPage={state.page}
      onPageChange={p => setPage(tab, p)}
      onPerPageChange={pp => setPerPage(tab, pp)}
      pageOptions={[10, 20, 50]}
    />
  );
};

const AdminDateRangeFilter = ({ tab, tabState, setDateFrom, setDateTo }) => {
  const state = tabState[tab] || { dateFrom: '', dateTo: '' };
  const setThisMonth = () => {
    const now = new Date();
    const from = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
    const to   = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);
    setDateFrom(tab, from);
    setDateTo(tab, to);
  };
  const setLast30 = () => {
    const to   = new Date().toISOString().slice(0, 10);
    const from = new Date(Date.now() - 29 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
    setDateFrom(tab, from);
    setDateTo(tab, to);
  };
  const reset = () => { setDateFrom(tab, ''); setDateTo(tab, ''); };
  const hasFilter = state.dateFrom || state.dateTo;
  return (
    <div className="flex flex-wrap items-center gap-2 mb-3" style={{fontFamily:'IBM Plex Sans,sans-serif'}}>
      <span className="text-sm whitespace-nowrap" style={{color:'var(--rv-text-muted)'}}>Период:</span>
      <input
        type="date"
        value={state.dateFrom || ''}
        onChange={e => setDateFrom(tab, e.target.value)}
        className="text-sm border border-gray-300 rounded px-2 py-1"
        data-testid={`${tab}-date-from`}
      />
      <span className="text-sm" style={{color:'var(--rv-text-muted)'}}>—</span>
      <input
        type="date"
        value={state.dateTo || ''}
        onChange={e => setDateTo(tab, e.target.value)}
        className="text-sm border border-gray-300 rounded px-2 py-1"
        data-testid={`${tab}-date-to`}
      />
      <button
        onClick={setThisMonth}
        className="text-xs px-2.5 py-1 rounded"
        style={{background:'rgba(13,148,136,0.07)', color:'var(--rv-teal)', border:'1px solid rgba(13,148,136,0.2)', cursor:'pointer'}}
        data-testid={`${tab}-preset-this-month`}
      >
        Этот месяц
      </button>
      <button
        onClick={setLast30}
        className="text-xs px-2.5 py-1 rounded"
        style={{background:'rgba(13,148,136,0.07)', color:'var(--rv-teal)', border:'1px solid rgba(13,148,136,0.2)', cursor:'pointer'}}
        data-testid={`${tab}-preset-last-30`}
      >
        Последние 30 дней
      </button>
      {hasFilter && (
        <button
          onClick={reset}
          className="text-xs px-2.5 py-1 rounded"
          style={{background:'rgba(239,68,68,0.06)', color:'#ef4444', border:'1px solid rgba(239,68,68,0.2)', cursor:'pointer'}}
          data-testid={`${tab}-date-reset`}
        >
          Сбросить
        </button>
      )}
    </div>
  );
};

// ---------- CSV Export utility ----------
const downloadCSV = (rows, columns, filename) => {
  if (!rows || rows.length === 0) return;
  const header = columns.map(c => `"${c.label}"`).join(',');
  const body = rows.map(row =>
    columns.map(c => {
      const val = c.get(row);
      const str = val == null ? '' : String(val);
      return `"${str.replace(/"/g, '""')}"`;
    }).join(',')
  ).join('\n');
  const csv = '\uFEFF' + header + '\n' + body;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const REFUND_TYPE_LABELS = {
  main: 'Основной платёж',
  surcharge: 'Доплата',
  price_decrease: 'Возврат разницы (понижение цены)',
  price_difference: 'Разница цены (понижение, выполнен)',
};

const PAYMENT_STATUS_LABELS = {
  escrowed: 'Оплачен (в эскроу)',
  refunded: 'Возвращён',
  pending: 'Ожидает оплаты',
  succeeded: 'Оплачен',
  canceled: 'Отменён',
  paid: 'Оплачен',
};

const REFUND_STATUS_LABELS = {
  pending: 'В обработке',
  succeeded: 'Возвращено',
  failed: 'Сбой',
  canceled: 'Отменён',
  manual_resolved: 'Возвращено вручную',
};

const REFUND_STATUS_STYLES = {
  pending: { background: 'rgba(217,119,6,0.1)', color: '#d97706' },
  succeeded: { background: 'rgba(16,185,129,0.1)', color: '#10b981' },
  failed: { background: 'rgba(239,68,68,0.1)', color: '#ef4444' },
  canceled: { background: 'rgba(107,114,128,0.1)', color: '#6b7280' },
  manual_resolved: { background: 'rgba(13,148,136,0.1)', color: '#0d9488' },
};

const USER_ROLE_LABELS = { customer: 'Заказчик', executor: 'Исполнитель', admin: 'Админ' };

const CSV_COLUMNS = {
  users: [
    { label: 'Имя',                get: r => r.name },
    { label: 'Email',              get: r => r.email },
    { label: 'Телефон',            get: r => r.phone },
    { label: 'Роль',               get: r => USER_ROLE_LABELS[r.role] || r.role },
    { label: 'Дата регистрации',   get: r => r.created_at ? formatMsk(r.created_at) : '' },
    { label: 'Последний вход',     get: r => r.last_login_at ? formatMsk(r.last_login_at) : '' },
    { label: 'Количество входов',  get: r => r.login_count ?? 0 },
    { label: 'Email подтверждён',  get: r => r.email_verified ? 'Да' : 'Нет' },
    { label: 'Личность подтверждена', get: r => r.identity_verified ? 'Да' : 'Нет' },
    { label: 'Проверенный исполнитель', get: r => r.is_verified_executor ? 'Да' : 'Нет' },
    { label: 'Рейтинг',            get: r => r.rating != null ? r.rating : '' },
    { label: 'Заданий',            get: r => r.total_tasks_count ?? r.total_tasks ?? 0 },
    { label: 'Отмен по сроку',     get: r => r.expired_cancellations ?? 0 },
    { label: 'Источник (utm_source)',   get: r => r.reg_utm?.utm_source || '' },
    { label: 'Кампания (utm_campaign)', get: r => r.reg_utm?.utm_campaign || '' },
    { label: 'Заблокирован',       get: r => r.is_blocked ? 'Да' : 'Нет' },
  ],
  payments: [
    { label: 'Задание',            get: r => r.task_title },
    { label: 'Заказчик',           get: r => r.customer_email },
    { label: 'Исполнитель',        get: r => r.executor_email },
    { label: 'Сумма (руб)',        get: r => r.amount },
    { label: 'Статус',             get: r => r.status === 'escrowed' ? 'Удерживается' : r.status === 'completed' ? 'Завершён' : 'Отменён' },
    { label: 'Дата создания',      get: r => r.created_at ? formatMsk(r.created_at) : '' },
    { label: 'Дата обработки',     get: r => r.processed_at ? formatMsk(r.processed_at) : '' },
  ],
  npd: [
    { label: 'ID чека',            get: r => r.receipt_id },
    { label: 'Сумма (руб)',        get: r => r.amount },
    { label: 'Услуга',             get: r => r.service_description },
    { label: 'ИНН платформы',      get: r => r.platform_inn },
    { label: 'Задание',            get: r => r.task_info?.title },
    { label: 'Тип услуги',         get: r => r.task_info?.service_type },
    { label: 'Статус',             get: r => r.status },
    { label: 'Тестовый',           get: r => r.is_mock ? 'Да' : 'Нет' },
    { label: 'Дата',               get: r => r.created_at ? formatMsk(r.created_at) : '' },
  ],
  payouts: [
    { label: 'Задание',            get: r => r.task_title },
    { label: 'Исполнитель',        get: r => r.executor_name },
    { label: 'Email',              get: r => r.executor_email },
    { label: 'Телефон',            get: r => r.executor_phone },
    { label: 'СБП',                get: r => r.executor_sbp_phone },
    { label: 'К выплате (руб)',    get: r => r.amount },
    { label: 'Комиссия (руб)',     get: r => r.commission_amount },
    { label: 'Ставка (%)',         get: r => r.commission_rate != null ? (r.commission_rate * 100).toFixed(0) : '' },
    { label: 'Дата подтверждения', get: r => r.confirmed_at ? formatMsk(r.confirmed_at) : '' },
  ],
  payoutHistory: [
    { label: 'Задание',            get: r => r.task_title },
    { label: 'Исполнитель',        get: r => r.executor_name },
    { label: 'Email',              get: r => r.executor_email },
    { label: 'К выплате (руб)',    get: r => r.amount },
    { label: 'Метод выплаты',      get: r => r.payment_method === 'sbp' ? 'СБП' : r.payment_method === 'card' ? 'Карта' : 'Другое' },
    { label: 'Дата выплаты',       get: r => r.paid_manually_at ? formatMsk(r.paid_manually_at) : '' },
    { label: 'Администратор',      get: r => r.paid_by_admin },
    { label: 'Заметки',            get: r => r.admin_notes },
  ],
  disputes: [
    { label: 'Задание',            get: r => r.task_title },
    { label: 'Инициатор',          get: r => r.initiator_email },
    { label: 'Заказчик',           get: r => r.customer_email },
    { label: 'Исполнитель',        get: r => r.executor_email },
    { label: 'Причина',            get: r => r.reason },
    { label: 'Описание',           get: r => r.description },
    { label: 'Статус',             get: r => r.status },
    { label: 'Сумма задания (руб)',get: r => r.task_price },
    { label: 'Дата создания',      get: r => r.created_at ? formatMsk(r.created_at) : '' },
  ],
  refunds: [
    { label: 'Задание',            get: r => r.task_title },
    { label: 'Заказчик',           get: r => r.customer_email },
    { label: 'Сумма (руб)',        get: r => r.amount },
    { label: 'Тип',                get: r => REFUND_TYPE_LABELS[r.refund_type] || 'Основной платёж' },
    { label: 'Статус',             get: r => REFUND_STATUS_LABELS[r.status] || r.status },
    { label: 'ID платежа YooKassa',get: r => r.yukassa_payment_id },
    { label: 'ID возврата',        get: r => r.yukassa_refund_id },
    { label: 'Ошибка',             get: r => r.error_message },
    { label: 'Тестовый режим',     get: r => r.mock_mode ? 'Да' : 'Нет' },
    { label: 'Дата',               get: r => r.created_at ? formatMsk(r.created_at) : '' },
  ],
  complaints: [
    { label: 'От (email)',             get: r => r.initiator_email },
    { label: 'На (email)',             get: r => r.target_email },
    { label: 'Задание',                get: r => r.task_title },
    { label: 'Описание',               get: r => r.description },
    { label: 'Статус',                 get: r => r.complaint_status },
    { label: 'Дата',                   get: r => r.created_at ? formatMsk(r.created_at) : '' },
    { label: 'Заметки администратора', get: r => r.admin_notes },
  ],
};

import {
  ChevronDown, FileText, Settings, Trash2, Archive, Coins, BarChart2,
  CreditCard, User, CheckCircle2, XCircle, Folder, Link, Download,
  Bell, Lock, Phone, Building2, Scale, AlertTriangle, Eye, RefreshCw,
  Video, Paperclip, Search, Ban, RotateCcw, Clock, Users, Flag,
  ShieldCheck, Wallet, Receipt, ListChecks, MessageSquare, ChevronRight, ClipboardList, Sparkles,
  Mail, Send, Image as ImageIcon, X as XIcon, BadgeCheck
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AI_DISPUTE_ACTION_LABELS = {
  release_to_executor: 'В пользу исполнителя',
  refund_to_customer: 'В пользу заказчика',
  rework: 'Отправить на доработку',
  need_more_info: 'Недостаточно данных — запросить уточнение',
};
const AI_DISPUTE_CONFIDENCE_LABELS = { high: 'Высокая', medium: 'Средняя', low: 'Низкая' };
const AI_DISPUTE_CONFIDENCE_COLORS = { high: '#10b981', medium: '#d97706', low: '#6b7280' };

export const AdminMediaFilesSection = ({ taskId, fileCount, showToast }) => {
  const [mediaFiles, setMediaFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const loadMediaFiles = async () => {
    if (loading) return;
    
    setLoading(true);
    try {
      const loadedFiles = [];
      for (let i = 0; i < fileCount; i++) {
        try {
          const response = await axios.get(`${API}/admin/tasks/${taskId}/files/${i}`);
          if (response.data) {
            loadedFiles.push({
              index: i,
              ...response.data
            });
          }
        } catch (error) {
          console.error(`Error loading file ${i}:`, error);
          loadedFiles.push({
            index: i,
            error: true,
            filename: `Файл ${i + 1}`,
            content_type: 'unknown'
          });
        }
      }
      setMediaFiles(loadedFiles);
    } catch (error) {
      console.error('Error loading media files:', error);
      showToast('Ошибка загрузки медиафайлов', 'error');
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = (fileData) => {
    if (fileData.error) {
      showToast('Файл недоступен для скачивания', 'error');
      return;
    }

    try {
      const byteCharacters = atob(fileData.content);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: fileData.content_type });
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileData.filename || `файл_${fileData.index + 1}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      showToast('Файл скачан', 'success');
    } catch (error) {
      console.error('Error downloading file:', error);
      showToast('Ошибка при скачивании файла', 'error');
    }
  };

  const renderMediaPreview = (fileData) => {
    if (fileData.error) {
      return (
        <div className="w-full h-32 rounded-md flex items-center justify-center" style={{background:'rgba(239,68,68,0.06)', border:'1px solid rgba(239,68,68,0.2)'}}>
          <span className="text-sm flex items-center gap-1" style={{color:'#ef4444'}}><AlertTriangle size={14}/>Ошибка загрузки</span>
        </div>
      );
    }

    const isImage = fileData.content_type && fileData.content_type.startsWith('image/');
    const isVideo = fileData.content_type && fileData.content_type.startsWith('video/');

    if (isImage && fileData.content) {
      return (
        <div className="relative group">
          <img
            src={`data:${fileData.content_type};base64,${fileData.content}`}
            alt={fileData.filename}
            className="w-full h-32 object-cover rounded-md cursor-pointer"
            onClick={() => setSelectedFile(fileData)}
          />
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 rounded-md flex items-center justify-center">
            <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center gap-1 text-sm">
              <Search size={14}/>Увеличить
            </span>
          </div>
        </div>
      );
    } else if (isVideo) {
      return (
        <div className="w-full h-32 rounded-md flex flex-col items-center justify-center" style={{background:'rgba(13,148,136,0.05)'}}>
          <Video size={28} style={{color:'var(--rv-teal)', marginBottom:4}}/>
          <span className="text-xs text-center" style={{color:'var(--rv-text-muted)'}}>
            Видеофайл<br/>{fileData.filename}
          </span>
        </div>
      );
    } else {
      return (
        <div className="w-full h-32 rounded-md flex flex-col items-center justify-center" style={{background:'rgba(13,148,136,0.04)'}}>
          <Paperclip size={28} style={{color:'var(--rv-text-muted)', marginBottom:4}}/>
          <span className="text-xs text-center" style={{color:'var(--rv-text-muted)'}}>
            {fileData.filename}<br/>
            {fileData.content_type || 'Неизвестный тип'}
          </span>
        </div>
      );
    }
  };

  return (
    <div className="reversia-card p-4">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-base font-semibold flex items-center gap-2" style={{fontFamily:'Manrope,sans-serif', color:'var(--rv-text-primary)'}}>
          <Folder size={16} style={{color:'var(--rv-teal)'}}/>Медиафайлы результата ({fileCount})
        </h3>
        {!loading && mediaFiles.length === 0 && (
          <button
            onClick={loadMediaFiles}
            className="flex items-center gap-1 px-3 py-1 rounded-md text-sm"
            style={{background:'var(--rv-teal)', color:'white', border:'none', cursor:'pointer', fontFamily:'IBM Plex Sans,sans-serif'}}
          >
            <Download size={14}/>Загрузить файлы
          </button>
        )}
      </div>
      
      {loading && (
        <div className="text-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 mx-auto" style={{borderColor:'var(--rv-teal)'}}></div>
          <p className="text-sm mt-2" style={{color:'var(--rv-text-muted)'}}>Загрузка файлов...</p>
        </div>
      )}

      {mediaFiles.length > 0 && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {mediaFiles.map((fileData) => (
              <div key={fileData.index} className="bg-white p-2 rounded-lg border">
                {renderMediaPreview(fileData)}
                <div className="mt-2 space-y-1">
                  <div className="text-xs text-gray-600 truncate">
                    {fileData.filename || `Файл ${fileData.index + 1}`}
                  </div>
                  <div className="text-xs text-gray-500">
                    {fileData.file_size ? `${Math.round(fileData.file_size / 1024)} KB` : 'Размер неизвестен'}
                  </div>
                  <button
                    onClick={() => downloadFile(fileData)}
                    className="w-full flex items-center justify-center gap-1 px-2 py-1 rounded text-xs"
                    style={{background:'var(--rv-teal)', color:'white', border:'none', cursor:'pointer'}}
                    disabled={fileData.error}
                  >
                    <Download size={12}/>Скачать
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* File Modal - ПОЛНОЭКРАННЫЙ ПРОСМОТР */}
      {selectedFile && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedFile(null)}
        >
          <div
            className="relative max-w-3xl w-full max-h-[90vh] flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex justify-between items-center bg-white bg-opacity-10 rounded-t-lg px-4 py-2">
              <h3 className="text-white text-sm font-medium truncate flex-1 mr-2">{selectedFile.filename || `Файл ${selectedFile.index + 1}`}</h3>
              <button
                onClick={() => setSelectedFile(null)}
                className="text-white hover:text-gray-300 text-2xl font-bold leading-none w-8 h-8 flex items-center justify-center flex-shrink-0"
              >
                ×
              </button>
            </div>
            <div className="flex items-center justify-center overflow-hidden rounded-b-lg bg-black">
              <img
                src={`data:${selectedFile.content_type};base64,${selectedFile.content}`}
                alt={selectedFile.filename}
                className="max-w-full max-h-[80vh] object-contain"
                draggable={false}
              />
            </div>
            <p className="text-center text-gray-400 text-xs mt-2">Нажмите за пределами фото для закрытия</p>
          </div>
        </div>
      )}
    </div>
  );
};

// Admin Archived Media Files Section Component (files from previous executor before reopen)
export const AdminArchivedMediaFilesSection = ({ taskId, fileCount, showToast }) => {
  const [mediaFiles, setMediaFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [collapsed, setCollapsed] = useState(true);

  const loadMediaFiles = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const loadedFiles = [];
      for (let i = 0; i < fileCount; i++) {
        try {
          const response = await axios.get(`${API}/admin/tasks/${taskId}/archived-files/${i}`);
          if (response.data) {
            loadedFiles.push({ index: i, ...response.data });
          }
        } catch (error) {
          loadedFiles.push({ index: i, error: true, filename: `Файл ${i + 1}`, content_type: 'unknown' });
        }
      }
      setMediaFiles(loadedFiles);
    } catch (error) {
      showToast('Ошибка загрузки архивных файлов', 'error');
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = (fileData) => {
    if (fileData.error) { showToast('Файл недоступен для скачивания', 'error'); return; }
    try {
      const byteCharacters = atob(fileData.content);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) byteNumbers[i] = byteCharacters.charCodeAt(i);
      const blob = new Blob([new Uint8Array(byteNumbers)], { type: fileData.content_type });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileData.filename || `archived_file_${fileData.index + 1}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showToast('Файл скачан', 'success');
    } catch (error) {
      showToast('Ошибка при скачивании файла', 'error');
    }
  };

  const renderPreview = (fileData) => {
    if (fileData.error) return (
      <div className="w-full h-32 bg-red-50 rounded-md flex items-center justify-center">
        <span className="text-red-600 text-sm">Ошибка загрузки</span>
      </div>
    );
    const isImage = fileData.content_type && fileData.content_type.startsWith('image/');
    if (isImage && fileData.content) return (
      <div className="relative group">
        <img
          src={`data:${fileData.content_type};base64,${fileData.content}`}
          alt={fileData.filename}
          className="w-full h-32 object-cover rounded-md cursor-pointer opacity-80"
          onClick={() => setSelectedFile(fileData)}
        />
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 rounded-md flex items-center justify-center">
          <span className="text-white opacity-0 group-hover:opacity-100 text-xs">Увеличить</span>
        </div>
      </div>
    );
    return (
      <div className="w-full h-32 bg-gray-100 rounded-md flex flex-col items-center justify-center">
      <div className="text-3xl mb-1">{fileData.content_type?.startsWith('video/') ? <Video size={28} style={{color:'var(--rv-teal)'}}/> : <Paperclip size={28} style={{color:'var(--rv-text-muted)'}}/>}</div>
        <span className="text-xs text-gray-600 text-center px-1">{fileData.filename}</span>
      </div>
    );
  };

  return (
    <div className="bg-gray-50 border border-gray-300 p-4 rounded-lg">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-700">
            Архив: файлы предыдущего исполнителя ({fileCount})
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Загружены до переоткрытия задания. Сохранены для аудита.
          </p>
        </div>
        <button
          onClick={() => {
            setCollapsed(v => !v);
            if (collapsed && mediaFiles.length === 0) loadMediaFiles();
          }}
          className="px-3 py-1 bg-gray-600 text-white rounded-md hover:bg-gray-700 text-sm flex-shrink-0"
        >
          {collapsed ? 'Показать' : 'Скрыть'}
        </button>
      </div>

      {!collapsed && (
        <div className="mt-3">
          {loading && (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-600 mx-auto"></div>
              <p className="text-gray-600 text-sm mt-2">Загрузка архивных файлов...</p>
            </div>
          )}
          {!loading && mediaFiles.length === 0 && (
            <button
              onClick={loadMediaFiles}
              className="px-3 py-1 bg-gray-500 text-white rounded-md hover:bg-gray-600 text-sm"
            >
              Загрузить файлы
            </button>
          )}
          {mediaFiles.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mt-2">
              {mediaFiles.map((fileData) => (
                <div key={fileData.index} className="bg-white p-2 rounded-lg border border-gray-200">
                  {renderPreview(fileData)}
                  <div className="mt-2 space-y-1">
                    <div className="text-xs text-gray-600 truncate">
                      {fileData.filename || `Файл ${fileData.index + 1}`}
                    </div>
                    <div className="text-xs text-gray-500">
                      {fileData.file_size ? `${Math.round(fileData.file_size / 1024)} KB` : ''}
                    </div>
                    <button
                      onClick={() => downloadFile(fileData)}
                      className="w-full px-2 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-700"
                      disabled={fileData.error}
                    >
                      Скачать
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {selectedFile && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedFile(null)}
        >
          <div className="relative max-w-3xl w-full max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center bg-white bg-opacity-10 rounded-t-lg px-4 py-2">
              <h3 className="text-white text-sm font-medium truncate flex-1 mr-2">{selectedFile.filename || `Файл ${selectedFile.index + 1}`}</h3>
              <button onClick={() => setSelectedFile(null)} className="text-white hover:text-gray-300 text-2xl font-bold w-8 h-8 flex items-center justify-center flex-shrink-0">×</button>
            </div>
            <div className="flex items-center justify-center overflow-hidden rounded-b-lg bg-black">
              <img
                src={`data:${selectedFile.content_type};base64,${selectedFile.content}`}
                alt={selectedFile.filename}
                className="max-w-full max-h-[80vh] object-contain"
                draggable={false}
              />
            </div>
            <p className="text-center text-gray-400 text-xs mt-2">Нажмите за пределами фото для закрытия</p>
          </div>
        </div>
      )}
    </div>
  );
};

// Admin External Links Section Component  
export const AdminExternalLinksSection = ({ taskId, links, showToast }) => {
  const [loadedLinks, setLoadedLinks] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadExternalLinks = async () => {
    if (loading) return;
    
    setLoading(true);
    try {
      const linkPromises = links.map(async (link, index) => {
        try {
          const response = await axios.get(`${API}/admin/tasks/${taskId}/external-links/${index}`);
          return {
            index,
            ...response.data,
            original: link
          };
        } catch (error) {
          console.error(`Error loading link ${index}:`, error);
          return {
            index,
            error: true,
            title: link.title || 'Ссылка недоступна',
            url: '#',
            original: link
          };
        }
      });
      
      const results = await Promise.all(linkPromises);
      setLoadedLinks(results);
    } catch (error) {
      console.error('Error loading external links:', error);
      showToast('Ошибка загрузки внешних ссылок', 'error');
    } finally {
      setLoading(false);
    }
  };

  const openLink = (linkData) => {
    if (linkData.error || !linkData.url || linkData.url === '#') {
      showToast('Ссылка недоступна', 'error');
      return;
    }

    try {
      window.open(linkData.url, '_blank', 'noopener,noreferrer');
      showToast('Ссылка открыта в новой вкладке', 'success');
    } catch (error) {
      console.error('Error opening link:', error);
      showToast('Ошибка при открытии ссылки', 'error');
    }
  };

  return (
    <div className="reversia-card p-4">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-base font-semibold flex items-center gap-2" style={{fontFamily:'Manrope,sans-serif', color:'var(--rv-text-primary)'}}>
          <Link size={16} style={{color:'var(--rv-teal)'}}/>Внешние ссылки ({links.length})
        </h3>
        {!loading && loadedLinks.length === 0 && (
          <button
            onClick={loadExternalLinks}
            className="flex items-center gap-1 px-3 py-1 rounded-md text-sm"
            style={{background:'var(--rv-teal)', color:'white', border:'none', cursor:'pointer', fontFamily:'IBM Plex Sans,sans-serif'}}
          >
            <Link size={14}/>Загрузить ссылки
          </button>
        )}
      </div>

      {loading && (
        <div className="text-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600 mx-auto"></div>
          <p className="text-teal-700 text-sm mt-2">Загрузка ссылок...</p>
        </div>
      )}

      {loadedLinks.length > 0 && (
        <div className="space-y-3">
          {loadedLinks.map((linkData) => (
            <div key={linkData.index} className="bg-white p-3 rounded-lg border border-teal-200">
              <div className="flex justify-between items-start">
                <div className="flex-grow min-w-0 mr-3">
                  <div className="font-medium text-teal-800 mb-1">
                    {linkData.title || linkData.original.title || 'Без названия'}
                  </div>
                  <div className="text-sm text-gray-600 mb-2">
                    {linkData.description || linkData.original.description || 'Описание отсутствует'}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {linkData.error ? 'Ссылка недоступна' : linkData.url}
                  </div>
                  {(linkData.added_at || linkData.original.added_at) && (
                    <div className="text-xs text-gray-400 mt-1">
                      Добавлено: {formatMsk(linkData.added_at || linkData.original.added_at)}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => openLink(linkData)}
                  className={`px-3 py-2 rounded-md text-sm flex-shrink-0 ${
                    linkData.error 
                      ? 'bg-gray-400 text-gray-600 cursor-not-allowed' 
                      : 'bg-teal-600 text-white hover:bg-teal-700'
                  }`}
                  disabled={linkData.error}
                >
                  {linkData.error ? <><AlertTriangle size={12}/>Недоступна</> : <><Link size={12}/>Открыть</>}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Admin Panel Component
export const AdminPanel = ({ user, showToast }) => {
  const [verificationRequests, setVerificationRequests] = useState([]);
  const [verificationsTotal, setVerificationsTotal] = useState(0);
  const [verificationsStatusCounts, setVerificationsStatusCounts] = useState({});
  const [identityVerifications, setIdentityVerifications] = useState([]);
  const [identityVerificationsTotal, setIdentityVerificationsTotal] = useState(0);
  const [identityPendingCount, setIdentityPendingCount] = useState(0);
  const [identityStatusFilter, setIdentityStatusFilter] = useState('pending');
  const [expandedIdentityVerifications, setExpandedIdentityVerifications] = useState(new Set());
  const [deletionRequests, setDeletionRequests] = useState([]);
  const [deletionsTotal, setDeletionsTotal] = useState(0);
  const [deletionsPendingCount, setDeletionsPendingCount] = useState(0);
  const [deletedUsersArchive, setDeletedUsersArchive] = useState([]);
  const [archiveTotal, setArchiveTotal] = useState(0);
  const [escrowPayments, setEscrowPayments] = useState([]);
  const [escrowTotal, setEscrowTotal] = useState(0);
  const [escrowedCount, setEscrowedCount] = useState(0);
  const [refunds, setRefunds] = useState([]);
  const [refundsTotal, setRefundsTotal] = useState(0);
  const [refundsFailedCount, setRefundsFailedCount] = useState(0);
  const [refundsPendingCount, setRefundsPendingCount] = useState(0);
  const [refundsSummary, setRefundsSummary] = useState(null);
  const [refundsStatusFilter, setRefundsStatusFilter] = useState('');
  const [refundsLoading, setRefundsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('verifications');
  const [expandedVerifications, setExpandedVerifications] = useState(new Set());
  const [verificationStatusFilter, setVerificationStatusFilter] = useState('all');
  const [taskDetailsModal, setTaskDetailsModal] = useState({ isOpen: false, taskId: null, details: null });
  const [taskDetailsLoading, setTaskDetailsLoading] = useState(false);
  const [npdReceipts, setNpdReceipts] = useState([]);
  const [npdTotal, setNpdTotal] = useState(0);
  const [npdReceiptsLoading, setNpdReceiptsLoading] = useState(false);
  
  // Новые state для выплат
  const [pendingPayouts, setPendingPayouts] = useState([]);
  const [pendingPayoutsTotal, setPendingPayoutsTotal] = useState(0);
  const [pendingPayoutsAmount, setPendingPayoutsAmount] = useState(0);
  const [payoutHistory, setPayoutHistory] = useState({ manual_payouts: [], auto_payouts: [] });
  const [payoutHistoryTotal, setPayoutHistoryTotal] = useState(0);
  const [payoutsLoading, setPayoutsLoading] = useState(false);
  
  // State для статистики пользователей
  const [usersStats, setUsersStats] = useState({ users: {}, tasks: {} });
  const [usersStatsLoading, setUsersStatsLoading] = useState(false);
  const [adminAllTasks, setAdminAllTasks] = useState([]);
  const [adminTasksTotal, setAdminTasksTotal] = useState(0);
  const [adminTasksLoading, setAdminTasksLoading] = useState(false);
  const [adminTaskStatusFilter, setAdminTaskStatusFilter] = useState('');
  
  // State для настроек выплат
  const [payoutMode, setPayoutMode] = useState("manual");
  const [payoutModeLoading, setPayoutModeLoading] = useState(false);

  // State для настроек доступа к сайту (запуск)
  const [siteConfig, setSiteConfig] = useState({ site_gate_enabled: false, paid_tasks_enabled: false, ai_helper_enabled: true, ai_dispute_assistant_enabled: true, push_anonymize: true, public_stats_enabled: false, public_stats_cities: 1, public_stats_min_executors: 100 });
  const [siteConfigLoading, setSiteConfigLoading] = useState(false);

  // State для email-рассылки
  const [broadcastForm, setBroadcastForm] = useState({
    subject: '', body_text: '', banner_url: '', cta_text: '', cta_url: '',
    target: 'all', ignore_consent: false, ignoreConsentConfirmed: false,
  });
  const [broadcastPreview, setBroadcastPreview] = useState({ total: 0, customers: 0, executors: 0 });
  const [broadcastSending, setBroadcastSending] = useState(false);
  const [broadcastBannerUploading, setBroadcastBannerUploading] = useState(false);
  const [broadcastHistory, setBroadcastHistory] = useState([]);
  const [broadcastHistoryLoading, setBroadcastHistoryLoading] = useState(false);
  const [broadcastCustomUsers, setBroadcastCustomUsers] = useState([]);
  const [broadcastUserSearch, setBroadcastUserSearch] = useState('');
  const [broadcastUserSearchResults, setBroadcastUserSearchResults] = useState([]);
  const [aiUsage, setAiUsage] = useState(null);
  const [activeAnnouncement, setActiveAnnouncement] = useState(null);
  const [announceForm, setAnnounceForm] = useState({ audience: 'all', target_email: '', title: '', message: '' });
  const [announceLoading, setAnnounceLoading] = useState(false);
  const [liveExecutorsCount, setLiveExecutorsCount] = useState(null);
  const [citiesDraft, setCitiesDraft] = useState("1");
  const [minExecutorsDraft, setMinExecutorsDraft] = useState("100");

  // State для вкладки Споры
  const [adminDisputes, setAdminDisputes] = useState([]);
  const [disputesTotal, setDisputesTotal] = useState(0);
  const [disputesActiveCount, setDisputesActiveCount] = useState(0);
  const [adminDisputesLoading, setAdminDisputesLoading] = useState(false);
  const [clarificationForms, setClarificationForms] = useState({});
  const [disputeStatusFilter, setDisputeStatusFilter] = useState('active');
  const [aiAnalyzing, setAiAnalyzing] = useState({});

  // State для вкладки Флаги чата
  const [flaggedMessages, setFlaggedMessages] = useState([]);
  const [flagsTotal, setFlagsTotal] = useState(0);
  const [flagsPendingCount, setFlagsPendingCount] = useState(0);
  const [flaggedLoading, setFlaggedLoading] = useState(false);
  const [flagStatusFilter, setFlagStatusFilter] = useState('pending');
  const [dismissNotes, setDismissNotes] = useState({});

  // State для просмотра переписки задания (admin chat viewer)
  const [chatView, setChatView] = useState(null); // { taskId, taskTitle, messages, highlightMsgId }

  // State для вкладки Пользователи (запись #24 аудита)
  const [usersList, setUsersList] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersSearchQuery, setUsersSearchQuery] = useState('');
  const [usersBlockedFilter, setUsersBlockedFilter] = useState(''); // '', 'true', 'false'
  const [blockReasonsMap, setBlockReasonsMap] = useState({});
  const [blockModal, setBlockModal] = useState(null); // { user, reason, notes }
  const [usersRoleFilter, setUsersRoleFilter] = useState(''); // '', 'customer', 'executor', 'admin'
  const [usersSummary, setUsersSummary] = useState(null); // сводные счётчики по всей базе

  // State для вкладки Жалобы (запись #25 аудита)
  const [complaintsList, setComplaintsList] = useState([]);
  const [complaintsTotal, setComplaintsTotal] = useState(0);
  const [complaintsOpenCount, setComplaintsOpenCount] = useState(0);
  const [complaintsLoading, setComplaintsLoading] = useState(false);
  const [complaintsStatusFilter, setComplaintsStatusFilter] = useState('open');
  const [complaintBlockModal, setComplaintBlockModal] = useState(null); // { complaint, reason, notes }
  const [complaintCloseNotes, setComplaintCloseNotes] = useState({});

  // ====== Аудит #17: Управление администраторами ======
  const [adminsList, setAdminsList] = useState([]);
  const [adminsLoading, setAdminsLoading] = useState(false);
  const [adminGrantSearch, setAdminGrantSearch] = useState('');
  const [adminGrantResults, setAdminGrantResults] = useState([]);
  const [adminGrantSearching, setAdminGrantSearching] = useState(false);
  const [adminActionLoading, setAdminActionLoading] = useState(null); // user_id

  // ====== Аудит #35: Комиссии и проверенные исполнители ======
  const [commissionSettings, setCommissionSettings] = useState(null);
  const [commissionLoading, setCommissionLoading] = useState(false);
  const [commissionDraft, setCommissionDraft] = useState({});
  const [commissionDiscountDraft, setCommissionDiscountDraft] = useState('');
  const [commissionSaving, setCommissionSaving] = useState(false);
  const [commissionPromotions, setCommissionPromotions] = useState([]);
  const [newPromotion, setNewPromotion] = useState({
    name: '',
    service_types: [],
    rates: {},
    valid_from: '',
    valid_until: ''
  });
  const [commissionAuditLog, setCommissionAuditLog] = useState([]);
  const [commissionAuditTotal, setCommissionAuditTotal] = useState(0);
  const [auditLogFrom, setAuditLogFrom] = useState('');
  const [auditLogTo, setAuditLogTo] = useState('');
  const [verifiedExecutors, setVerifiedExecutors] = useState([]);
  const [verifiedExecutorsTotal, setVerifiedExecutorsTotal] = useState(0);
  const [verifiedExecutorsLoading, setVerifiedExecutorsLoading] = useState(false);
  const [verifiedSearch, setVerifiedSearch] = useState('');
  const [verifiedOnlyFilter, setVerifiedOnlyFilter] = useState(false);
  const [selectedExecutors, setSelectedExecutors] = useState(new Set());
  const [bulkVerifyNotes, setBulkVerifyNotes] = useState('');

  // ====== Pagination / Search / Sort state for admin tabs ======
  const [tabState, setTabState] = useState({
    verifications:    { page: 1, perPage: 10, search: '', sort: 'date_desc' },
    deletions:        { page: 1, perPage: 10, search: '', sort: 'date_desc' },
    archive:          { page: 1, perPage: 10, search: '', sort: 'date_desc' },
    payments:         { page: 1, perPage: 10, search: '', sort: 'date_desc', dateFrom: '', dateTo: '' },
    npd:              { page: 1, perPage: 10, search: '', sort: 'date_desc', dateFrom: '', dateTo: '' },
    payouts:          { page: 1, perPage: 10, search: '', sort: 'date_desc', dateFrom: '', dateTo: '' },
    payoutHistory:    { page: 1, perPage: 10, search: '', sort: 'date_desc', dateFrom: '', dateTo: '' },
    disputes:         { page: 1, perPage: 10, search: '', sort: 'date_desc', dateFrom: '', dateTo: '' },
    flags:            { page: 1, perPage: 10, search: '', sort: 'date_desc' },
    users:            { page: 1, perPage: 10, search: '', sort: 'date_desc', dateFrom: '', dateTo: '' },
    complaints:       { page: 1, perPage: 10, search: '', sort: 'date_desc', dateFrom: '', dateTo: '' },
    verifiedExecutors:{ page: 1, perPage: 10, search: '', sort: 'name_asc' },
    adminTasks:       { page: 1, perPage: 10, search: '', sort: 'date_desc' },
    identityVerifications: { page: 1, perPage: 10, search: '' },
    refunds:          { page: 1, perPage: 10, search: '', sort: 'date_desc', dateFrom: '', dateTo: '' },
    commissionAudit:  { page: 1, perPage: 20 },
  });
  const ts = (tab) => tabState[tab] || { page: 1, perPage: 10, search: '', sort: 'date_desc' };
  const setTSPage    = (tab, page)    => setTabState(prev => ({ ...prev, [tab]: { ...prev[tab], page } }));
  const setTSPerPage = (tab, perPage) => setTabState(prev => ({ ...prev, [tab]: { ...prev[tab], perPage, page: 1 } }));
  const setTSSearch  = (tab, search)  => setTabState(prev => ({ ...prev, [tab]: { ...prev[tab], search, page: 1 } }));
  const setTSSort    = (tab, sort)    => setTabState(prev => ({ ...prev, [tab]: { ...prev[tab], sort, page: 1 } }));
  const setTSDateFrom = (tab, dateFrom) => setTabState(prev => ({ ...prev, [tab]: { ...prev[tab], dateFrom, page: 1 } }));
  const setTSDateTo   = (tab, dateTo)   => setTabState(prev => ({ ...prev, [tab]: { ...prev[tab], dateTo,   page: 1 } }));
  // Экспорт CSV по ВСЕЙ выборке (с учётом текущих фильтров/поиска), а не только текущей странице —
  // повторно вызывает тот же серверный endpoint с большим limit.
  const exportCSV = async (url, extraParams, itemsKey, columns, filenamePrefix) => {
    try {
      const params = new URLSearchParams(extraParams);
      params.set('page', '1');
      params.set('limit', '5000');
      const res = await axios.get(`${API}${url}?${params.toString()}`);
      const items = itemsKey ? (res.data[itemsKey] || []) : (Array.isArray(res.data) ? res.data : []);
      if (items.length === 0) {
        showToast('Нет данных для экспорта', 'error');
        return;
      }
      downloadCSV(items, columns, `${filenamePrefix}_${new Date().toISOString().slice(0,10)}.csv`);
    } catch (error) {
      showToast('Ошибка экспорта CSV', 'error');
    }
  };

  const SERVICE_TYPE_OPTIONS = [
    { value: 'general', label: 'Общие услуги' },
    { value: 'secret_shopper', label: 'Тайный покупатель' },
    { value: 'grave_care', label: 'Мемориальный сервис' },
    { value: 'mobile_journalist', label: 'Мобильный корреспондент' },
  ];

  const fetchCommissionSettings = async () => {
    try {
      setCommissionLoading(true);
      const response = await axios.get(`${API}/admin/commission/settings`);
      setCommissionSettings(response.data);
      const draft = {};
      Object.entries(response.data.base_rates || {}).forEach(([k, v]) => {
        draft[k] = (Number(v) * 100).toFixed(2);
      });
      setCommissionDraft(draft);
      setCommissionDiscountDraft(((Number(response.data.verified_executor_discount) || 0) * 100).toFixed(2));
      setCommissionPromotions(response.data.active_promotions || []);
    } catch (error) {
      console.error('Error fetching commission settings:', error);
      showToast('Ошибка загрузки настроек комиссии', 'error');
    } finally {
      setCommissionLoading(false);
    }
  };

  const saveCommissionSettings = async () => {
    try {
      setCommissionSaving(true);
      const rates = {};
      for (const [k, v] of Object.entries(commissionDraft)) {
        const num = parseFloat(v);
        if (isNaN(num) || num < 1 || num > 50) {
          showToast(`Ставка для "${k}" должна быть от 1% до 50%`, 'error');
          setCommissionSaving(false);
          return;
        }
        rates[k] = num / 100;
      }
      const discount = parseFloat(commissionDiscountDraft);
      if (isNaN(discount) || discount < 0 || discount > 20) {
        showToast('Скидка проверенному должна быть от 0% до 20%', 'error');
        setCommissionSaving(false);
        return;
      }
      const response = await axios.put(`${API}/admin/commission/settings`, {
        rates,
        verified_executor_discount: discount / 100,
      });
      setCommissionSettings(response.data);
      setCommissionPromotions(response.data.active_promotions || []);
      showToast('Настройки комиссии сохранены', 'success');
    } catch (error) {
      console.error('Error saving commission settings:', error);
      showToast(error.response?.data?.detail || 'Ошибка сохранения', 'error');
    } finally {
      setCommissionSaving(false);
    }
  };

  const createCommissionPromotion = async () => {
    try {
      if (!newPromotion.name.trim()) {
        showToast('Укажите название акции', 'error');
        return;
      }
      if (newPromotion.service_types.length === 0) {
        showToast('Выберите хотя бы один тип услуг', 'error');
        return;
      }
      if (!newPromotion.valid_from || !newPromotion.valid_until) {
        showToast('Укажите период акции', 'error');
        return;
      }
      const rates = {};
      for (const st of newPromotion.service_types) {
        const raw = newPromotion.rates[st];
        const num = parseFloat(raw);
        if (isNaN(num) || num < 1 || num > 50) {
          showToast(`Ставка для "${st}" должна быть от 1% до 50%`, 'error');
          return;
        }
        rates[st] = num / 100;
      }
      await axios.post(`${API}/admin/commission/promotions`, {
        name: newPromotion.name.trim(),
        service_types: newPromotion.service_types,
        rates,
        valid_from: new Date(newPromotion.valid_from).toISOString(),
        valid_until: new Date(newPromotion.valid_until).toISOString(),
      });
      showToast('Акция создана', 'success');
      setNewPromotion({ name: '', service_types: [], rates: {}, valid_from: '', valid_until: '' });
      await fetchCommissionSettings();
    } catch (error) {
      console.error('Error creating promotion:', error);
      showToast(error.response?.data?.detail || 'Ошибка создания акции', 'error');
    }
  };

  const cancelPromotion = async (promoId) => {
    if (!window.confirm('Отменить акцию досрочно?')) return;
    try {
      await axios.delete(`${API}/admin/commission/promotions/${promoId}`);
      showToast('Акция отменена', 'success');
      await fetchCommissionSettings();
    } catch (error) {
      console.error('Error cancelling promotion:', error);
      showToast(error.response?.data?.detail || 'Ошибка отмены акции', 'error');
    }
  };

  const fetchCommissionAuditLog = async () => {
    try {
      const state = ts('commissionAudit');
      const params = new URLSearchParams();
      if (auditLogFrom) params.append('from', auditLogFrom);
      if (auditLogTo) params.append('to', auditLogTo);
      params.append('page', state.page);
      params.append('limit', state.perPage);
      const response = await axios.get(`${API}/admin/commission/audit-log?${params.toString()}`);
      setCommissionAuditLog(response.data.items || []);
      setCommissionAuditTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching audit log:', error);
      showToast('Ошибка загрузки журнала', 'error');
    }
  };

  const fetchVerifiedExecutors = async () => {
    try {
      setVerifiedExecutorsLoading(true);
      const state = ts('verifiedExecutors');
      const params = new URLSearchParams();
      if (verifiedSearch) params.append('q', verifiedSearch);
      if (verifiedOnlyFilter) params.append('only_verified', 'true');
      params.append('sort', state.sort);
      params.append('page', state.page);
      params.append('limit', state.perPage);
      const response = await axios.get(`${API}/admin/users/verified-executors?${params.toString()}`);
      setVerifiedExecutors(response.data.items || []);
      setVerifiedExecutorsTotal(response.data.total || 0);
      setSelectedExecutors(new Set());
    } catch (error) {
      console.error('Error fetching verified executors:', error);
      showToast('Ошибка загрузки исполнителей', 'error');
    } finally {
      setVerifiedExecutorsLoading(false);
    }
  };

  // Аудит #17: Управление администраторами
  const fetchAdmins = async () => {
    try {
      setAdminsLoading(true);
      const response = await axios.get(`${API}/admin/users/admins`);
      setAdminsList(response.data);
    } catch (error) {
      showToast('Ошибка загрузки списка администраторов', 'error');
    } finally {
      setAdminsLoading(false);
    }
  };

  const searchUsersForAdmin = async () => {
    if (!adminGrantSearch.trim()) return;
    try {
      setAdminGrantSearching(true);
      const response = await axios.get(`${API}/admin/users?q=${encodeURIComponent(adminGrantSearch)}&limit=10`);
      const usersArray = Array.isArray(response.data) ? response.data : (response.data.users || []);
      setAdminGrantResults(usersArray.filter(u => !u.is_admin));
    } catch (error) {
      showToast('Ошибка поиска пользователей', 'error');
    } finally {
      setAdminGrantSearching(false);
    }
  };

  const grantAdmin = async (userId) => {
    try {
      setAdminActionLoading(userId);
      await axios.post(`${API}/admin/users/${userId}/grant-admin`);
      showToast('Права администратора выданы', 'success');
      setAdminGrantResults(prev => prev.filter(u => u.id !== userId));
      await fetchAdmins();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Ошибка выдачи прав', 'error');
    } finally {
      setAdminActionLoading(null);
    }
  };

  const revokeAdmin = async (userId) => {
    try {
      setAdminActionLoading(userId);
      await axios.post(`${API}/admin/users/${userId}/revoke-admin`);
      showToast('Права администратора отозваны', 'success');
      await fetchAdmins();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Ошибка отзыва прав', 'error');
    } finally {
      setAdminActionLoading(null);
    }
  };

  const grantVerifiedExecutor = async (userId, notes = '') => {
    try {
      await axios.post(`${API}/admin/users/${userId}/verify-executor`, { notes });
      showToast('Бейдж присвоен', 'success');
      await fetchVerifiedExecutors();
    } catch (error) {
      console.error('Error granting badge:', error);
      showToast(error.response?.data?.detail || 'Ошибка присвоения', 'error');
    }
  };

  const revokeVerifiedExecutor = async (userId) => {
    if (!window.confirm('Снять бейдж "Проверенный исполнитель" у пользователя?')) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}/verify-executor`);
      showToast('Бейдж снят', 'success');
      await fetchVerifiedExecutors();
    } catch (error) {
      console.error('Error revoking badge:', error);
      showToast(error.response?.data?.detail || 'Ошибка снятия', 'error');
    }
  };

  const bulkVerifyExecutors = async () => {
    if (selectedExecutors.size === 0) {
      showToast('Никто не выбран', 'error');
      return;
    }
    if (!window.confirm(`Присвоить бейдж "Проверенный исполнитель" ${selectedExecutors.size} пользователям?`)) return;
    try {
      const response = await axios.post(`${API}/admin/users/bulk-verify-executors`, {
        user_ids: Array.from(selectedExecutors),
        notes: bulkVerifyNotes || null,
      });
      showToast(`Присвоено: ${response.data.verified_count}`, 'success');
      setBulkVerifyNotes('');
      await fetchVerifiedExecutors();
    } catch (error) {
      console.error('Error bulk verify:', error);
      showToast(error.response?.data?.detail || 'Ошибка массового присвоения', 'error');
    }
  };

  const toggleExecutorSelection = (userId) => {
    setSelectedExecutors(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId); else next.add(userId);
      return next;
    });
  };

  // Легковесные счётчики для бейджей вкладок — один запрос при монтировании, без загрузки списков
  useEffect(() => {
    axios.get(`${API}/admin/badge-counts`).then(res => {
      const d = res.data;
      setVerificationsStatusCounts(prev => ({ ...prev, pending: d.verifications_pending }));
      setDeletionsPendingCount(d.deletions_pending);
      setArchiveTotal(d.archive_total);
      setEscrowedCount(d.escrowed_count);
      setNpdTotal(d.npd_total);
      setPendingPayoutsTotal(d.pending_payouts_count);
      setPendingPayoutsAmount(d.pending_payouts_amount);
      setDisputesActiveCount(d.disputes_active);
      setFlagsPendingCount(d.flags_pending);
      setComplaintsOpenCount(d.complaints_open);
      setIdentityPendingCount(d.identity_pending);
      setRefundsFailedCount(d.refunds_failed || 0);
    }).catch(() => {});
  }, []);

  // ----- Email-рассылка -----
  useEffect(() => {
    if (activeTab !== 'broadcast') return;
    const idsParam = broadcastForm.target === 'custom' ? broadcastCustomUsers.map(u => u.id).join(',') : '';
    if (broadcastForm.target === 'custom' && broadcastCustomUsers.length === 0) {
      setBroadcastPreview({ total: 0, customers: 0, executors: 0 });
      return;
    }
    const params = new URLSearchParams();
    params.set('target', broadcastForm.target);
    params.set('ignore_consent', broadcastForm.ignore_consent ? 'true' : 'false');
    if (idsParam) params.set('user_ids', idsParam);
    axios.get(`${API}/admin/broadcast/recipients-preview?${params.toString()}`)
      .then(res => setBroadcastPreview(res.data))
      .catch(() => setBroadcastPreview({ total: 0, customers: 0, executors: 0 }));
  }, [activeTab, broadcastForm.target, broadcastForm.ignore_consent, broadcastCustomUsers]);

  const fetchBroadcastHistory = async () => {
    try {
      setBroadcastHistoryLoading(true);
      const res = await axios.get(`${API}/admin/broadcast/history?limit=50`);
      setBroadcastHistory(res.data.campaigns || []);
    } catch (error) {
      showToast(error.response?.data?.detail || 'Ошибка загрузки истории рассылок', 'error');
    } finally {
      setBroadcastHistoryLoading(false);
    }
  };

  const uploadBroadcastBanner = async (file) => {
    if (!file) return;
    try {
      setBroadcastBannerUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/admin/broadcast/upload-banner`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setBroadcastForm(f => ({ ...f, banner_url: res.data.banner_url }));
      showToast('Банер загружен', 'success');
    } catch (error) {
      showToast(error.response?.data?.detail || 'Ошибка загрузки банера', 'error');
    } finally {
      setBroadcastBannerUploading(false);
    }
  };

  const searchBroadcastUsers = async (q) => {
    setBroadcastUserSearch(q);
    if (!q || q.trim().length < 2) { setBroadcastUserSearchResults([]); return; }
    try {
      const res = await axios.get(`${API}/admin/users?q=${encodeURIComponent(q)}&limit=20`);
      setBroadcastUserSearchResults(res.data.users || []);
    } catch (error) {
      setBroadcastUserSearchResults([]);
    }
  };

  const toggleBroadcastCustomUser = (u) => {
    setBroadcastCustomUsers(prev => {
      const exists = prev.find(p => p.id === u.id);
      if (exists) return prev.filter(p => p.id !== u.id);
      return [...prev, u];
    });
  };

  const sendBroadcast = async () => {
    if (!broadcastForm.subject.trim() || !broadcastForm.body_text.trim()) {
      showToast('Заполните тему и текст письма', 'error');
      return;
    }
    if (broadcastForm.target === 'custom' && broadcastCustomUsers.length === 0) {
      showToast('Выберите хотя бы одного получателя', 'error');
      return;
    }
    if (broadcastForm.ignore_consent && !broadcastForm.ignoreConsentConfirmed) {
      showToast('Подтвердите, что понимаете риск отправки без согласия', 'error');
      return;
    }
    try {
      setBroadcastSending(true);
      const res = await axios.post(`${API}/admin/broadcast/send`, {
        subject: broadcastForm.subject.trim(),
        body_text: broadcastForm.body_text.trim(),
        banner_url: broadcastForm.banner_url || null,
        cta_text: broadcastForm.cta_text.trim() || null,
        cta_url: broadcastForm.cta_url.trim() || null,
        target: broadcastForm.target,
        user_ids: broadcastForm.target === 'custom' ? broadcastCustomUsers.map(u => u.id) : null,
        ignore_consent: broadcastForm.ignore_consent,
      });
      showToast(`Рассылка запущена: ${res.data.total_recipients} получателей`, 'success');
      setBroadcastForm({ subject: '', body_text: '', banner_url: '', cta_text: '', cta_url: '', target: 'all', ignore_consent: false, ignoreConsentConfirmed: false });
      setBroadcastCustomUsers([]);
      setBroadcastUserSearch('');
      setBroadcastUserSearchResults([]);
      fetchBroadcastHistory();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Ошибка отправки рассылки', 'error');
    } finally {
      setBroadcastSending(false);
    }
  };

  const fetchVerificationRequests = async () => {
    try {
      const state = ts('verifications');
      const params = new URLSearchParams();
      if (verificationStatusFilter !== 'all') params.set('status', verificationStatusFilter);
      if (state.search) params.set('search', state.search);
      params.set('sort', state.sort || 'date_desc');
      params.set('page', state.page);
      params.set('limit', state.perPage);
      const response = await axios.get(`${API}/admin/journalist-verifications?${params.toString()}`);
      setVerificationRequests(response.data.items || []);
      setVerificationsTotal(response.data.total || 0);
      setVerificationsStatusCounts(response.data.status_counts || {});
    } catch (error) {
      console.error("Error fetching verification requests:", error);
      showToast("Ошибка загрузки заявок", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchDeletionRequests = async () => {
    try {
      const state = ts('deletions');
      const params = new URLSearchParams();
      if (state.search) params.set('search', state.search);
      params.set('sort', state.sort || 'date_desc');
      params.set('page', state.page);
      params.set('limit', state.perPage);
      const response = await axios.get(`${API}/admin/deletion-requests?${params.toString()}`);
      setDeletionRequests(response.data.items || []);
      setDeletionsTotal(response.data.total || 0);
      setDeletionsPendingCount(response.data.pending_count || 0);
    } catch (error) {
      console.error("Error fetching deletion requests:", error);
      showToast("Ошибка загрузки запросов на удаление", "error");
    }
  };

  const fetchDeletedUsersArchive = async () => {
    try {
      const state = ts('archive');
      const params = new URLSearchParams();
      if (state.search) params.set('search', state.search);
      params.set('sort', state.sort || 'date_desc');
      params.set('page', state.page);
      params.set('limit', state.perPage);
      const response = await axios.get(`${API}/admin/deleted-users-archive?${params.toString()}`);
      setDeletedUsersArchive(response.data.items || []);
      setArchiveTotal(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching deleted users archive:", error);
      showToast("Ошибка загрузки архива", "error");
    }
  };

  const fetchEscrowPayments = async () => {
    try {
      const state = ts('payments');
      const params = new URLSearchParams();
      if (state.search) params.set('search', state.search);
      params.set('sort', state.sort || 'date_desc');
      if (state.dateFrom) params.set('date_from', state.dateFrom);
      if (state.dateTo) params.set('date_to', state.dateTo);
      params.set('page', state.page);
      params.set('limit', state.perPage);
      const response = await axios.get(`${API}/admin/escrow-payments?${params.toString()}`);
      setEscrowPayments(response.data.items || []);
      setEscrowTotal(response.data.total || 0);
      setEscrowedCount(response.data.escrowed_count || 0);
    } catch (error) {
      console.error("Error fetching escrow payments:", error);
      showToast("Ошибка загрузки платежей", "error");
    }
  };

  const fetchRefunds = async () => {
    try {
      setRefundsLoading(true);
      const state = ts('refunds');
      const params = new URLSearchParams();
      if (refundsStatusFilter) params.set('status', refundsStatusFilter);
      if (state.search) params.set('search', state.search);
      params.set('sort', state.sort || 'date_desc');
      if (state.dateFrom) params.set('date_from', state.dateFrom);
      if (state.dateTo) params.set('date_to', state.dateTo);
      params.set('page', state.page);
      params.set('limit', state.perPage);
      const response = await axios.get(`${API}/admin/refunds?${params.toString()}`);
      setRefunds(response.data.items || []);
      setRefundsTotal(response.data.total || 0);
      setRefundsFailedCount(response.data.failed_count || 0);
      setRefundsPendingCount(response.data.pending_count || 0);
      setRefundsSummary(response.data.summary || null);
    } catch (error) {
      console.error("Error fetching refunds:", error);
      showToast("Ошибка загрузки возвратов", "error");
    } finally {
      setRefundsLoading(false);
    }
  };

  // TZ-7 п.6 (PKG-3а): повтор возврата — сервер блокирует двойной запуск, здесь — защита от двойного клика
  const [retryingRefundId, setRetryingRefundId] = useState(null);
  const retryRefund = async (refund) => {
    if (retryingRefundId) return;
    if (!window.confirm(`Повторить возврат ${formatRub(refund.amount)} ₽ через ЮKassa? Для зависшей записи сначала будет перечитан статус в ЮKassa.`)) return;
    setRetryingRefundId(refund.id);
    try {
      const r = await axios.post(`${API}/admin/refunds/${refund.id}/retry`);
      showToast(r.data.message || "Повтор выполнен", "success");
    } catch (error) {
      showToast(error.response?.data?.detail || "Не удалось повторить возврат", "error");
    } finally {
      setRetryingRefundId(null);
      fetchRefunds();
    }
  };

  const markRefundResolved = async (refundId) => {
    const note = window.prompt("Комментарий администратора (например: возврат сделан вручную в кабинете YooKassa)");
    if (note === null) return;
    try {
      await axios.post(`${API}/admin/refunds/${refundId}/mark-resolved`, { note });
      showToast("Возврат отмечен как выполненный вручную", "success");
      fetchRefunds();
    } catch (error) {
      showToast(error.response?.data?.detail || "Ошибка при обновлении возврата", "error");
    }
  };

  const fetchAdminDisputes = async () => {
    try {
      setAdminDisputesLoading(true);
      const state = ts('disputes');
      const params = new URLSearchParams();
      if (disputeStatusFilter) params.set('status', disputeStatusFilter);
      if (state.search) params.set('search', state.search);
      params.set('sort', state.sort || 'date_desc');
      if (state.dateFrom) params.set('date_from', state.dateFrom);
      if (state.dateTo) params.set('date_to', state.dateTo);
      params.set('page', state.page);
      params.set('limit', state.perPage);
      const response = await axios.get(`${API}/admin/disputes-all?${params.toString()}`);
      setAdminDisputes(response.data.items || []);
      setDisputesTotal(response.data.total || 0);
      setDisputesActiveCount(response.data.active_count || 0);
    } catch (error) {
      console.error("Error fetching disputes:", error);
      showToast("Ошибка загрузки споров", "error");
    } finally {
      setAdminDisputesLoading(false);
    }
  };

  const fetchFlaggedMessages = async (status = flagStatusFilter) => {
    try {
      setFlaggedLoading(true);
      const state = ts('flags');
      const params = new URLSearchParams();
      params.set('status', status);
      if (state.search) params.set('search', state.search);
      params.set('sort', state.sort || 'date_desc');
      params.set('page', state.page);
      params.set('limit', state.perPage);
      const response = await axios.get(`${API}/admin/flagged-messages?${params.toString()}`);
      setFlaggedMessages(response.data.items || []);
      setFlagsTotal(response.data.total || 0);
      setFlagsPendingCount(response.data.pending_count || 0);
    } catch (error) {
      console.error("Error fetching flagged messages:", error);
      showToast("Ошибка загрузки флагов", "error");
    } finally {
      setFlaggedLoading(false);
    }
  };

  const dismissFlag = async (flagId) => {
    try {
      await axios.post(`${API}/admin/flagged-messages/${flagId}/dismiss`, {
        notes: dismissNotes[flagId] || null,
      });
      showToast("Флаг снят", "success");
      setDismissNotes(prev => { const n = { ...prev }; delete n[flagId]; return n; });
      fetchFlaggedMessages(flagStatusFilter);
    } catch (error) {
      showToast(error.response?.data?.detail || "Ошибка при снятии флага", "error");
    }
  };

  const warnUser = async (flagId) => {
    try {
      const res = await axios.post(`${API}/admin/flagged-messages/${flagId}/warn`, {
        notes: dismissNotes[flagId] || null,
      });
      showToast(`Предупреждение отправлено на ${res.data.email_sent_to}`, "success");
      setDismissNotes(prev => { const n = { ...prev }; delete n[flagId]; return n; });
      fetchFlaggedMessages(flagStatusFilter);
    } catch (error) {
      showToast(error.response?.data?.detail || "Ошибка при отправке предупреждения", "error");
    }
  };

  const openChatView = async (taskId, highlightMsgId) => {
    try {
      const res = await axios.get(`${API}/admin/tasks/${taskId}/messages`);
      setChatView({
        taskId,
        taskTitle: res.data.task_title,
        messages: res.data.messages,
        highlightMsgId,
      });
    } catch (error) {
      showToast("Ошибка загрузки переписки", "error");
    }
  };

  // ----- Запись #24: пользователи и блокировки -----
  const fetchUsers = async (q = usersSearchQuery, blocked = usersBlockedFilter, role = usersRoleFilter) => {
    try {
      setUsersLoading(true);
      const state = ts('users');
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (blocked) params.set('blocked', blocked);
      if (role) params.set('role', role);
      if (state.dateFrom) params.set('date_from', state.dateFrom);
      if (state.dateTo) params.set('date_to', state.dateTo);
      params.set('sort', state.sort || 'date_desc');
      params.set('page', state.page);
      params.set('limit', state.perPage);
      const res = await axios.get(`${API}/admin/users?${params.toString()}`);
      setUsersList(res.data.users || []);
      setUsersTotal(res.data.total || 0);
      setUsersSummary(res.data.summary || null);
      setBlockReasonsMap(res.data.block_reasons || {});
    } catch (error) {
      showToast(error.response?.data?.detail || "Ошибка загрузки пользователей", "error");
    } finally {
      setUsersLoading(false);
    }
  };

  const submitBlockUser = async () => {
    if (!blockModal) return;
    if (!blockModal.reason) {
      showToast("Выберите причину блокировки", "error");
      return;
    }
    try {
      await axios.post(`${API}/admin/users/${blockModal.user.id}/block`, {
        reason: blockModal.reason,
        notes: blockModal.notes || null,
      });
      showToast(`Пользователь ${blockModal.user.email} заблокирован`, "success");
      setBlockModal(null);
      fetchUsers();
    } catch (error) {
      showToast(error.response?.data?.detail || "Ошибка блокировки", "error");
    }
  };

  const unblockUser = async (userObj) => {
    if (!window.confirm(`Разблокировать ${userObj.email}?`)) return;
    try {
      await axios.post(`${API}/admin/users/${userObj.id}/unblock`);
      showToast(`Пользователь ${userObj.email} разблокирован`, "success");
      fetchUsers();
    } catch (error) {
      showToast(error.response?.data?.detail || "Ошибка разблокировки", "error");
    }
  };

  // ----- Запись #25: жалобы -----
  const fetchComplaints = async (status = complaintsStatusFilter) => {
    try {
      setComplaintsLoading(true);
      const state = ts('complaints');
      const params = new URLSearchParams();
      params.set('status', status);
      if (state.search) params.set('search', state.search);
      params.set('sort', state.sort || 'date_desc');
      if (state.dateFrom) params.set('date_from', state.dateFrom);
      if (state.dateTo) params.set('date_to', state.dateTo);
      params.set('page', state.page);
      params.set('limit', state.perPage);
      const res = await axios.get(`${API}/admin/complaints?${params.toString()}`);
      setComplaintsList(res.data.items || []);
      setComplaintsTotal(res.data.total || 0);
      setComplaintsOpenCount(res.data.open_count || 0);
    } catch (error) {
      showToast(error.response?.data?.detail || "Ошибка загрузки жалоб", "error");
    } finally {
      setComplaintsLoading(false);
    }
  };

  const closeComplaint = async (complaintId) => {
    try {
      await axios.post(`${API}/admin/complaints/${complaintId}/close`, {
        notes: complaintCloseNotes[complaintId] || null,
      });
      showToast("Жалоба закрыта", "success");
      setComplaintCloseNotes(prev => { const n = { ...prev }; delete n[complaintId]; return n; });
      fetchComplaints(complaintsStatusFilter);
    } catch (error) {
      showToast(error.response?.data?.detail || "Ошибка закрытия жалобы", "error");
    }
  };

  const submitBlockFromComplaint = async () => {
    if (!complaintBlockModal) return;
    if (!complaintBlockModal.reason) {
      showToast("Выберите причину блокировки", "error");
      return;
    }
    try {
      await axios.post(`${API}/admin/complaints/${complaintBlockModal.complaint.id}/block-user`, {
        reason: complaintBlockModal.reason,
        notes: complaintBlockModal.notes || null,
      });
      showToast("Пользователь заблокирован, жалоба закрыта", "success");
      setComplaintBlockModal(null);
      fetchComplaints(complaintsStatusFilter);
    } catch (error) {
      showToast(error.response?.data?.detail || "Ошибка блокировки", "error");
    }
  };

  const handleRequestClarification = async (disputeId, target, message) => {
    try {
      await axios.post(`${API}/admin/disputes/${disputeId}/request-clarification`, { target, message });
      showToast("Запрос уточнения отправлен на email", "success");
      setClarificationForms(prev => ({ ...prev, [disputeId]: { show: false, target: 'customer', text: '' } }));
    } catch (error) {
      showToast(error.response?.data?.detail || "Ошибка отправки запроса", "error");
    }
  };

  const handleVerification = async (requestId, action, reason = "") => {
    try {
      await axios.post(`${API}/admin/journalist-verifications/${requestId}/${action}`, {
        reason
      });
      showToast(`Заявка ${action === 'approve' ? 'одобрена' : 'отклонена'}`, "success");
      fetchVerificationRequests(); // Refresh list
    } catch (error) {
      showToast("Ошибка при обработке заявки", "error");
    }
  };

  const fetchIdentityVerifications = async () => {
    try {
      const state = tabState.identityVerifications || { page: 1, perPage: 10, search: '' };
      const params = { page: state.page, limit: state.perPage };
      if (identityStatusFilter && identityStatusFilter !== 'all') params.status = identityStatusFilter;
      if (state.search && state.search.trim()) params.search = state.search.trim();
      const res = await axios.get(`${API}/admin/identity-verifications`, { params });
      setIdentityVerifications(res.data.items || []);
      setIdentityVerificationsTotal(res.data.total || 0);
      setIdentityPendingCount(res.data.pending_count || 0);
    } catch (error) {
      showToast("Ошибка загрузки заявок на верификацию личности", "error");
    }
  };

  // Серверная пагинация/поиск: перезапрашиваем заявки при смене вкладки, фильтра, страницы, лимита или поиска
  useEffect(() => {
    if (activeTab !== 'identity-verifications') return;
    const state = tabState.identityVerifications || { page: 1, perPage: 10, search: '' };
    const delay = state.search ? 400 : 0;
    const handle = setTimeout(() => { fetchIdentityVerifications(); }, delay);
    return () => clearTimeout(handle);
  }, [activeTab, identityStatusFilter, tabState.identityVerifications?.page, tabState.identityVerifications?.perPage, tabState.identityVerifications?.search]);

  // ====== Серверная пагинация для остальных вкладок админ-панели ======
  useEffect(() => {
    if (activeTab !== 'refunds') return;
    const state = ts('refunds');
    const handle = setTimeout(() => { fetchRefunds(); }, state.search ? 400 : 0);
    return () => clearTimeout(handle);
  }, [activeTab, refundsStatusFilter, tabState.refunds?.page, tabState.refunds?.perPage, tabState.refunds?.search, tabState.refunds?.sort, tabState.refunds?.dateFrom, tabState.refunds?.dateTo]);

  useEffect(() => {
    if (activeTab !== 'verifications') return;
    const state = ts('verifications');
    const handle = setTimeout(() => { fetchVerificationRequests(); }, state.search ? 400 : 0);
    return () => clearTimeout(handle);
  }, [activeTab, verificationStatusFilter, tabState.verifications?.page, tabState.verifications?.perPage, tabState.verifications?.search, tabState.verifications?.sort]);

  useEffect(() => {
    if (activeTab !== 'deletions') return;
    const state = ts('deletions');
    const handle = setTimeout(() => { fetchDeletionRequests(); }, state.search ? 400 : 0);
    return () => clearTimeout(handle);
  }, [activeTab, tabState.deletions?.page, tabState.deletions?.perPage, tabState.deletions?.search, tabState.deletions?.sort]);

  useEffect(() => {
    if (activeTab !== 'archive') return;
    const state = ts('archive');
    const handle = setTimeout(() => { fetchDeletedUsersArchive(); }, state.search ? 400 : 0);
    return () => clearTimeout(handle);
  }, [activeTab, tabState.archive?.page, tabState.archive?.perPage, tabState.archive?.search, tabState.archive?.sort]);

  useEffect(() => {
    if (activeTab !== 'payments') return;
    const state = ts('payments');
    const handle = setTimeout(() => { fetchEscrowPayments(); }, state.search ? 400 : 0);
    return () => clearTimeout(handle);
  }, [activeTab, tabState.payments?.page, tabState.payments?.perPage, tabState.payments?.search, tabState.payments?.sort, tabState.payments?.dateFrom, tabState.payments?.dateTo]);

  useEffect(() => {
    if (activeTab !== 'npd') return;
    const state = ts('npd');
    const handle = setTimeout(() => { fetchNpdReceipts(); }, state.search ? 400 : 0);
    return () => clearTimeout(handle);
  }, [activeTab, tabState.npd?.page, tabState.npd?.perPage, tabState.npd?.search, tabState.npd?.sort, tabState.npd?.dateFrom, tabState.npd?.dateTo]);

  useEffect(() => {
    if (activeTab !== 'payouts') return;
    const state = ts('payouts');
    const handle = setTimeout(() => { fetchPendingPayouts(); }, state.search ? 400 : 0);
    return () => clearTimeout(handle);
  }, [activeTab, tabState.payouts?.page, tabState.payouts?.perPage, tabState.payouts?.search, tabState.payouts?.sort, tabState.payouts?.dateFrom, tabState.payouts?.dateTo]);

  useEffect(() => {
    if (activeTab !== 'payout-history') return;
    const state = ts('payoutHistory');
    const handle = setTimeout(() => { fetchPayoutHistory(); }, state.search ? 400 : 0);
    return () => clearTimeout(handle);
  }, [activeTab, tabState.payoutHistory?.page, tabState.payoutHistory?.perPage, tabState.payoutHistory?.search, tabState.payoutHistory?.sort, tabState.payoutHistory?.dateFrom, tabState.payoutHistory?.dateTo]);

  useEffect(() => {
    if (activeTab !== 'disputes') return;
    const state = ts('disputes');
    const handle = setTimeout(() => { fetchAdminDisputes(); }, state.search ? 400 : 0);
    return () => clearTimeout(handle);
  }, [activeTab, disputeStatusFilter, tabState.disputes?.page, tabState.disputes?.perPage, tabState.disputes?.search, tabState.disputes?.sort, tabState.disputes?.dateFrom, tabState.disputes?.dateTo]);

  useEffect(() => {
    if (activeTab !== 'flags') return;
    const state = ts('flags');
    const handle = setTimeout(() => { fetchFlaggedMessages(flagStatusFilter); }, state.search ? 400 : 0);
    return () => clearTimeout(handle);
  }, [activeTab, flagStatusFilter, tabState.flags?.page, tabState.flags?.perPage, tabState.flags?.search, tabState.flags?.sort]);

  useEffect(() => {
    if (activeTab !== 'users') return;
    const state = ts('users');
    const handle = setTimeout(() => { fetchUsers(usersSearchQuery, usersBlockedFilter); }, state.search !== undefined ? 0 : 0);
    return () => clearTimeout(handle);
  }, [activeTab, tabState.users?.page, tabState.users?.perPage, tabState.users?.sort, tabState.users?.dateFrom, tabState.users?.dateTo]);

  useEffect(() => {
    if (activeTab !== 'complaints') return;
    const state = ts('complaints');
    const handle = setTimeout(() => { fetchComplaints(complaintsStatusFilter); }, state.search ? 400 : 0);
    return () => clearTimeout(handle);
  }, [activeTab, complaintsStatusFilter, tabState.complaints?.page, tabState.complaints?.perPage, tabState.complaints?.search, tabState.complaints?.sort, tabState.complaints?.dateFrom, tabState.complaints?.dateTo]);

  useEffect(() => {
    if (activeTab !== 'stats') return;
    const state = ts('adminTasks');
    const handle = setTimeout(() => { fetchAdminTasks(); }, state.search ? 400 : 0);
    return () => clearTimeout(handle);
  }, [activeTab, adminTaskStatusFilter, tabState.adminTasks?.page, tabState.adminTasks?.perPage, tabState.adminTasks?.search, tabState.adminTasks?.sort]);

  useEffect(() => {
    if (activeTab !== 'verified-executors') return;
    const handle = setTimeout(() => { fetchVerifiedExecutors(); }, 0);
    return () => clearTimeout(handle);
  }, [activeTab, tabState.verifiedExecutors?.page, tabState.verifiedExecutors?.perPage, tabState.verifiedExecutors?.sort]);

  useEffect(() => {
    if (activeTab !== 'commissions') return;
    fetchCommissionAuditLog();
  }, [activeTab, tabState.commissionAudit?.page, tabState.commissionAudit?.perPage, auditLogFrom, auditLogTo]);

  const handleApproveIdentity = async (verificationId) => {
    try {
      await axios.post(`${API}/admin/identity-verifications/${verificationId}/approve`);
      showToast("Личность подтверждена", "success");
      fetchIdentityVerifications();
    } catch (error) {
      showToast(error.response?.data?.detail || "Ошибка при одобрении заявки", "error");
    }
  };

  const handleRejectIdentity = async (verificationId) => {
    const reason = prompt("Укажите причину отклонения (обязательно):");
    if (!reason || !reason.trim()) {
      showToast("Причина отклонения обязательна", "error");
      return;
    }
    try {
      await axios.post(`${API}/admin/identity-verifications/${verificationId}/reject`, { reason: reason.trim() });
      showToast("Заявка отклонена", "success");
      fetchIdentityVerifications();
    } catch (error) {
      showToast(error.response?.data?.detail || "Ошибка при отклонении заявки", "error");
    }
  };

  const viewIdentityPhoto = async (verificationId, photoType) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/admin/identity-verifications/${verificationId}/photo/${photoType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), '_blank');
    } catch {
      showToast('Фото недоступно (возможно, истёк срок хранения 30 дней)', 'error');
    }
  };

  const handleCancelDeletion = async (requestId) => {
    if (!confirm("Отменить удаление аккаунта? Пользователь останется в системе.")) {
      return;
    }

    try {
      await axios.post(`${API}/admin/deletion-requests/${requestId}/cancel`);
      showToast("Запрос на удаление отменен", "success");
      fetchDeletionRequests();
    } catch (error) {
      showToast("Ошибка при отмене удаления", "error");
    }
  };

  const handleExportUserData = async (userId, userEmail) => {
    if (!confirm(`Экспортировать данные удаленного пользователя ${userEmail}? Это действие будет записано в журнал администратора.`)) {
      return;
    }

    try {
      const response = await axios.get(`${API}/admin/deleted-users-archive/${userId}/export`);
      
      // Download as JSON file
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { 
        type: 'application/json' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `deleted_user_export_${userId}_${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      window.URL.revokeObjectURL(url);
      
      showToast("Данные экспортированы", "success");
    } catch (error) {
      showToast("Ошибка при экспорте данных", "error");
    }
  };

  const handleEscrowAction = async (paymentId, action) => {
    const actionText = action === 'release_to_executor'
      ? 'освободить средства исполнителю'
      : 'решить спор в пользу заказчика. Заказчик сам выберет: вернуть деньги или найти нового исполнителя';
    
    if (!confirm(`Вы действительно хотите ${actionText}? Это действие необратимо.`)) {
      return;
    }

    try {
      if (action === 'release_to_executor') {
        await axios.post(`${API}/admin/escrow-payments/${paymentId}/release_to_executor`);
        showToast("Средства освобождены исполнителю. Уведомления отправлены.", "success");
      } else {
        await axios.post(`${API}/admin/escrow-payments/${paymentId}/refund_to_customer`, { resolution_type: "customer_choice" });
        showToast("Решение принято в пользу заказчика. Ожидаем его выбора.", "success");
      }
      fetchEscrowPayments();
      fetchAdminDisputes();
    } catch (error) {
      showToast("Ошибка при обработке платежа", "error");
    }
  };

  const handleDisputeRework = async (paymentId) => {
    if (!confirm("Отправить задание исполнителю на доработку (48 часов)?")) return;
    try {
      await axios.post(`${API}/admin/escrow-payments/${paymentId}/refund_to_customer`, { resolution_type: "rework" });
      showToast("Задание отправлено на доработку. Исполнитель получил уведомление.", "success");
      fetchAdminDisputes();
    } catch (error) {
      showToast("Ошибка при отправке на доработку", "error");
    }
  };

  const handleAiAnalyzeDispute = async (disputeId) => {
    try {
      setAiAnalyzing(prev => ({ ...prev, [disputeId]: true }));
      const response = await axios.post(`${API}/admin/disputes/${disputeId}/ai-analyze`);
      setAdminDisputes(prev => prev.map(d => d.id === disputeId ? { ...d, ai_recommendation: response.data } : d));
      showToast("AI-рекомендация готова", "success");
    } catch (error) {
      showToast(error.response?.data?.detail || "Не удалось получить рекомендацию AI", "error");
    } finally {
      setAiAnalyzing(prev => ({ ...prev, [disputeId]: false }));
    }
  };

  const fetchTaskDetails = async (taskId) => {
    if (!taskId) {
      showToast("ID задания не определён в записи платежа", "error");
      return;
    }
    try {
      setTaskDetailsLoading(true);
      const response = await axios.get(`${API}/admin/tasks/${taskId}/details`);
      setTaskDetailsModal({ isOpen: true, taskId, details: response.data });
    } catch (error) {
      console.error("Error fetching task details:", error);
      const msg = error.response?.data?.detail || "Ошибка загрузки деталей задания";
      showToast(msg, "error");
    } finally {
      setTaskDetailsLoading(false);
    }
  };

  const viewTaskDetails = (taskId) => {
    fetchTaskDetails(taskId);
  };

  const closeTaskDetailsModal = () => {
    setTaskDetailsModal({ isOpen: false, taskId: null, details: null });
  };

  const fetchNpdReceipts = async () => {
    try {
      setNpdReceiptsLoading(true);
      const state = ts('npd');
      const params = new URLSearchParams();
      if (state.search) params.set('search', state.search);
      params.set('sort', state.sort || 'date_desc');
      if (state.dateFrom) params.set('date_from', state.dateFrom);
      if (state.dateTo) params.set('date_to', state.dateTo);
      params.set('page', state.page);
      params.set('limit', state.perPage);
      const response = await axios.get(`${API}/admin/npd-receipts?${params.toString()}`);
      setNpdReceipts(response.data.receipts || []);
      setNpdTotal(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching NPD receipts:", error);
      showToast("Ошибка загрузки налоговых чеков", "error");
    } finally {
      setNpdReceiptsLoading(false);
    }
  };

  // Функции для управления выплатами
  const fetchPendingPayouts = async () => {
    try {
      setPayoutsLoading(true);
      const state = ts('payouts');
      const params = new URLSearchParams();
      if (state.search) params.set('search', state.search);
      params.set('sort', state.sort || 'date_desc');
      if (state.dateFrom) params.set('date_from', state.dateFrom);
      if (state.dateTo) params.set('date_to', state.dateTo);
      params.set('page', state.page);
      params.set('limit', state.perPage);
      const response = await axios.get(`${API}/admin/pending-payouts?${params.toString()}`);
      setPendingPayouts(response.data.items || []);
      setPendingPayoutsTotal(response.data.total || 0);
      setPendingPayoutsAmount(response.data.total_amount || 0);
    } catch (error) {
      console.error("Error fetching pending payouts:", error); 
      showToast("Ошибка загрузки ожидающих выплат", "error");
    } finally {
      setPayoutsLoading(false);
    }
  };

  const fetchPayoutHistory = async () => {
    try {
      const state = ts('payoutHistory');
      const params = new URLSearchParams();
      if (state.search) params.set('search', state.search);
      params.set('sort', state.sort || 'date_desc');
      if (state.dateFrom) params.set('date_from', state.dateFrom);
      if (state.dateTo) params.set('date_to', state.dateTo);
      params.set('page', state.page);
      params.set('limit', state.perPage);
      const response = await axios.get(`${API}/admin/payout-history?${params.toString()}`);
      setPayoutHistory(response.data);
      setPayoutHistoryTotal(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching payout history:", error);
      showToast("Ошибка загрузки истории выплат", "error");
    }
  };

  const markPayoutAsPaid = async (payoutId, paymentMethod, adminNotes) => {
    try {
      const response = await axios.post(`${API}/admin/mark-payout-paid`, {
        payout_id: payoutId,
        payment_method: paymentMethod,
        admin_notes: adminNotes
      });
      showToast("Выплата отмечена как выполненная", "success");
      fetchPendingPayouts(); // Обновляем список
      fetchPayoutHistory(); // Обновляем историю
    } catch (error) {
      console.error("Error marking payout as paid:", error);
      showToast("Ошибка при отметке выплаты", "error");
    }
  };

  const fetchUsersStats = async () => {
    try {
      setUsersStatsLoading(true);
      const response = await axios.get(`${API}/admin/users-stats`);
      setUsersStats(response.data);
    } catch (error) {
      console.error("Error fetching users stats:", error);
      showToast("Ошибка загрузки статистики", "error");
    } finally {
      setUsersStatsLoading(false);
    }
  };

  const fetchAdminTasks = async () => {
    try {
      setAdminTasksLoading(true);
      const state = ts('adminTasks');
      const params = new URLSearchParams();
      if (adminTaskStatusFilter) params.set('status', adminTaskStatusFilter);
      if (state.search) params.set('search', state.search);
      params.set('sort', state.sort || 'date_desc');
      params.set('page', state.page);
      params.set('limit', state.perPage);
      const response = await axios.get(`${API}/admin/all-tasks?${params.toString()}`);
      setAdminAllTasks(response.data.items || []);
      setAdminTasksTotal(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching admin tasks:", error);
      showToast("Ошибка загрузки заданий", "error");
    } finally {
      setAdminTasksLoading(false);
    }
  };


  const translateServiceType = (type) => {
    const types = { 'general': 'Общие услуги', 'secret_shopper': 'Тайный покупатель', 'grave_care': 'Мемориальный сервис', 'mobile_journalist': 'Мобильный корреспондент' };
    return types[type] || (type || '—');
  };
  const getTaskStatusBg = (status) => {
    const bgs = { 'pending': 'rgba(217,119,6,0.1)', 'pending_payment': 'rgba(217,119,6,0.08)', 'paid': 'rgba(16,185,129,0.1)', 'in_progress': 'rgba(59,130,246,0.1)', 'completed': 'rgba(16,185,129,0.1)', 'confirmed': 'rgba(16,185,129,0.15)', 'cancelled': 'rgba(107,114,128,0.1)', 'disputed': 'rgba(239,68,68,0.1)', 'refunded': 'rgba(107,114,128,0.1)', 'archived': 'rgba(107,114,128,0.08)' };
    return bgs[status] || 'rgba(107,114,128,0.1)';
  };
  const getTaskStatusColor = (status) => {
    const colors = { 'pending': '#d97706', 'pending_payment': '#d97706', 'paid': '#10b981', 'in_progress': '#3b82f6', 'completed': '#10b981', 'confirmed': '#059669', 'cancelled': '#6b7280', 'disputed': '#ef4444', 'refunded': '#6b7280', 'archived': '#9ca3af' };
    return colors[status] || '#6b7280';
  };

  // Функции для управления режимом выплат
  const fetchPayoutMode = async () => {
    try {
      setPayoutModeLoading(true);
      const response = await axios.get(`${API}/admin/payout-mode`);
      setPayoutMode(response.data.payout_mode);
    } catch (error) {
      console.error("Error fetching payout mode:", error);
      showToast("Ошибка загрузки режима выплат", "error");
    } finally {
      setPayoutModeLoading(false);
    }
  };

  const updatePayoutMode = async (newMode) => {
    try {
      setPayoutModeLoading(true);
      await axios.post(`${API}/admin/payout-mode`, { 
        payout_mode: newMode 
      });
      setPayoutMode(newMode);
      showToast(`Режим выплат изменен на ${newMode === 'auto' ? 'автоматический' : 'ручной'}`, "success");
    } catch (error) {
      console.error("Error updating payout mode:", error);
      showToast("Ошибка изменения режима выплат", "error");
    } finally {
      setPayoutModeLoading(false);
    }
  };

  const fetchSiteConfig = async () => {
    try {
      setSiteConfigLoading(true);
      const response = await axios.get(`${API}/admin/site-config`);
      setSiteConfig({
        site_gate_enabled: !!response.data.site_gate_enabled,
        paid_tasks_enabled: !!response.data.paid_tasks_enabled,
        ai_helper_enabled: response.data.ai_helper_enabled !== false,
        ai_dispute_assistant_enabled: response.data.ai_dispute_assistant_enabled !== false,
        push_anonymize: response.data.push_anonymize !== false,
        public_stats_enabled: !!response.data.public_stats_enabled,
        public_stats_cities: response.data.public_stats_cities ?? 1,
        public_stats_min_executors: response.data.public_stats_min_executors ?? 100,
      });
      setCitiesDraft(String(response.data.public_stats_cities ?? 1));
      setMinExecutorsDraft(String(response.data.public_stats_min_executors ?? 100));
      try {
        const statsRes = await axios.get(`${API}/public/platform-stats`);
        setLiveExecutorsCount(statsRes.data.executors_count);
      } catch (e) {
        setLiveExecutorsCount(null);
      }
      try {
        const aiRes = await axios.get(`${API}/admin/ai-usage-stats`);
        setAiUsage(aiRes.data);
      } catch (e) {
        setAiUsage(null);
      }
      try {
        const annRes = await axios.get(`${API}/admin/announcements/active`);
        setActiveAnnouncement(annRes.data.announcement || null);
      } catch (e) {
        setActiveAnnouncement(null);
      }
    } catch (error) {
      console.error("Error fetching site config:", error);
      showToast("Ошибка загрузки настроек доступа", "error");
    } finally {
      setSiteConfigLoading(false);
    }
  };

  const updateSiteConfig = async (partial) => {
    try {
      setSiteConfigLoading(true);
      const response = await axios.post(`${API}/admin/site-config`, partial);
      setSiteConfig({
        site_gate_enabled: !!response.data.site_gate_enabled,
        paid_tasks_enabled: !!response.data.paid_tasks_enabled,
        ai_helper_enabled: response.data.ai_helper_enabled !== false,
        ai_dispute_assistant_enabled: response.data.ai_dispute_assistant_enabled !== false,
        push_anonymize: response.data.push_anonymize !== false,
        public_stats_enabled: !!response.data.public_stats_enabled,
        public_stats_cities: response.data.public_stats_cities ?? 1,
        public_stats_min_executors: response.data.public_stats_min_executors ?? 100,
      });
      setCitiesDraft(String(response.data.public_stats_cities ?? 1));
      setMinExecutorsDraft(String(response.data.public_stats_min_executors ?? 100));
      showToast("Настройки доступа обновлены", "success");
    } catch (error) {
      console.error("Error updating site config:", error);
      showToast("Ошибка обновления настроек доступа", "error");
    } finally {
      setSiteConfigLoading(false);
    }
  };

  const sendAnnouncement = async () => {
    const title = announceForm.title.trim();
    const message = announceForm.message.trim();
    if (!title || !message) {
      showToast("Заполните заголовок и текст объявления", "error");
      return;
    }
    if (announceForm.audience === 'user' && !announceForm.target_email.trim()) {
      showToast("Укажите email пользователя", "error");
      return;
    }
    try {
      setAnnounceLoading(true);
      const res = await axios.post(`${API}/admin/announcements`, {
        title,
        message,
        audience: announceForm.audience,
        target_email: announceForm.audience === 'user' ? announceForm.target_email.trim() : null,
      });
      setActiveAnnouncement(res.data.announcement || null);
      setAnnounceForm({ audience: 'all', target_email: '', title: '', message: '' });
      showToast("Объявление отправлено", "success");
    } catch (error) {
      const msg = error?.response?.data?.detail || "Ошибка отправки объявления";
      showToast(msg, "error");
    } finally {
      setAnnounceLoading(false);
    }
  };

  const deactivateAnnouncement = async () => {
    try {
      setAnnounceLoading(true);
      await axios.post(`${API}/admin/announcements/deactivate`);
      setActiveAnnouncement(null);
      showToast("Объявление деактивировано", "success");
    } catch (error) {
      showToast("Ошибка деактивации", "error");
    } finally {
      setAnnounceLoading(false);
    }
  };


  // Функция перевода статусов заданий на русский язык
  const translateTaskStatus = (status) => {
    const translations = {
      'pending': 'Ожидает',
      'pending_payment': 'Ожидает оплаты',
      'paid': 'Оплачено',
      'assigned': 'Назначено',
      'in_progress': 'В работе',
      'completed': 'Завершено',
      'confirmed': 'Подтверждено',
      'cancelled': 'Отменено',
      'disputed': 'Спор',
      'refunded': 'Возврат',
      'archived': 'Архив'
    };
    return translations[status] || status;
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        <p className="text-gray-600 mt-2">Загрузка...</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <h2 className="text-2xl font-bold mb-6 flex items-center gap-2" style={{fontFamily:'Manrope,sans-serif', color:'var(--rv-text-primary)'}}>
        <Settings size={22} style={{color:'var(--rv-teal)'}}/>Админ-панель
      </h2>
      
      {/* Tabs — горизонтальный скролл на мобильном */}
      <div className="mb-6" style={{borderBottom:'2px solid var(--rv-border)'}}>
        <nav className="-mb-0.5 flex flex-wrap gap-1 pb-0">
          {[
            { key: 'verifications', label: 'Верификации', Icon: ShieldCheck, testId: 'admin-tab-verifications', badge: (verificationsStatusCounts.pending || 0) + (verificationsStatusCounts.under_review || 0) },
            { key: 'identity-verifications', label: 'Верификация личности', Icon: BadgeCheck, testId: 'admin-tab-identity-verifications', badge: identityPendingCount, onSelect: () => { setIdentityStatusFilter('pending'); setTSPage('identityVerifications', 1); } },
            { key: 'deletions', label: 'Удаления', Icon: Trash2, badge: deletionsPendingCount, testId: 'admin-tab-deletions' },
            { key: 'archive', label: 'Архив', Icon: Archive, badge: archiveTotal, testId: 'admin-tab-archive' },
            { key: 'payments', label: 'Платежи', Icon: Coins, badge: escrowedCount, testId: 'admin-tab-payments' },
            { key: 'refunds', label: 'Возвраты', Icon: RotateCcw, badge: refundsFailedCount, testId: 'admin-tab-refunds' },
            { key: 'npd', label: 'Чеки', Icon: Receipt, badge: npdTotal, testId: 'admin-tab-npd' },
            { key: 'payouts', label: 'Выплаты', Icon: Wallet, badge: pendingPayoutsTotal, testId: 'admin-tab-payouts' },
            { key: 'stats', label: 'Статистика', Icon: BarChart2, testId: 'admin-tab-stats', onSelect: fetchUsersStats },
            { key: 'settings', label: 'Настройки', Icon: Settings, testId: 'admin-tab-settings', onSelect: () => { fetchPayoutMode(); fetchSiteConfig(); } },
            { key: 'disputes', label: 'Споры', Icon: Scale, badge: disputesActiveCount, testId:'admin-tab-disputes' },
            { key: 'flags', label: 'Флаги', Icon: Flag, badge: flagsPendingCount, testId:'admin-tab-flags', onSelect: ()=>{ setFlagStatusFilter('pending'); } },
            { key: 'users', label: 'Пользователи', Icon: Users, testId:'admin-tab-users', onSelect: ()=>{ setUsersSearchQuery(''); setUsersBlockedFilter(''); setUsersRoleFilter(''); setTSPage('users', 1); } },
            { key: 'complaints', label: 'Жалобы', Icon: AlertTriangle, badge: complaintsOpenCount, testId:'admin-tab-complaints', onSelect: ()=>{ setComplaintsStatusFilter('open'); } },
            { key: 'commissions', label: 'Комиссии', Icon: BarChart2, testId:'admin-tab-commissions', onSelect: fetchCommissionSettings },
            { key: 'verified-executors', label: 'Проверенные', Icon: CheckCircle2, testId:'admin-tab-verified-executors' },
            { key: 'broadcast', label: 'Рассылка', Icon: Mail, testId:'admin-tab-broadcast', onSelect: fetchBroadcastHistory },
            { key: 'admins', label: 'Администраторы', Icon: ShieldCheck, testId:'admin-tab-admins', onSelect: fetchAdmins },
            { key: 'b2b', label: 'B2B', Icon: Building2, testId:'admin-tab-b2b' },
            { key: 'leads', label: 'Заявки', Icon: Mail, testId:'admin-tab-leads' },
          ].map(({ key, label, Icon, badge, testId, onSelect }) => (
            <button
              key={key}
              data-testid={testId}
              onClick={() => { setActiveTab(key); onSelect && onSelect(); }}
              className="relative flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium whitespace-nowrap transition-colors flex-shrink-0"
              style={{
                color: activeTab === key ? 'var(--rv-teal)' : 'var(--rv-text-muted)',
                background: 'none',
                borderTop: 'none',
                borderLeft: 'none',
                borderRight: 'none',
                borderBottom: activeTab === key ? '2px solid var(--rv-teal)' : '2px solid transparent',
                cursor: 'pointer',
                fontFamily: 'IBM Plex Sans, sans-serif',
                marginBottom: '-2px',
              }}
            >
              <Icon size={14}/>{label}
              {badge > 0 && (
                <span className="ml-0.5 px-1.5 rounded-full text-white" style={{background:'var(--rv-error)', fontSize:'0.6rem', fontWeight:700, minWidth:'18px', textAlign:'center'}}>
                  {badge}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Verifications Tab */}
      {activeTab === 'verifications' && (
        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
            <h3 className="text-lg font-semibold">Заявки на верификацию журналистов</h3>
            <div className="flex flex-wrap gap-2" data-testid="verification-filter-bar">
              {[
                { key: 'all', label: 'Все' },
                { key: 'pending', label: 'Ожидают' },
                { key: 'under_review', label: 'На проверке' },
                { key: 'approved', label: 'Одобрены' },
                { key: 'rejected', label: 'Отклонены' },
              ].map(f => (
                <button
                  key={f.key}
                  data-testid={`filter-verif-${f.key}`}
                  onClick={() => { setVerificationStatusFilter(f.key); setTSPage('verifications', 1); }}
                  className={`text-xs px-3 py-1.5 rounded-full font-medium border transition-colors ${
                    verificationStatusFilter === f.key
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400 hover:text-blue-600'
                  }`}
                >
                  {f.label}
                  {f.key !== 'all' && (
                    <span className="ml-1 opacity-70">
                      ({verificationsStatusCounts[f.key] || 0})
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {(() => {
            const pageItems = verificationRequests;
            const pageTotal = verificationsTotal;
            return (
              <>
                <AdminSearchSort tabState={tabState} setSearch={setTSSearch} setSort={setTSSort} tab="verifications" placeholder="Поиск по имени или email..." sortOptions={[{value:'date_desc',label:'Дата: новые'},{value:'date_asc',label:'Дата: старые'},{value:'status',label:'По статусу'}]} testPrefix="verif" />
                {pageItems.length === 0 ? (
                  <p className="text-gray-500 text-sm">Ничего не найдено</p>
                ) : (
                  <div className="space-y-3">
                    {pageItems.map((request) => {
                  const isExpanded = expandedVerifications.has(request.id);
                  const toggleExpand = () => {
                    setExpandedVerifications(prev => {
                      const next = new Set(prev);
                      if (next.has(request.id)) next.delete(request.id);
                      else next.add(request.id);
                      return next;
                    });
                  };
                  const statusLabel = request.status === 'approved' ? 'Одобрено' :
                    request.status === 'rejected' ? 'Отклонено' :
                    request.status === 'under_review' ? 'На проверке' : 'Ожидает';
                  const statusClass = request.status === 'approved' ? 'bg-green-100 text-green-800' :
                    request.status === 'rejected' ? 'bg-red-100 text-red-800' :
                    request.status === 'under_review' ? 'bg-blue-100 text-blue-800' :
                    'bg-yellow-100 text-yellow-800';

                  return (
                    <div key={request.id} className="border border-gray-200 rounded-lg overflow-hidden" data-testid={`verification-card-${request.id}`}>
                      {/* Collapsed header — always visible */}
                      <button
                        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                        onClick={toggleExpand}
                        data-testid={`verification-toggle-${request.id}`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <span className={`text-xs px-2 py-1 rounded-full font-medium flex-shrink-0 ${statusClass}`}>
                            {statusLabel}
                          </span>
                          <span className="font-medium text-gray-900 truncate">
                            {request.user_name || request.contact_person || '—'}
                          </span>
                          <span className="text-xs text-gray-400 hidden sm:inline flex-shrink-0">
                            {request.submitted_at ? formatMsk(request.submitted_at) : ''}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                          <span className="text-xs text-blue-600 font-medium">
                            {isExpanded ? 'Свернуть' : 'Подробнее'}
                          </span>
                          <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        </div>
                      </button>

                      {/* Expanded body */}
                      {isExpanded && (
                        <div className="px-4 pb-4 border-t border-gray-100 bg-gray-50">
                          <div className="pt-4 grid grid-cols-1 md:grid-cols-2 gap-5">
                            {/* Left column — identity */}
                            <div className="space-y-1.5">
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Контактная информация</p>
                              <p className="text-sm text-gray-700"><span className="font-medium">Email:</span> {request.user_email || request.contact_email || '—'}</p>
                              <p className="text-sm text-gray-700"><span className="font-medium">Телефон:</span> {request.contact_phone || '—'}</p>
                              <p className="text-sm text-gray-700"><span className="font-medium">Удостоверение:</span> {request.press_card_number || '—'}</p>
                              <p className="text-sm text-gray-700"><span className="font-medium">СМИ:</span> {request.media_organization || '—'}</p>
                              <p className="text-sm text-gray-700"><span className="font-medium">Регион работы:</span> {request.work_region || '—'}</p>
                              {request.press_card_photo_id && (
                                <button
                                  onClick={async () => {
                                    try {
                                      const token = localStorage.getItem('token');
                                      const res = await fetch(`${API}/admin/portfolio-items/${request.press_card_photo_id}`, {
                                        headers: { Authorization: `Bearer ${token}` }
                                      });
                                      if (!res.ok) throw new Error();
                                      const blob = await res.blob();
                                      window.open(URL.createObjectURL(blob), '_blank');
                                    } catch { showToast('Не удалось открыть фото удостоверения', 'error'); }
                                  }}
                                  className="mt-1 flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
                                >
                                  <FileText className="w-3.5 h-3.5" /> Фото удостоверения
                                </button>
                              )}
                              <p className="text-sm text-gray-400 mt-1">
                                Подано: {request.submitted_at ? formatMsk(request.submitted_at) : '—'}
                              </p>
                            </div>

                            {/* Right column — professional details */}
                            <div className="space-y-3">
                              {/* Specializations */}
                              <div>
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Специализации</p>
                                {(request.specializations || []).length > 0 ? (
                                  <div className="flex flex-wrap gap-1.5">
                                    {request.specializations.map((s, i) => (
                                      <span key={i} className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">{s}</span>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-xs text-gray-400">Не указаны</p>
                                )}
                              </div>

                              {/* Equipment */}
                              <div>
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Оборудование</p>
                                {(request.equipment || []).length > 0 ? (
                                  <div className="flex flex-wrap gap-1.5">
                                    {request.equipment.map((e, i) => (
                                      <span key={i} className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full">{e}</span>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-xs text-gray-400">Не указано</p>
                                )}
                              </div>

                              {/* Portfolio */}
                              <div>
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Ссылки на портфолио</p>
                                {(request.portfolio_urls || []).length > 0 ? (
                                  <div className="space-y-1">
                                    {request.portfolio_urls.map((link, idx) => (
                                      <a key={idx} href={link} target="_blank" rel="noopener noreferrer"
                                        className="block text-xs text-blue-600 hover:underline break-all">
                                        {link}
                                      </a>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-xs text-gray-400">Не указаны</p>
                                )}
                              </div>

                              {/* Uploaded portfolio files */}
                              {(request.portfolio_items || []).length > 0 && (
                                <div>
                                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                                    Загруженные файлы ({request.portfolio_items.length})
                                  </p>
                                  <div className="space-y-2">
                                    {request.portfolio_items.map((item, idx) => {
                                      const isImage = item.content_type && item.content_type.startsWith('image/');
                                      const isPdf = item.content_type === 'application/pdf';
                                      const fileUrl = `${API}/admin/portfolio-items/${item.id}`;
                                      const qualityScore = item.quality_score != null ? Math.round(item.quality_score * 100) : null;
                                      return (
                                        <div key={idx} className="flex items-center justify-between p-2 bg-white border border-gray-200 rounded-md">
                                          <div className="flex items-center gap-2 min-w-0">
                                            <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                            <div className="min-w-0">
                                              <p className="text-xs font-medium text-gray-800 truncate">{item.filename}</p>
                                              <p className="text-xs text-gray-400">
                                                {(item.file_size / 1024 / 1024).toFixed(1)} МБ
                                                {qualityScore != null && (
                                                  <span className={`ml-2 font-medium ${qualityScore >= 80 ? 'text-green-600' : qualityScore >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
                                                    {qualityScore}%
                                                  </span>
                                                )}
                                              </p>
                                            </div>
                                          </div>
                                          <button
                                            onClick={async () => {
                                              try {
                                                const token = localStorage.getItem('token');
                                                const res = await fetch(`${API}/admin/portfolio-items/${item.id}`, {
                                                  headers: { Authorization: `Bearer ${token}` }
                                                });
                                                if (!res.ok) throw new Error('Ошибка загрузки файла');
                                                const blob = await res.blob();
                                                const url = URL.createObjectURL(blob);
                                                window.open(url, '_blank');
                                              } catch (e) {
                                                showToast('Не удалось открыть файл', 'error');
                                              }
                                            }}
                                            className="ml-3 flex-shrink-0 text-xs text-blue-600 hover:text-blue-800 font-medium"
                                          >
                                            {isImage ? 'Просмотр' : isPdf ? 'Открыть PDF' : 'Скачать'}
                                          </button>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          {request.verification_notes && (
                            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                              <p className="text-xs font-semibold text-yellow-700 mb-1">Примечание администратора:</p>
                              <p className="text-sm text-yellow-800">{request.verification_notes}</p>
                            </div>
                          )}

                          {(request.status === 'pending' || request.status === 'under_review') && (
                            <div className="mt-4 flex gap-2">
                              <button
                                data-testid={`approve-verification-${request.id}`}
                                onClick={() => handleVerification(request.id, 'approve')}
                                className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 text-sm font-medium"
                              >
                                Одобрить
                              </button>
                              <button
                                data-testid={`reject-verification-${request.id}`}
                                onClick={() => {
                                  const reason = prompt("Причина отклонения (опционально):");
                                  handleVerification(request.id, 'reject', reason || '');
                                }}
                                className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 text-sm font-medium"
                              >
                                Отклонить
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              )}
              <AdminPager tabState={tabState} setPage={setTSPage} setPerPage={setTSPerPage} tab="verifications" total={pageTotal} />
              </>
            );
          })()}
        </div>
      )}

      {/* Identity Verifications Tab */}
      {activeTab === 'identity-verifications' && (
        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
            <h3 className="text-lg font-semibold">Заявки на верификацию личности исполнителей</h3>
            <div className="flex flex-wrap gap-2" data-testid="identity-verification-filter-bar">
              {[
                { key: 'pending', label: 'Ожидают' },
                { key: 'approved', label: 'Одобрены' },
                { key: 'rejected', label: 'Отклонены' },
                { key: 'all', label: 'Все' },
              ].map(f => (
                <button
                  key={f.key}
                  data-testid={`filter-identity-verif-${f.key}`}
                  onClick={() => { setIdentityStatusFilter(f.key); setTSPage('identityVerifications', 1); }}
                  className={`text-xs px-3 py-1.5 rounded-full font-medium border transition-colors ${
                    identityStatusFilter === f.key
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400 hover:text-blue-600'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mb-4">
            <input
              type="text"
              placeholder="Поиск по ФИО, email или телефону исполнителя..."
              value={ts('identityVerifications').search || ''}
              onChange={e => setTSSearch('identityVerifications', e.target.value)}
              className="text-sm border border-gray-300 rounded px-3 py-1.5 w-full sm:w-96"
              data-testid="identity-verif-search-input"
              style={{ fontFamily: 'IBM Plex Sans,sans-serif' }}
            />
          </div>

          {identityVerifications.length === 0 ? (
            <p className="text-gray-500 text-sm" data-testid="identity-verif-empty-state">Заявок не найдено</p>
          ) : (
            <div className="space-y-3">
              {identityVerifications.map((request) => {
                const isExpanded = expandedIdentityVerifications.has(request.id);
                const toggleExpand = () => {
                  setExpandedIdentityVerifications(prev => {
                    const next = new Set(prev);
                    if (next.has(request.id)) next.delete(request.id);
                    else next.add(request.id);
                    return next;
                  });
                };
                const statusLabel = request.status === 'approved' ? 'Одобрено' :
                  request.status === 'rejected' ? 'Отклонено' : 'Ожидает';
                const statusClass = request.status === 'approved' ? 'bg-green-100 text-green-800' :
                  request.status === 'rejected' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800';
                const exifWarnings = request.photo_metadata?.warnings || [];

                return (
                  <div key={request.id} className="border border-gray-200 rounded-lg overflow-hidden" data-testid={`identity-verification-card-${request.id}`}>
                    <button
                      className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                      onClick={toggleExpand}
                      data-testid={`identity-verification-toggle-${request.id}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className={`text-xs px-2 py-1 rounded-full font-medium flex-shrink-0 ${statusClass}`}>
                          {statusLabel}
                        </span>
                        <span className="font-medium text-gray-900 truncate">
                          {request.full_name || request.user_name || '—'}
                        </span>
                        <span className="text-xs text-gray-400 hidden sm:inline flex-shrink-0">
                          {request.submitted_at ? formatMsk(request.submitted_at) : ''}
                        </span>
                      </div>
                      <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`} />
                    </button>

                    {isExpanded && (
                      <div className="px-4 pb-4 border-t border-gray-100 bg-gray-50">
                        <div className="pt-4 grid grid-cols-1 md:grid-cols-2 gap-5">
                          <div className="space-y-1.5">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Данные исполнителя</p>
                            <p className="text-sm text-gray-700"><span className="font-medium">Email:</span> {request.user_email || '—'}</p>
                            <p className="text-sm text-gray-700"><span className="font-medium">Телефон:</span> {request.user_phone || '—'}</p>
                            <p className="text-sm text-gray-700"><span className="font-medium">Дата рождения:</span> {request.birth_date || '—'}</p>
                          </div>
                          <div className="space-y-1.5">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Паспортные данные</p>
                            <p className="text-sm text-gray-700"><span className="font-medium">Серия/номер:</span> {request.passport_number_masked || '—'}</p>
                            <p className="text-sm text-gray-700"><span className="font-medium">Кем выдан:</span> {request.passport_issued_by || '—'}</p>
                            <p className="text-sm text-gray-700"><span className="font-medium">Дата выдачи:</span> {request.passport_issue_date || '—'}</p>
                            <p className="text-sm text-gray-700"><span className="font-medium">Адрес регистрации:</span> {request.registration_address || '—'}</p>
                          </div>
                        </div>

                        {request.has_photos ? (
                          <div className="mt-4 flex gap-2">
                            <button
                              data-testid={`identity-view-passport-${request.id}`}
                              onClick={() => viewIdentityPhoto(request.id, 'passport')}
                              className="flex items-center gap-1 text-xs bg-blue-100 text-blue-700 px-3 py-1.5 rounded-md hover:bg-blue-200 font-medium"
                            >
                              <Eye className="w-3.5 h-3.5" /> Фото паспорта
                            </button>
                            <button
                              data-testid={`identity-view-selfie-${request.id}`}
                              onClick={() => viewIdentityPhoto(request.id, 'selfie')}
                              className="flex items-center gap-1 text-xs bg-blue-100 text-blue-700 px-3 py-1.5 rounded-md hover:bg-blue-200 font-medium"
                            >
                              <Eye className="w-3.5 h-3.5" /> Селфи с паспортом
                            </button>
                          </div>
                        ) : (
                          <p className="mt-4 text-xs text-gray-400">Фото удалены (истёк срок хранения 30 дней)</p>
                        )}

                        {exifWarnings.length > 0 && (
                          <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-md">
                            <p className="text-xs font-semibold text-orange-700 mb-1">Анализ фото паспорта (EXIF):</p>
                            {exifWarnings.map((w, i) => (
                              <p key={i} className="text-xs text-orange-700">{w}</p>
                            ))}
                          </div>
                        )}

                        {request.status === 'rejected' && request.rejection_reason && (
                          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                            <p className="text-xs font-semibold text-red-700 mb-1">Причина отклонения:</p>
                            <p className="text-sm text-red-800">{request.rejection_reason}</p>
                          </div>
                        )}

                        {request.status === 'pending' && (
                          <div className="mt-4 flex gap-2">
                            <button
                              data-testid={`identity-approve-${request.id}`}
                              onClick={() => handleApproveIdentity(request.id)}
                              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 text-sm font-medium"
                            >
                              Одобрить
                            </button>
                            <button
                              data-testid={`identity-reject-${request.id}`}
                              onClick={() => handleRejectIdentity(request.id)}
                              className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 text-sm font-medium"
                            >
                              Отклонить
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <AdminPager tabState={tabState} setPage={setTSPage} setPerPage={setTSPerPage} tab="identityVerifications" total={identityVerificationsTotal} />
        </div>
      )}


      {/* Deletions Tab */}
      {activeTab === 'deletions' && (
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
          <h3 className="text-base font-semibold mb-4 flex items-center gap-2" style={{fontFamily:'Manrope,sans-serif'}}><Trash2 size={16} style={{color:'var(--rv-teal)'}}/>Запросы на удаление аккаунтов</h3>
          <AdminSearchSort tabState={tabState} setSearch={setTSSearch} setSort={setTSSort} tab="deletions" placeholder="Поиск по email..." sortOptions={[{value:'date_desc',label:'Запрос: новые'},{value:'date_asc',label:'Запрос: старые'},{value:'del_desc',label:'Удаление: позже'},{value:'del_asc',label:'Удаление: скорее'},{value:'status',label:'По статусу'}]} testPrefix="del" />
          {(() => {
            const pi = deletionRequests;
            const pt = deletionsTotal;
            return (
              <>
                {pi.length === 0 ? (
                  <p className="text-gray-600">Нет активных запросов на удаление</p>
                ) : (
                  <div className="space-y-4">
                    {pi.map((request) => (
                      <div key={request.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <h4 className="font-medium">{request.user_email}</h4>
                            <p className="text-sm text-gray-600">
                              Запрос: {formatMsk(request.request_date)}
                            </p>
                            <p className="text-sm text-gray-600">
                              Удаление: {formatMsk(request.scheduled_deletion_date)}
                            </p>
                            <p className="text-sm text-gray-600">
                              Статус: <span className={`px-2 py-1 rounded text-xs ${
                                request.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                request.status === 'completed' ? 'bg-green-100 text-green-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {request.status === 'pending' ? <><Clock size={12}/>В ожидании</> :
                                 request.status === 'completed' ? <><CheckCircle2 size={12}/>Выполнено</> :
                                 <><XCircle size={12}/>Отменено</>}
                              </span>
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Причина: {request.reason}</p>
                            <p className="text-sm text-gray-600">
                              GDPR запрос: {request.gdpr_request ? 'Да' : 'Нет'}
                            </p>
                            {request.status === 'pending' && (
                              <div className="mt-2">
                                {(() => {
                                  const daysLeft = Math.ceil((new Date(request.scheduled_deletion_date) - new Date()) / (1000 * 60 * 60 * 24));
                                  return daysLeft > 0 ? (
                                    <p className="text-xs text-gray-500" data-testid="deletion-days-left">
                                      Осталось дней: {daysLeft}
                                    </p>
                                  ) : (
                                    <p className="text-xs font-medium text-red-600" data-testid="deletion-overdue">
                                      Срок истёк — аккаунт будет удалён при ближайшей проверке
                                    </p>
                                  );
                                })()}
                              </div>
                            )}
                          </div>
                        </div>
                        {request.status === 'pending' && (
                          <div className="mt-4">
                            <button
                              onClick={() => handleCancelDeletion(request.id)}
                              className="flex items-center gap-1.5 px-4 py-2 rounded-md text-sm"
                              style={{background:'var(--rv-error)', color:'white', border:'none', cursor:'pointer'}}
                            >
                              <Ban size={14}/>Отменить удаление
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <AdminPager tabState={tabState} setPage={setTSPage} setPerPage={setTSPerPage} tab="deletions" total={pt} />
              </>
            );
          })()}
        </div>
      )}

      {/* Archive Tab */}
      {activeTab === 'archive' && (
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
          <h3 className="text-base font-semibold mb-2 flex items-center gap-2" style={{fontFamily:'Manrope,sans-serif'}}><Archive size={16} style={{color:'var(--rv-teal)'}}/>Архив удалённых пользователей</h3>
          <p className="text-sm text-gray-600 mb-2">
            Данные сохранены для соблюдения требований 152-ФЗ и налогового законодательства
          </p>
          <AdminSearchSort tabState={tabState} setSearch={setTSSearch} setSort={setTSSort} tab="archive" placeholder="Поиск по имени или email..." sortOptions={[{value:'date_desc',label:'Дата: новые'},{value:'date_asc',label:'Дата: старые'},{value:'name_asc',label:'Имя А-Я'},{value:'name_desc',label:'Имя Я-А'}]} testPrefix="archive" />
          {(() => {
            const pi = deletedUsersArchive;
            const pt = archiveTotal;
            return (
              <>
                {pi.length === 0 ? (
                  <p className="text-gray-600">Архив пуст</p>
                ) : (
                  <div className="space-y-4">
                    {pi.map((archive) => (
                      <div key={archive.original_user_id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <h4 className="font-medium text-gray-800">{archive.original_name || "Имя недоступно"}</h4>
                            <p className="text-sm text-gray-600">Email: {archive.original_email || "Email недоступен"}</p>
                            <p className="text-sm text-gray-600">Удален: {formatMsk(archive.deletion_date)}</p>
                          </div>
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="text-xs text-gray-500">ID: {archive.original_user_id}</p>
                              <p className="text-xs text-gray-500">
                                Причина хранения: {archive.reason_for_keeping === 'legal_compliance' ? 'Законодательство' : archive.reason_for_keeping}
                              </p>
                            </div>
                            <button
                              onClick={() => handleExportUserData(archive.original_user_id, archive.original_email)}
                              className="flex items-center gap-1 px-3 py-2 rounded-md text-xs"
                              style={{background:'var(--rv-teal)', color:'white', border:'none', cursor:'pointer'}}
                            >
                              <FileText size={12}/>Экспорт данных
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <AdminPager tabState={tabState} setPage={setTSPage} setPerPage={setTSPerPage} tab="archive" total={pt} />
              </>
            );
          })()}
        </div>
      )}

      {/* Payments Tab */}
      {activeTab === 'payments' && (
        <div>
          <h3 className="text-base font-semibold mb-2 flex items-center gap-2" style={{fontFamily:'Manrope,sans-serif'}}><Coins size={16} style={{color:'var(--rv-teal)'}}/>Управление платежами</h3>
          <p className="text-sm mb-3" style={{color:'var(--rv-text-muted)'}}>
            Замороженные эскроу-средства по заданиям в ожидании подтверждения или в споре
          </p>
          <AdminSearchSort tabState={tabState} setSearch={setTSSearch} setSort={setTSSort} tab="payments" placeholder="Поиск по заданию, email..." sortOptions={[{value:'date_desc',label:'Дата: новые'},{value:'date_asc',label:'Дата: старые'},{value:'amount_desc',label:'Сумма: высокая'},{value:'amount_asc',label:'Сумма: низкая'},{value:'status',label:'По статусу'}]} testPrefix="pay" />
          <AdminDateRangeFilter tab="payments" tabState={tabState} setDateFrom={setTSDateFrom} setDateTo={setTSDateTo} />
          {(() => {
            const pi = escrowPayments;
            const pt = escrowTotal;
            return (
              <>
                {pi.length === 0 ? (
                  <p style={{color:'var(--rv-text-muted)'}}>Нет активных платежей</p>
                ) : (
                  <div className="space-y-4">
                    {pi.map((payment) => (
                      <div key={payment.id} className="reversia-card p-4">
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                          <div className="lg:col-span-2">
                            <h4 className="font-medium mb-2" style={{color:'var(--rv-text-primary)'}}>
                              Задание: {payment.task_title}
                            </h4>
                            <div className="text-sm space-y-1" style={{color:'var(--rv-text-muted)'}}>
                              <p><span className="font-medium">Заказчик:</span> {payment.customer_email}</p>
                              <p><span className="font-medium">Исполнитель:</span> {payment.executor_email}</p>
                              <p><span className="font-medium">Сумма:</span> {payment.amount} ₽</p>
                              <p><span className="font-medium">Статус:</span>
                                <span className="ml-1 px-2 py-0.5 rounded-full text-xs font-medium" style={{
                                  background: payment.status === 'escrowed' ? 'rgba(217,119,6,0.1)' : payment.status === 'completed' ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
                                  color: payment.status === 'escrowed' ? '#d97706' : payment.status === 'completed' ? '#10b981' : '#ef4444',
                                }}>
                                  {payment.status === 'escrowed' ? 'Удерживается' : payment.status === 'completed' ? 'Завершён' : 'Отменён'}
                                </span>
                              </p>
                              <p><span className="font-medium">Дата создания:</span> {formatMsk(payment.created_at)}</p>
                              {payment.dispute_reason && (
                                <p><span className="font-medium">Причина спора:</span> {payment.dispute_reason}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col space-y-2">
                            {payment.status === 'escrowed' && (
                              <>
                                <button onClick={() => handleEscrowAction(payment.id, 'release_to_executor')} className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-md text-sm" style={{background:'rgba(16,185,129,0.1)', color:'#10b981', border:'1px solid rgba(16,185,129,0.3)', cursor:'pointer'}}>
                                  <CheckCircle2 size={14}/>Освободить исполнителю
                                </button>
                                <button onClick={() => handleEscrowAction(payment.id, 'refund_to_customer')} className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-md text-sm" style={{background:'rgba(13,148,136,0.08)', color:'var(--rv-teal)', border:'1px solid rgba(13,148,136,0.2)', cursor:'pointer'}}>
                                  <Coins size={14}/>Вернуть заказчику
                                </button>
                                <button onClick={() => viewTaskDetails(payment.task_id)} className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-md text-sm" style={{background:'rgba(107,114,128,0.08)', color:'var(--rv-text-primary)', border:'1px solid var(--rv-border)', cursor:'pointer'}}>
                                  <Eye size={14}/>Детали задания
                                </button>
                              </>
                            )}
                            {payment.status !== 'escrowed' && (
                              <div className="text-xs" style={{color:'var(--rv-text-muted)'}}>
                                Обработан: {formatMsk(payment.processed_at)}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex flex-wrap items-center justify-between gap-3 mt-2">
                  <AdminPager tabState={tabState} setPage={setTSPage} setPerPage={setTSPerPage} tab="payments" total={pt} />
                  <button
                    data-testid="csv-export-payments"
                    onClick={() => {
                      const state = ts('payments');
                      const params = { sort: state.sort || 'date_desc' };
                      if (state.search) params.search = state.search;
                      if (state.dateFrom) params.date_from = state.dateFrom;
                      if (state.dateTo) params.date_to = state.dateTo;
                      exportCSV('/admin/escrow-payments', params, 'items', CSV_COLUMNS.payments, 'platezhi');
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm flex-shrink-0"
                    style={{background:'rgba(13,148,136,0.08)',color:'var(--rv-teal)',border:'1px solid rgba(13,148,136,0.2)',cursor:'pointer',fontFamily:'IBM Plex Sans,sans-serif'}}
                  >
                    <Download size={13}/>Скачать CSV ({pt})
                  </button>
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* Refunds Tab */}
      {activeTab === 'refunds' && (
        <div data-testid="admin-refunds-section">
          <h3 className="text-base font-semibold mb-2 flex items-center gap-2" style={{fontFamily:'Manrope,sans-serif'}}><RotateCcw size={16} style={{color:'var(--rv-teal)'}}/>Возвраты средств заказчикам</h3>
          <p className="text-sm mb-3" style={{color:'var(--rv-text-muted)'}}>
            Все возвраты по отменённым заданиям и решениям споров: основные платежи и доплаты. Сбои требуют ручного возврата в кабинете YooKassa.
          </p>

          <RefundsSummaryBar summary={refundsSummary} onFailedClick={() => { setRefundsStatusFilter('failed'); setTSPage('refunds', 1); }} />
          <AutoRefundOnExpiryToggle showToast={showToast} />
          <div className="flex flex-wrap gap-2 mb-3">
            {[
              { key: '', label: 'Все' },
              { key: 'failed', label: `Сбои${refundsFailedCount ? ` (${refundsFailedCount})` : ''}` },
              { key: 'pending', label: `В обработке${refundsPendingCount ? ` (${refundsPendingCount})` : ''}` },
              { key: 'succeeded', label: 'Возвращено' },
              { key: 'manual_resolved', label: 'Вручную' },
            ].map(f => (
              <button
                key={f.key || 'all'}
                data-testid={`refunds-filter-${f.key || 'all'}`}
                onClick={() => { setRefundsStatusFilter(f.key); setTSPage('refunds', 1); }}
                className="px-3 py-1.5 rounded-md text-sm"
                style={refundsStatusFilter === f.key
                  ? {background:'var(--rv-teal)', color:'white', border:'1px solid var(--rv-teal)', cursor:'pointer'}
                  : {background:'white', color:'var(--rv-text-primary)', border:'1px solid var(--rv-border)', cursor:'pointer'}}
              >
                {f.label}
              </button>
            ))}
          </div>

          <AdminSearchSort tabState={tabState} setSearch={setTSSearch} setSort={setTSSort} tab="refunds" placeholder="Поиск по заданию, email, ID платежа..." sortOptions={[{value:'date_desc',label:'Дата: новые'},{value:'date_asc',label:'Дата: старые'},{value:'amount_desc',label:'Сумма: высокая'},{value:'amount_asc',label:'Сумма: низкая'},{value:'status',label:'По статусу'}]} testPrefix="refunds" />
          <AdminDateRangeFilter tab="refunds" tabState={tabState} setDateFrom={setTSDateFrom} setDateTo={setTSDateTo} />

          {refundsLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto" style={{borderColor:'var(--rv-teal)'}}></div>
            </div>
          ) : refunds.length === 0 ? (
            <p data-testid="refunds-empty" style={{color:'var(--rv-text-muted)'}}>Возвратов не найдено</p>
          ) : (
            <div className="space-y-4">
              {refunds.map((refund) => (
                <div key={refund.id} data-testid={`refund-card-${refund.id}`} className="reversia-card p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <h4 className="font-medium mb-2 break-words" style={{color:'var(--rv-text-primary)'}}>
                        Задание: {refund.task_title}
                      </h4>
                      <div className="text-sm space-y-1" style={{color:'var(--rv-text-muted)'}}>
                        <p><span className="font-medium">Заказчик:</span> {refund.customer_email}</p>
                        <p>
                          <span className="font-medium">Сумма:</span> {refund.amount} ₽
                          <span className="ml-2 px-2 py-0.5 rounded-full text-xs" style={{background:'rgba(107,114,128,0.1)', color:'var(--rv-text-primary)'}}>
                            {REFUND_TYPE_LABELS[refund.refund_type] || 'Основной платёж'}
                          </span>
                          {refund.mock_mode && (
                            <span className="ml-2 px-2 py-0.5 rounded-full text-xs" style={{background:'rgba(217,119,6,0.1)', color:'#d97706'}}>Тестовый режим</span>
                          )}
                        </p>
                        <p>
                          <span className="font-medium">Статус:</span>
                          <span data-testid={`refund-status-${refund.id}`} className="ml-1 px-2 py-0.5 rounded-full text-xs font-medium" style={REFUND_STATUS_STYLES[refund.status] || REFUND_STATUS_STYLES.canceled}>
                            {REFUND_STATUS_LABELS[refund.status] || refund.status}
                          </span>
                          {refund.needs_manual_review && refund.status === 'pending' && (
                            <span data-testid={`refund-manual-review-${refund.id}`} className="ml-1 px-2 py-0.5 rounded-full text-xs font-medium" style={{background:'#FEF2F2', color:'#B91C1C'}} title={refund.stuck_reason || ''}>Зависший: ручной разбор</span>
                          )}
                        </p>
                        {refund.yukassa_payment_id && (
                          <p className="break-all"><span className="font-medium">ID платежа YooKassa:</span> {refund.yukassa_payment_id}</p>
                        )}
                        {refund.yukassa_refund_id && (
                          <p className="break-all"><span className="font-medium">ID возврата:</span> {refund.yukassa_refund_id}</p>
                        )}
                        <p><span className="font-medium">Создан:</span> {refund.created_at ? formatMsk(refund.created_at) : '—'}</p>
                        {refund.admin_note && (
                          <p><span className="font-medium">Заметка администратора:</span> {refund.admin_note}</p>
                        )}
                        {refund.retry_attempts?.length > 0 && (() => {
                          const last = refund.retry_attempts[refund.retry_attempts.length - 1];
                          return (
                            <p data-testid={`refund-retry-info-${refund.id}`}>
                              <span className="font-medium">Повторов: {refund.retry_attempts.length}.</span> Последний: {formatMsk(last.at)}, {last.by} — {last.message}
                            </p>
                          );
                        })()}
                      </div>
                      {refund.error_message && (
                        <div data-testid={`refund-error-${refund.id}`} className="mt-2 p-2 rounded-md text-xs break-words" style={{background:'rgba(239,68,68,0.06)', border:'1px solid rgba(239,68,68,0.25)', color:'#b91c1c'}}>
                          <span className="font-semibold">Ошибка возврата:</span> {refund.error_message}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-2 flex-shrink-0">
                      <button
                        data-testid={`refund-view-task-${refund.id}`}
                        onClick={() => viewTaskDetails(refund.task_id)}
                        className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-md text-sm"
                        style={{background:'rgba(107,114,128,0.08)', color:'var(--rv-text-primary)', border:'1px solid var(--rv-border)', cursor:'pointer'}}
                      >
                        <Eye size={14}/>Детали задания
                      </button>
                      {(refund.status === 'failed' || (refund.status === 'pending' && refund.needs_manual_review)) && refund.source !== 'price_change_payments' && (
                        <button
                          data-testid={`refund-retry-${refund.id}`}
                          onClick={() => retryRefund(refund)}
                          disabled={!!retryingRefundId}
                          className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-md text-sm"
                          style={{background:'rgba(13,148,136,0.1)', color:'var(--rv-teal)', border:'1px solid rgba(13,148,136,0.3)', cursor: retryingRefundId ? 'wait' : 'pointer', opacity: retryingRefundId && retryingRefundId !== refund.id ? 0.5 : 1}}
                        >
                          <RefreshCw size={14} className={retryingRefundId === refund.id ? 'animate-spin' : ''}/>{retryingRefundId === refund.id ? 'Повтор...' : 'Повторить возврат'}
                        </button>
                      )}
                      {(refund.status === 'failed' || refund.status === 'canceled') && (
                        <button
                          data-testid={`refund-mark-resolved-${refund.id}`}
                          onClick={() => markRefundResolved(refund.id)}
                          className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-md text-sm"
                          style={{background:'rgba(16,185,129,0.1)', color:'#10b981', border:'1px solid rgba(16,185,129,0.3)', cursor:'pointer'}}
                        >
                          <CheckCircle2 size={14}/>Возвращено вручную
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3 mt-2">
            <AdminPager tabState={tabState} setPage={setTSPage} setPerPage={setTSPerPage} tab="refunds" total={refundsTotal} />
            <button
              data-testid="csv-export-refunds"
              onClick={() => {
                const state = ts('refunds');
                const params = { sort: state.sort || 'date_desc' };
                if (refundsStatusFilter) params.status = refundsStatusFilter;
                if (state.search) params.search = state.search;
                if (state.dateFrom) params.date_from = state.dateFrom;
                if (state.dateTo) params.date_to = state.dateTo;
                exportCSV('/admin/refunds', params, 'items', CSV_COLUMNS.refunds, 'vozvraty');
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm flex-shrink-0"
              style={{background:'rgba(13,148,136,0.08)',color:'var(--rv-teal)',border:'1px solid rgba(13,148,136,0.2)',cursor:'pointer',fontFamily:'IBM Plex Sans,sans-serif'}}
            >
              <Download size={13}/>Скачать CSV ({refundsTotal})
            </button>
          </div>
        </div>
      )}

      {/* НПД Receipts Tab */}
      {activeTab === 'npd' && (
        <div>
          <h3 className="text-base font-semibold mb-2 flex items-center gap-2" style={{fontFamily:'Manrope,sans-serif'}}><Receipt size={16} style={{color:'var(--rv-teal)'}}/>Налоговые чеки платформы</h3>
          <p className="text-sm text-gray-600 mb-2">
            Автоматически созданные чеки за комиссию платформы (ИНН: 230808284051)
          </p>

          {npdReceiptsLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto" style={{borderColor:'var(--rv-teal)'}}></div>
              <p className="text-sm mt-2" style={{color:'var(--rv-text-muted)'}}>Загрузка налоговых чеков...</p>
            </div>
          ) : (() => {
            const pi = npdReceipts;
            const pt = npdTotal;
            return (
              <>
                <AdminSearchSort tabState={tabState} setSearch={setTSSearch} setSort={setTSSort} tab="npd" placeholder="Поиск по ID чека, заданию..." sortOptions={[{value:'date_desc',label:'Дата: новые'},{value:'date_asc',label:'Дата: старые'},{value:'amount_desc',label:'Сумма: высокая'},{value:'amount_asc',label:'Сумма: низкая'}]} testPrefix="npd" />
                <AdminDateRangeFilter tab="npd" tabState={tabState} setDateFrom={setTSDateFrom} setDateTo={setTSDateTo} />
                {pi.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-600">Налоговые чеки отсутствуют</p>
                    <p className="text-sm text-gray-500 mt-2">Чеки будут создаваться автоматически при подтверждении заданий</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pi.map((receipt) => (
                      <div key={receipt.id} className="bg-white border rounded-lg p-4 shadow-sm">
                        <div className="flex justify-between items-start">
                          <div className="flex-grow">
                            <div className="flex items-center gap-2 mb-2">
                              <h4 className="font-medium text-gray-800">Чек #{receipt.receipt_id}</h4>
                              <span className={`px-2 py-1 rounded text-xs ${receipt.status === 'CREATED' ? 'bg-blue-100 text-blue-800' : receipt.status === 'PRINTED' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>{receipt.status}</span>
                              {receipt.is_mock && <span className="px-2 py-1 rounded text-xs bg-orange-100 text-orange-800">MOCK</span>}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600 mb-2">
                              <div><strong>Сумма:</strong> {receipt.amount}₽</div>
                              <div><strong>Создан:</strong> {formatMsk(receipt.created_at)}</div>
                              <div><strong>ИНН платформы:</strong> {receipt.platform_inn}</div>
                              {receipt.task_info && <div><strong>Задание:</strong> {receipt.task_info.title}</div>}
                            </div>
                            <div className="text-sm text-gray-700"><strong>Услуга:</strong> {receipt.service_description}</div>
                            {receipt.task_info && (
                              <div className="mt-2 text-xs text-gray-500">
                                Задание: {receipt.task_info.title} (₽{receipt.task_info.price}) - {receipt.task_info.service_type}
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col gap-2 ml-4">
                            {receipt.receipt_url && !receipt.is_mock && (
                              <button onClick={() => window.open(receipt.receipt_url, '_blank')} className="flex items-center gap-1 px-3 py-1 rounded text-xs" style={{background:'var(--rv-teal)', color:'white', border:'none', cursor:'pointer'}}>
                                <FileText size={12}/>Открыть чек
                              </button>
                            )}
                            <button
                              onClick={async () => {
                                try {
                                  const response = await axios.get(`${API}/admin/npd-receipts/${receipt.receipt_id}/status`);
                                  showToast(`Статус: ${response.data.status}`, 'success');
                                } catch (error) { showToast('Ошибка проверки статуса', 'error'); }
                              }}
                              className="flex items-center gap-1 px-3 py-1 rounded text-xs"
                              style={{background:'rgba(107,114,128,0.15)', color:'var(--rv-text-primary)', border:'none', cursor:'pointer'}}
                            >
                              <Search size={12}/>Статус
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                    <div className="text-center mt-2">
                      <button onClick={fetchNpdReceipts} className="flex items-center gap-1.5 px-4 py-2 rounded-md mx-auto" style={{background:'var(--rv-teal)', color:'white', border:'none', cursor:'pointer', fontFamily:'IBM Plex Sans,sans-serif'}}>
                        <RefreshCw size={14}/>Обновить список
                      </button>
                    </div>
                  </div>
                )}
                <div className="flex flex-wrap items-center justify-between gap-3 mt-2">
                  <AdminPager tabState={tabState} setPage={setTSPage} setPerPage={setTSPerPage} tab="npd" total={pt} />
                  <button
                    data-testid="csv-export-npd"
                    onClick={() => {
                      const state = ts('npd');
                      const params = { sort: state.sort || 'date_desc' };
                      if (state.search) params.search = state.search;
                      if (state.dateFrom) params.date_from = state.dateFrom;
                      if (state.dateTo) params.date_to = state.dateTo;
                      exportCSV('/admin/npd-receipts', params, 'receipts', CSV_COLUMNS.npd, 'cheki_npd');
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm flex-shrink-0"
                    style={{background:'rgba(13,148,136,0.08)',color:'var(--rv-teal)',border:'1px solid rgba(13,148,136,0.2)',cursor:'pointer',fontFamily:'IBM Plex Sans,sans-serif'}}
                  >
                    <Download size={13}/>Скачать CSV ({pt})
                  </button>
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* Payouts Tab - НОВАЯ ВКЛАДКА */}
      {activeTab === 'payouts' && (
        <div>
          <h3 className="text-base font-semibold mb-3 flex items-center gap-2" style={{fontFamily:'Manrope,sans-serif'}}><Wallet size={16} style={{color:'var(--rv-teal)'}}/>Управление выплатами исполнителям</h3>
          <div className="p-3 rounded mb-4" style={{background:'rgba(217,119,6,0.06)', border:'1px solid rgba(217,119,6,0.2)'}}>
            <p className="text-sm" style={{color:'#92400e'}}>
              <strong>Текущий режим:</strong> {payoutMode === 'auto' ? 'Автоматический' : 'Ручной'} | 
              <button 
                onClick={() => setActiveTab('settings')}
                className="ml-2 text-blue-600 hover:text-blue-800 underline"
              >
                Изменить режим
              </button>
            </p>
            <p className="text-xs text-yellow-700 mt-1">
              {payoutMode === 'auto' 
                ? 'Выплаты происходят автоматически через YuKassa'
                : 'Выплаты требуют ручного подтверждения администратором'
              }
            </p>
          </div>

          {payoutsLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-600 mt-2">Загрузка выплат...</p>
            </div>
          ) : (() => {
            const pi = pendingPayouts;
            const pt = pendingPayoutsTotal;
            return (
              <>
                <AdminSearchSort tabState={tabState} setSearch={setTSSearch} setSort={setTSSort} tab="payouts" placeholder="Поиск по исполнителю, email, заданию..." sortOptions={[{value:'date_desc',label:'Дата: новые'},{value:'date_asc',label:'Дата: старые'},{value:'amount_desc',label:'Сумма: высокая'},{value:'amount_asc',label:'Сумма: низкая'}]} testPrefix="payout" />
                <AdminDateRangeFilter tab="payouts" tabState={tabState} setDateFrom={setTSDateFrom} setDateTo={setTSDateTo} />
                {pt === 0 ? (
                  <div className="text-center py-6">
                    <p className="text-gray-600">Нет ожидающих выплат</p>
                    <p className="text-sm text-gray-500 mt-2">Выплаты появятся после подтверждения заданий заказчиками</p>
                    <div className="mt-6 flex items-center justify-center gap-3">
                      <button onClick={() => { fetchPayoutHistory(); setActiveTab('payout-history'); }} className="flex items-center gap-1.5 px-4 py-2 rounded-md text-sm" style={{background:'rgba(13,148,136,0.08)', color:'var(--rv-teal)', border:'1px solid rgba(13,148,136,0.2)', cursor:'pointer'}}>
                        <ListChecks size={14}/>История выплат
                      </button>
                      <button onClick={() => { fetchPendingPayouts(); fetchPayoutHistory(); }} className="flex items-center gap-1.5 px-4 py-2 rounded-md text-sm" style={{background:'var(--rv-teal)', color:'white', border:'none', cursor:'pointer'}}>
                        <RefreshCw size={14}/>Обновить данные
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <h4 className="font-semibold mb-3 flex items-center gap-1.5" style={{color:'#d97706'}}>
                      <Clock size={14}/>Ожидают ручной выплаты ({pt}) — {pendingPayoutsAmount.toFixed(0)}₽
                    </h4>
                    {pi.length === 0 ? (
                      <p className="text-gray-500 text-sm">Ничего не найдено по запросу</p>
                    ) : (
                      <div className="space-y-4">
                        {pi.map((payout) => (
                          <div key={payout.id} className="border p-4 rounded-lg" style={{background:'rgba(217,119,6,0.04)', borderColor:'rgba(217,119,6,0.2)'}}>
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <h5 className="font-medium" style={{color:'var(--rv-text-primary)'}}>{payout.task_title}</h5>
                                <div className="mt-2 grid grid-cols-2 gap-4 text-sm">
                                  <div><span className="font-medium">Исполнитель:</span> {payout.executor_name}</div>
                                  <div><span className="font-medium">Телефон:</span> {payout.executor_phone}</div>
                                  <div><span className="font-medium">СБП:</span> {payout.executor_sbp_phone || "Не указан"}</div>
                                  <div><span className="font-medium">Email:</span> {payout.executor_email}</div>
                                  <div><span className="font-medium">К выплате:</span> <span className="font-bold" style={{color:'#10b981'}}> {payout.amount}₽</span></div>
                                  <div><span className="font-medium">Комиссия:</span> {payout.commission_amount}₽ ({(payout.commission_rate * 100).toFixed(0)}%)</div>
                                </div>
                                <div className="mt-2 text-xs" style={{color:'var(--rv-text-muted)'}}>
                                  Подтверждено: {formatMsk(payout.confirmed_at)}
                                </div>
                              </div>
                              <div className="flex flex-col gap-2 ml-4">
                                <button onClick={() => { if (window.confirm(`Подтвердить выплату через СБП исполнителю ${payout.executor_name} на сумму ${payout.amount}₽?`)) { markPayoutAsPaid(payout.id, 'sbp', 'Выплата через СБП - перевод выполнен'); } }} className="flex items-center gap-1 px-3 py-1 rounded text-xs" style={{background:'rgba(16,185,129,0.1)', color:'#10b981', border:'1px solid rgba(16,185,129,0.3)', cursor:'pointer'}}>
                                  <CheckCircle2 size={12}/>СБП
                                </button>
                                <button onClick={() => { if (window.confirm(`Подтвердить выплату на банковскую карту исполнителю ${payout.executor_name} на сумму ${payout.amount}₽?`)) { markPayoutAsPaid(payout.id, 'card', 'Выплата на банковскую карту - перевод выполнен'); } }} className="flex items-center gap-1 px-3 py-1 rounded text-xs" style={{background:'rgba(13,148,136,0.1)', color:'var(--rv-teal)', border:'1px solid rgba(13,148,136,0.3)', cursor:'pointer'}}>
                                  <CreditCard size={12}/>Карта
                                </button>
                                <button onClick={() => { const method = window.prompt("Способ выплаты:", "Наличные"); if (method) { const confirmMsg = `Подтвердить выплату способом "${method}" исполнителю ${payout.executor_name} на сумму ${payout.amount}₽?`; if (window.confirm(confirmMsg)) { markPayoutAsPaid(payout.id, 'other', `Выплата через: ${method}`); } } }} className="flex items-center gap-1 px-3 py-1 rounded text-xs" style={{background:'rgba(107,114,128,0.1)', color:'var(--rv-text-primary)', border:'1px solid var(--rv-border)', cursor:'pointer'}}>
                                  <FileText size={12}/>Другое
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex flex-wrap items-center justify-between gap-3 mt-2">
                      <AdminPager tabState={tabState} setPage={setTSPage} setPerPage={setTSPerPage} tab="payouts" total={pt} />
                      <button
                        data-testid="csv-export-payouts"
                        onClick={() => {
                          const state = ts('payouts');
                          const params = { sort: state.sort || 'date_desc' };
                          if (state.search) params.search = state.search;
                          if (state.dateFrom) params.date_from = state.dateFrom;
                          if (state.dateTo) params.date_to = state.dateTo;
                          exportCSV('/admin/pending-payouts', params, 'items', CSV_COLUMNS.payouts, 'viplaty');
                        }}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm flex-shrink-0"
                        style={{background:'rgba(13,148,136,0.08)',color:'var(--rv-teal)',border:'1px solid rgba(13,148,136,0.2)',cursor:'pointer',fontFamily:'IBM Plex Sans,sans-serif'}}
                      >
                        <Download size={13}/>Скачать CSV ({pt})
                      </button>
                    </div>
                    <div className="text-center mt-4 flex items-center justify-center gap-3">
                      <button onClick={fetchPendingPayouts} className="flex items-center gap-1.5 px-4 py-2 rounded-md" style={{background:'var(--rv-teal)', color:'white', border:'none', cursor:'pointer', fontFamily:'IBM Plex Sans,sans-serif'}}>
                        <RefreshCw size={14}/>Обновить список
                      </button>
                      <button onClick={() => { fetchPayoutHistory(); setActiveTab('payout-history'); }} className="flex items-center gap-1.5 px-4 py-2 rounded-md" style={{background:'rgba(13,148,136,0.08)', color:'var(--rv-teal)', border:'1px solid rgba(13,148,136,0.2)', cursor:'pointer', fontFamily:'IBM Plex Sans,sans-serif'}}>
                        <ListChecks size={14}/>История выплат
                      </button>
                    </div>
                  </>
                )}
              </>
            );
          })()}
        </div>
      )}

      {/* Payout History Tab */}
      {activeTab === 'payout-history' && (
        <div>
          <h3 className="text-base font-semibold mb-3 flex items-center gap-2" style={{fontFamily:'Manrope,sans-serif'}}><ListChecks size={16} style={{color:'var(--rv-teal)'}}/>История выплат</h3>
          <div className="mb-4">
            <button
              onClick={() => setActiveTab('payouts')}
              className="flex items-center gap-1.5 px-4 py-2 rounded-md text-sm"
              style={{background:'rgba(13,148,136,0.08)', color:'var(--rv-teal)', border:'1px solid rgba(13,148,136,0.2)', cursor:'pointer'}}
            >
              ← Назад к ожидающим
            </button>
          </div>
          
          {payoutHistoryTotal === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-600">История выплат пуста</p>
            </div>
          ) : (() => {
            const pi = payoutHistory.manual_payouts;
            const pt = payoutHistoryTotal;
            return (
              <>
                <div className="p-3 rounded mb-3" style={{background:'rgba(16,185,129,0.06)', border:'1px solid rgba(16,185,129,0.2)'}}>
                  <h4 className="font-semibold flex items-center gap-1.5" style={{color:'#10b981'}}>
                    <CheckCircle2 size={14}/>Выплачено вручную: {payoutHistory.manual_total}₽
                  </h4>
                </div>
                <AdminSearchSort tabState={tabState} setSearch={setTSSearch} setSort={setTSSort} tab="payoutHistory" placeholder="Поиск по исполнителю, email, заданию..." sortOptions={[{value:'date_desc',label:'Дата: новые'},{value:'date_asc',label:'Дата: старые'},{value:'amount_desc',label:'Сумма: высокая'},{value:'amount_asc',label:'Сумма: низкая'}]} testPrefix="payout-hist" />
                <AdminDateRangeFilter tab="payoutHistory" tabState={tabState} setDateFrom={setTSDateFrom} setDateTo={setTSDateTo} />
                <div className="space-y-4">
                  {pi.map((payout) => (
                    <div key={payout.id} className="bg-white border border-gray-200 p-4 rounded-lg">
                      <div className="flex justify-between items-start">
                        <div>
                          <h5 className="font-medium text-gray-900">{payout.task_title}</h5>
                          <div className="text-sm mt-1" style={{color:'var(--rv-text-muted)'}}>
                            {payout.executor_name} • {payout.amount}₽ •
                            {payout.payment_method === 'sbp' ? 'СБП' : payout.payment_method === 'card' ? 'Карта' : 'Другое'}
                          </div>
                          <div className="text-xs mt-1" style={{color:'var(--rv-text-muted)'}}>
                            Выплачено: {formatMsk(payout.paid_manually_at)}
                            • Администратор: {payout.paid_by_admin}
                          </div>
                          {payout.admin_notes && (
                            <div className="text-xs mt-1 flex items-center gap-1" style={{color:'var(--rv-teal)'}}>
                              <MessageSquare size={11}/>{payout.admin_notes}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3 mt-2">
                  <AdminPager tabState={tabState} setPage={setTSPage} setPerPage={setTSPerPage} tab="payoutHistory" total={pt} />
                  <button
                    data-testid="csv-export-payout-history"
                    onClick={() => {
                      const state = ts('payoutHistory');
                      const params = { sort: state.sort || 'date_desc' };
                      if (state.search) params.search = state.search;
                      if (state.dateFrom) params.date_from = state.dateFrom;
                      if (state.dateTo) params.date_to = state.dateTo;
                      exportCSV('/admin/payout-history', params, 'manual_payouts', CSV_COLUMNS.payoutHistory, 'istoriya_viplat');
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm flex-shrink-0"
                    style={{background:'rgba(13,148,136,0.08)',color:'var(--rv-teal)',border:'1px solid rgba(13,148,136,0.2)',cursor:'pointer',fontFamily:'IBM Plex Sans,sans-serif'}}
                  >
                    <Download size={13}/>Скачать CSV ({pt})
                  </button>
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* Users Statistics Tab */}
      {activeTab === 'stats' && (
        <div>
          <h3 className="text-base font-semibold mb-4 flex items-center gap-2" style={{fontFamily:'Manrope,sans-serif'}}><BarChart2 size={16} style={{color:'var(--rv-teal)'}}/>Статистика пользователей</h3>
          
          {usersStatsLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto" style={{borderColor:'var(--rv-teal)'}}></div>
              <p className="mt-2" style={{color:'var(--rv-text-muted)'}}>Загрузка статистики...</p>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="reversia-card p-4 text-center">
                  <div className="text-2xl font-bold" style={{color:'var(--rv-teal)', fontFamily:'Manrope,sans-serif'}}>{usersStats.users?.total_customers || 0}</div>
                  <div className="text-sm flex items-center justify-center gap-1 mt-1" style={{color:'var(--rv-text-muted)'}}><Users size={13}/>Заказчики</div>
                  <div className="text-xs mt-1" style={{color:'var(--rv-text-muted)'}}>
                    +{usersStats.users?.recent_customers || 0} за неделю
                  </div>
                </div>
                
                <div className="reversia-card p-4 text-center">
                  <div className="text-2xl font-bold" style={{color:'#10b981', fontFamily:'Manrope,sans-serif'}}>{usersStats.users?.total_executors || 0}</div>
                  <div className="text-sm flex items-center justify-center gap-1 mt-1" style={{color:'var(--rv-text-muted)'}}><User size={13}/>Исполнители</div>
                  <div className="text-xs mt-1" style={{color:'var(--rv-text-muted)'}}>
                    +{usersStats.users?.recent_executors || 0} за неделю
                  </div>
                </div>
                
                <div className="reversia-card p-4 text-center">
                  <div className="text-2xl font-bold" style={{color:'#3b82f6', fontFamily:'Manrope,sans-serif'}}>{usersStats.users?.active_executors || 0}</div>
                  <div className="text-sm flex items-center justify-center gap-1 mt-1" style={{color:'var(--rv-text-muted)'}}><CheckCircle2 size={13}/>Активные</div>
                  <div className="text-xs mt-1" style={{color:'var(--rv-text-muted)'}}>
                    С выполненными заданиями
                  </div>
                </div>
                
                <div className="reversia-card p-4 text-center">
                  <div className="text-2xl font-bold" style={{color:'#d97706', fontFamily:'Manrope,sans-serif'}}>{pendingPayouts.length}</div>
                  <div className="text-sm flex items-center justify-center gap-1 mt-1" style={{color:'var(--rv-text-muted)'}}><Wallet size={13}/>Ожидают выплат</div>
                  <div className="text-xs mt-1" style={{color:'var(--rv-text-muted)'}}>
                    {pendingPayouts.reduce((sum, p) => sum + p.amount, 0).toFixed(0)}₽ общая сумма
                  </div>
                </div>
              </div>
              
              <div className="reversia-card p-4">
                <h4 className="font-semibold mb-3 flex items-center gap-2" style={{fontFamily:'Manrope,sans-serif'}}><ListChecks size={14} style={{color:'var(--rv-teal)'}}/>Статистика заданий по статусам</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  {Object.entries(usersStats.tasks?.by_status || {}).map(([status, count]) => (
                    <div key={status} className="text-center">
                      <div className="font-bold text-lg">{count}</div>
                      <div style={{color:'var(--rv-text-muted)'}}>{translateTaskStatus(status)}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Список всех заданий */}
              <div className="reversia-card p-4">
                <h4 className="font-semibold mb-3 flex items-center gap-2" style={{fontFamily:'Manrope,sans-serif'}}>
                  <ClipboardList size={14} style={{color:'var(--rv-teal)'}}/>Все задания
                </h4>

                {/* Фильтр по статусу */}
                <div className="flex flex-wrap gap-2 mb-3" data-testid="admin-tasks-status-filter">
                  {[
                    { key: '', label: 'Все' },
                    { key: 'pending', label: 'Ожидает' },
                    { key: 'pending_payment', label: 'Ожид. оплаты' },
                    { key: 'paid', label: 'Оплачено' },
                    { key: 'discussing', label: 'Обсуждение' },
                    { key: 'in_progress', label: 'В работе' },
                    { key: 'completed', label: 'Завершено' },
                    { key: 'confirmed', label: 'Подтверждено' },
                    { key: 'cancelled', label: 'Отменено' },
                    { key: 'disputed', label: 'Спор' },
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      data-testid={`admin-tasks-filter-${key || 'all'}`}
                      onClick={() => { setAdminTaskStatusFilter(key); setTSPage('adminTasks', 1); }}
                      className="px-2 py-1 rounded text-xs font-medium"
                      style={{
                        background: adminTaskStatusFilter === key ? 'var(--rv-teal)' : 'rgba(0,0,0,0.04)',
                        color: adminTaskStatusFilter === key ? 'white' : 'var(--rv-text-muted)',
                        border: 'none', cursor: 'pointer'
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                <AdminSearchSort
                  tabState={tabState}
                  setSearch={setTSSearch}
                  setSort={setTSSort}
                  tab="adminTasks"
                  placeholder="Поиск по заказчику, email, названию..."
                  sortOptions={[
                    {value:'date_desc', label:'Дата: новые'},
                    {value:'date_asc',  label:'Дата: старые'},
                    {value:'amount_desc', label:'Цена: высокая'},
                    {value:'amount_asc',  label:'Цена: низкая'},
                    {value:'status',   label:'По статусу'},
                  ]}
                  testPrefix="admin-tasks"
                />

                {adminTasksLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 mx-auto" style={{borderColor:'var(--rv-teal)'}}></div>
                    <p className="mt-2 text-sm" style={{color:'var(--rv-text-muted)'}}>Загрузка заданий...</p>
                  </div>
                ) : (() => {
                  const pi = adminAllTasks;
                  const pt = adminTasksTotal;
                  return (
                    <>
                      {pt === 0 ? (
                        <div className="text-center py-6" style={{color:'var(--rv-text-muted)'}}>
                          Заданий не найдено
                        </div>
                      ) : (
                        <div className="overflow-x-auto mt-2">
                          <table className="w-full text-sm" style={{borderCollapse:'collapse'}}>
                            <thead>
                              <tr style={{borderBottom:'1px solid var(--rv-border)'}}>
                                <th className="text-left py-2 pr-3 text-xs font-medium" style={{color:'var(--rv-text-muted)'}}>Задание</th>
                                <th className="text-left py-2 pr-3 text-xs font-medium" style={{color:'var(--rv-text-muted)'}}>Статус</th>
                                <th className="text-left py-2 pr-3 text-xs font-medium" style={{color:'var(--rv-text-muted)'}}>Цена</th>
                                <th className="text-left py-2 pr-3 text-xs font-medium hidden md:table-cell" style={{color:'var(--rv-text-muted)'}}>Заказчик</th>
                                <th className="text-left py-2 pr-3 text-xs font-medium" style={{color:'var(--rv-text-muted)'}}>Исполнитель</th>
                                <th className="text-left py-2 pr-3 text-xs font-medium hidden md:table-cell" style={{color:'var(--rv-text-muted)'}}>Место</th>
                                <th className="text-left py-2 text-xs font-medium hidden lg:table-cell" style={{color:'var(--rv-text-muted)'}}>Дата</th>
                              </tr>
                            </thead>
                            <tbody>
                              {pi.map((task, i) => (
                                <tr key={task.id} data-testid="admin-task-row" style={{borderBottom:'1px solid var(--rv-border)', background: i%2===0 ? 'white' : 'rgba(0,0,0,0.015)'}}>
                                  <td className="py-2 pr-3" style={{maxWidth:'180px'}}>
                                    <div className="font-medium text-xs truncate" style={{color:'var(--rv-text-primary)'}}>{task.title}</div>
                                    <ExpiredBadge task={task} testId={`admin-task-expired-${task.id}`} />
                                    <div className="text-xs" style={{color:'var(--rv-text-muted)'}}>{translateServiceType(task.service_type)}</div>
                                  </td>
                                  <td className="py-2 pr-3">
                                    <span className="px-2 py-0.5 rounded-full text-xs whitespace-nowrap" style={{background: getTaskStatusBg(task.status), color: getTaskStatusColor(task.status)}}>
                                      {translateTaskStatus(task.status)}
                                    </span>
                                  </td>
                                  <td className="py-2 pr-3">
                                    <span className="font-semibold text-xs">{task.price}₽</span>
                                  </td>
                                  <td className="py-2 pr-3 hidden md:table-cell">
                                    <div className="text-xs" style={{color:'var(--rv-text-primary)'}}>{task.customer_name || '—'}</div>
                                    <div className="text-xs" style={{color:'var(--rv-text-muted)'}}>{task.customer_email || '—'}</div>
                                  </td>
                                  <td className="py-2 pr-3" data-testid="admin-task-executor">
                                    {task.status === 'discussing' && (task.discussing_executor_name || task.discussing_executor_email) ? (
                                      <div>
                                        <span className="inline-block px-1.5 py-0.5 rounded text-xs mb-0.5" style={{background:'rgba(217,119,6,0.1)', color:'#d97706'}}>Обсуждает</span>
                                        <div className="text-xs" style={{color:'var(--rv-text-primary)'}}>{task.discussing_executor_name || '—'}</div>
                                        <div className="text-xs" style={{color:'var(--rv-text-muted)'}}>{task.discussing_executor_email || '—'}</div>
                                      </div>
                                    ) : (task.executor_name || task.executor_email) ? (
                                      <div>
                                        <div className="text-xs" style={{color:'var(--rv-text-primary)'}}>{task.executor_name || '—'}</div>
                                        <div className="text-xs" style={{color:'var(--rv-text-muted)'}}>{task.executor_email || '—'}</div>
                                      </div>
                                    ) : (
                                      <span className="text-xs" style={{color:'var(--rv-text-muted)'}}>—</span>
                                    )}
                                  </td>
                                  <td className="py-2 pr-3 hidden md:table-cell" style={{maxWidth:'140px'}}>
                                    <div className="text-xs truncate" style={{color:'var(--rv-text-muted)'}}>
                                      {task.location_required === false ? 'Удалённо' : (task.location || '—')}
                                    </div>
                                  </td>
                                  <td className="py-2 hidden lg:table-cell">
                                    <div className="text-xs" style={{color:'var(--rv-text-muted)'}}>
                                      {task.created_at ? formatMskDate(task.created_at) : '—'}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      <AdminPager tabState={tabState} setPage={setTSPage} setPerPage={setTSPerPage} tab="adminTasks" total={pt} />
                    </>
                  );
                })()}
              </div>
              
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={() => { fetchUsersStats(); fetchAdminTasks(); }}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-md"
                  style={{background:'var(--rv-teal)', color:'white', border:'none', cursor:'pointer'}}
                >
                  <RefreshCw size={14}/>Обновить статистику
                </button>
                <button
                  onClick={() => setActiveTab('payouts')}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-md"
                  style={{background:'rgba(217,119,6,0.08)', color:'#d97706', border:'1px solid rgba(217,119,6,0.2)', cursor:'pointer'}}
                >
                  <Wallet size={14}/>Перейти к выплатам
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Disputes Tab */}
      {activeTab === 'disputes' && (
        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <h3 className="text-lg font-semibold text-red-700">Споры и жалобы</h3>
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'active',                   label: 'Активные' },
                { key: 'awaiting_customer_action', label: 'Ожидает заказчика' },
                { key: 'resolved',                 label: 'Завершённые' },
                { key: '',                         label: 'Все' },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => { setDisputeStatusFilter(key); setTSPage('disputes', 1); }}
                  className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                    disputeStatusFilter === key
                      ? 'bg-red-100 border-red-400 text-red-700 font-medium'
                      : 'border-gray-300 text-gray-500 hover:border-gray-400'
                  }`}
                  data-testid={`disputes-filter-${key || 'all'}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          {adminDisputesLoading ? (
            <p className="text-gray-600">Загрузка...</p>
          ) : (() => {
            const pi = adminDisputes;
            const pt = disputesTotal;
            return (
              <>
                <AdminSearchSort tabState={tabState} setSearch={setTSSearch} setSort={setTSSort} tab="disputes" placeholder="Поиск по заданию, email..." sortOptions={[{value:'date_desc',label:'Дата: новые'},{value:'date_asc',label:'Дата: старые'},{value:'status',label:'По статусу'}]} testPrefix="disputes" />
                <AdminDateRangeFilter tab="disputes" tabState={tabState} setDateFrom={setTSDateFrom} setDateTo={setTSDateTo} />
                {pi.length === 0 ? (
                  <p className="text-gray-600">Споров и жалоб нет</p>
                ) : (
                  <div className="space-y-4">
                    {pi.map((dispute) => {
                const isPending = dispute.status === 'manual_review' || dispute.status === 'pending';
                const isRework = dispute.status === 'manual_review' || dispute.status === 'pending';
                const revisionRound = dispute.revision_round || 0;
                const statusLabel = {
                  manual_review: 'Ожидает решения',
                  pending: 'Ожидает решения',
                  awaiting_customer_action: 'Ожидает выбора заказчика',
                  auto_resolved: 'Разрешён автоматически',
                  resolved: 'Разрешён',
                }[dispute.status] || dispute.status;
                const isAwaitingCustomer = dispute.status === 'awaiting_customer_action';

                return (
                  <div key={dispute.id} className={`border rounded-lg p-4 ${isPending ? 'border-red-300 bg-red-50' : isAwaitingCustomer ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-white'}`}>
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h4 className="font-medium text-gray-800">{dispute.task_title || dispute.task_id}</h4>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${isPending ? 'bg-red-100 text-red-700' : isAwaitingCustomer ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                            {statusLabel}
                          </span>
                          {revisionRound > 0 && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-medium">
                              Раунд {revisionRound + 1} — финальное решение
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-sm text-gray-500">
                        {dispute.created_at ? formatMsk(dispute.created_at) : ''}
                      </div>
                    </div>
                    <div className="text-sm space-y-1 text-gray-700">
                      <div><strong>Инициатор:</strong> {dispute.initiator_email || dispute.created_by || '—'}</div>
                      <div><strong>Заказчик:</strong> <span className="text-blue-700">{dispute.customer_name_label || '—'}</span> — {dispute.customer_email || '—'}</div>
                      <div><strong>Исполнитель:</strong> <span className="text-green-700">{dispute.executor_name_label || '—'}</span> — {dispute.executor_email || '—'}</div>
                      <div><strong>Причина:</strong> {dispute.reason}</div>
                      <div><strong>Описание:</strong> {dispute.description}</div>
                      {dispute.task_price > 0 && <div><strong>Сумма задания:</strong> {dispute.task_price} ₽</div>}
                      {dispute.revision_deadline && (
                        <div className="text-orange-600 text-xs">
                          Дедлайн доработки: {formatMsk(dispute.revision_deadline)}
                        </div>
                      )}
                    </div>
                    {dispute.ai_recommendation && (
                      <div className="mt-3 p-3 rounded-lg border" data-testid={`ai-recommendation-${dispute.id}`} style={{background:'rgba(124,58,237,0.05)', borderColor:'rgba(124,58,237,0.25)'}}>
                        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                          <Sparkles size={14} style={{color:'#7c3aed'}}/>
                          <span className="text-sm font-semibold" style={{color:'#7c3aed'}}>Рекомендация AI:</span>
                          <span className="text-sm font-medium text-gray-800">
                            {AI_DISPUTE_ACTION_LABELS[dispute.ai_recommendation.recommended_action] || dispute.ai_recommendation.recommended_action}
                          </span>
                          <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{
                            background: `${AI_DISPUTE_CONFIDENCE_COLORS[dispute.ai_recommendation.confidence] || '#6b7280'}1a`,
                            color: AI_DISPUTE_CONFIDENCE_COLORS[dispute.ai_recommendation.confidence] || '#6b7280',
                          }}>
                            Уверенность: {AI_DISPUTE_CONFIDENCE_LABELS[dispute.ai_recommendation.confidence] || dispute.ai_recommendation.confidence}
                          </span>
                        </div>
                        {dispute.ai_recommendation.reasoning && (
                          <p className="text-sm text-gray-700">{dispute.ai_recommendation.reasoning}</p>
                        )}
                        {dispute.ai_recommendation.key_points?.length > 0 && (
                          <ul className="mt-1.5 text-xs text-gray-600 list-disc list-inside space-y-0.5">
                            {dispute.ai_recommendation.key_points.map((kp, idx) => (
                              <li key={idx}>{kp}</li>
                            ))}
                          </ul>
                        )}
                        <p className="text-xs text-gray-400 mt-1.5">
                          AI не принимает решение — финальное действие подтверждает администратор вручную.
                        </p>
                      </div>
                    )}
                    {isPending && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {siteConfig.ai_dispute_assistant_enabled && (
                          <button
                            data-testid={`dispute-ai-analyze-btn-${dispute.id}`}
                            onClick={() => handleAiAnalyzeDispute(dispute.id)}
                            disabled={!!aiAnalyzing[dispute.id]}
                            className="px-3 py-1.5 text-white rounded-md text-sm flex items-center gap-1.5 disabled:opacity-60"
                            style={{background:'#7c3aed'}}
                          >
                            <Sparkles size={14}/>{aiAnalyzing[dispute.id] ? 'Анализирую...' : (dispute.ai_recommendation ? 'Обновить AI-анализ' : 'AI-анализ')}
                          </button>
                        )}
                        <button
                          data-testid={`dispute-executor-btn-${dispute.id}`}
                          onClick={() => handleEscrowAction(dispute.payment_id || dispute.task_id, 'release_to_executor')}
                          className="px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm"
                        >
                          В пользу исполнителя
                        </button>
                        {revisionRound === 0 && (
                          <button
                            data-testid={`dispute-rework-btn-${dispute.id}`}
                            onClick={() => handleDisputeRework(dispute.payment_id || dispute.task_id)}
                            className="px-3 py-1.5 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 text-sm"
                          >
                            Доработка (48ч)
                          </button>
                        )}
                        <button
                          data-testid={`dispute-customer-btn-${dispute.id}`}
                          onClick={() => handleEscrowAction(dispute.payment_id || dispute.task_id, 'refund_to_customer')}
                          className="px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
                        >
                          {revisionRound > 0 ? 'В пользу заказчика (финально)' : 'В пользу заказчика'}
                        </button>
                        <button
                          onClick={() => fetchTaskDetails(dispute.task_id)}
                          className="px-3 py-1.5 bg-gray-600 text-white rounded-md hover:bg-gray-700 text-sm"
                        >
                          Детали задания
                        </button>
                        <button
                          data-testid={`dispute-clarify-btn-${dispute.id}`}
                          onClick={() => setClarificationForms(prev => ({
                            ...prev,
                            [dispute.id]: { show: !prev[dispute.id]?.show, target: prev[dispute.id]?.target || 'customer', text: prev[dispute.id]?.text || '' }
                          }))}
                          className="px-3 py-1.5 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-sm"
                        >
                          Запросить уточнение
                        </button>
                      </div>
                    )}
                    {isAwaitingCustomer && (
                      <div className="mt-3">
                        <p className="text-sm text-blue-700 font-medium mb-2">Решение принято в пользу заказчика. Ожидается выбор заказчика.</p>
                        <button
                          onClick={() => fetchTaskDetails(dispute.task_id)}
                          className="px-3 py-1.5 bg-gray-600 text-white rounded-md hover:bg-gray-700 text-sm"
                        >
                          Детали задания
                        </button>
                      </div>
                    )}
                    {clarificationForms[dispute.id]?.show && (
                      <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-md space-y-2">
                        <p className="text-sm font-medium text-purple-800">Запрос уточнения по email</p>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setClarificationForms(prev => ({ ...prev, [dispute.id]: { ...prev[dispute.id], target: 'customer' } }))}
                            className={`px-3 py-1 rounded text-xs ${clarificationForms[dispute.id]?.target === 'customer' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                          >
                            Заказчику ({dispute.customer_email || '—'})
                          </button>
                          <button
                            onClick={() => setClarificationForms(prev => ({ ...prev, [dispute.id]: { ...prev[dispute.id], target: 'executor' } }))}
                            className={`px-3 py-1 rounded text-xs ${clarificationForms[dispute.id]?.target === 'executor' ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                          >
                            Исполнителю ({dispute.executor_email || '—'})
                          </button>
                        </div>
                        <textarea
                          value={clarificationForms[dispute.id]?.text || ''}
                          onChange={(e) => setClarificationForms(prev => ({ ...prev, [dispute.id]: { ...prev[dispute.id], text: e.target.value } }))}
                          placeholder="Введите вопрос..."
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleRequestClarification(dispute.id, clarificationForms[dispute.id]?.target, clarificationForms[dispute.id]?.text)}
                            disabled={!clarificationForms[dispute.id]?.text?.trim()}
                            className="px-3 py-1.5 bg-purple-600 text-white rounded text-sm hover:bg-purple-700 disabled:opacity-50"
                          >
                            Отправить письмо
                          </button>
                          <button
                            onClick={() => setClarificationForms(prev => ({ ...prev, [dispute.id]: { ...prev[dispute.id], show: false } }))}
                            className="px-3 py-1.5 bg-gray-400 text-white rounded text-sm hover:bg-gray-500"
                          >
                            Отмена
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            )}
            <div className="flex flex-wrap items-center justify-between gap-3 mt-2">
            <AdminPager tabState={tabState} setPage={setTSPage} setPerPage={setTSPerPage} tab="disputes" total={pt} />
            <button
              data-testid="csv-export-disputes"
              onClick={() => {
                const state = ts('disputes');
                const params = { sort: state.sort || 'date_desc' };
                if (disputeStatusFilter) params.status = disputeStatusFilter;
                if (state.search) params.search = state.search;
                if (state.dateFrom) params.date_from = state.dateFrom;
                if (state.dateTo) params.date_to = state.dateTo;
                exportCSV('/admin/disputes-all', params, 'items', CSV_COLUMNS.disputes, 'spory');
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm flex-shrink-0"
              style={{background:'rgba(13,148,136,0.08)',color:'var(--rv-teal)',border:'1px solid rgba(13,148,136,0.2)',cursor:'pointer',fontFamily:'IBM Plex Sans,sans-serif'}}
            >
              <Download size={13}/>Скачать CSV ({pt})
            </button>
            </div>
            </>
          );
        })()}
        </div>
      )}

      {/* Флаги чата */}
      {activeTab === 'flags' && (
        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
            <h3 className="text-lg font-semibold text-orange-700">Флаги чата — модерация</h3>
            <div className="flex gap-2">
              {[
                { key: 'pending',   label: 'Активные' },
                { key: 'dismissed', label: 'Снятые' },
                { key: 'all',       label: 'Все' },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => { setFlagStatusFilter(key); setTSPage('flags', 1); }}
                  className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                    flagStatusFilter === key
                      ? 'bg-orange-100 border-orange-400 text-orange-700 font-medium'
                      : 'border-gray-300 text-gray-500 hover:border-gray-400'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {flaggedLoading ? (
            <p className="text-gray-500">Загрузка...</p>
          ) : (() => {
            const pi = flaggedMessages;
            const pt = flagsTotal;
            return (
              <>
                <AdminSearchSort tabState={tabState} setSearch={setTSSearch} setSort={setTSSort} tab="flags" placeholder="Поиск по заданию, email отправителя..." sortOptions={[{value:'date_desc',label:'Дата: новые'},{value:'date_asc',label:'Дата: старые'}]} testPrefix="flags" />
                {pi.length === 0 ? (
                  <p className="text-gray-500">Флагов нет</p>
                ) : (
                  <div className="space-y-4">
                    {pi.map((flag) => {
                      const reasonLabels = {
                        phone:     { label: 'Телефон',    cls: 'bg-red-100 text-red-700' },
                        messenger: { label: 'Мессенджер', cls: 'bg-purple-100 text-purple-700' },
                        payment:   { label: 'Оплата',     cls: 'bg-yellow-100 text-yellow-700' },
                      };
                      const isPending = flag.status === 'pending';
                      return (
                        <div
                          key={flag.id}
                          data-testid={`flag-card-${flag.id}`}
                          className={`border rounded-lg p-4 ${isPending ? 'border-orange-300 bg-orange-50' : 'border-gray-200 bg-gray-50'}`}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <button onClick={() => openChatView(flag.task_id, flag.message_id)} className="font-medium text-blue-700 hover:text-blue-900 hover:underline text-left" data-testid={`flag-task-link-${flag.id}`}>
                                {flag.task_title || flag.task_id}
                              </button>
                              <p className="text-sm text-gray-500 mt-0.5">Отправитель: <span className="text-gray-700">{flag.sender_email}</span></p>
                            </div>
                            <span className="text-xs text-gray-400 whitespace-nowrap ml-4">{flag.created_at ? formatMsk(flag.created_at) : ''}</span>
                          </div>
                          <div className="flex flex-wrap gap-1.5 mb-3">
                            {(flag.flag_reasons || []).map(r => (
                              <span key={r} className={`text-xs px-2 py-0.5 rounded-full font-medium ${(reasonLabels[r] || { cls: 'bg-gray-100 text-gray-600' }).cls}`}>
                                {(reasonLabels[r] || { label: r }).label}
                              </span>
                            ))}
                          </div>
                          <p className="text-sm text-gray-700 bg-white border border-gray-200 rounded p-2 mb-3 italic">«{flag.text_preview}»</p>
                          {flag.status === 'dismissed' && (
                            <div className="text-xs text-gray-500">
                              Снят: {flag.resolved_by} — {flag.resolved_at ? formatMsk(flag.resolved_at) : ''}
                              {flag.notes && <span className="ml-2 italic">«{flag.notes}»</span>}
                            </div>
                          )}
                          {isPending && (
                            <div className="flex flex-col sm:flex-row gap-2 mt-2">
                              <input type="text" placeholder="Заметка (необязательно)" value={dismissNotes[flag.id] || ''} onChange={e => setDismissNotes(prev => ({ ...prev, [flag.id]: e.target.value }))} className="flex-1 text-sm border border-gray-300 rounded px-2 py-1" data-testid={`flag-notes-${flag.id}`} />
                              <button data-testid={`flag-warn-btn-${flag.id}`} onClick={() => warnUser(flag.id)} className="px-4 py-1.5 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors whitespace-nowrap">Предупредить</button>
                              <button data-testid={`flag-dismiss-btn-${flag.id}`} onClick={() => dismissFlag(flag.id)} className="px-4 py-1.5 bg-orange-600 text-white text-sm rounded hover:bg-orange-700 transition-colors whitespace-nowrap">Снять флаг</button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                <AdminPager tabState={tabState} setPage={setTSPage} setPerPage={setTSPerPage} tab="flags" total={pt} />
              </>
            );
          })()}
        </div>
      )}

      {/* Users Tab — запись #24 аудита */}
      {activeTab === 'users' && (
        <div className="bg-white p-6 rounded-lg shadow-md" data-testid="users-panel">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
            <h3 className="text-lg font-semibold text-indigo-700">Пользователи</h3>
            <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
              <input
                type="text"
                placeholder="Поиск по email или имени..."
                value={usersSearchQuery}
                onChange={e => setUsersSearchQuery(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { setTSPage('users', 1); fetchUsers(usersSearchQuery, usersBlockedFilter); } }}
                className="text-sm border border-gray-300 rounded px-3 py-1.5 w-full sm:w-72"
                data-testid="users-search-input"
              />
              <select
                value={usersRoleFilter}
                onChange={e => { setUsersRoleFilter(e.target.value); setTSPage('users', 1); fetchUsers(usersSearchQuery, usersBlockedFilter, e.target.value); }}
                className="text-sm border border-gray-300 rounded px-3 py-1.5"
                data-testid="users-role-filter"
              >
                <option value="">Все роли</option>
                <option value="customer">Заказчики</option>
                <option value="executor">Исполнители</option>
                <option value="admin">Админы</option>
              </select>
              <select
                value={usersBlockedFilter}
                onChange={e => { setUsersBlockedFilter(e.target.value); setTSPage('users', 1); fetchUsers(usersSearchQuery, e.target.value); }}
                className="text-sm border border-gray-300 rounded px-3 py-1.5"
                data-testid="users-blocked-filter"
              >
                <option value="">Все</option>
                <option value="false">Активные</option>
                <option value="true">Заблокированные</option>
              </select>
              <button
                onClick={() => { setTSPage('users', 1); fetchUsers(usersSearchQuery, usersBlockedFilter); }}
                className="px-4 py-1.5 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700"
                data-testid="users-search-btn"
              >
                Найти
              </button>
            </div>
          </div>

          {usersLoading ? (
            <p className="text-gray-500">Загрузка...</p>
          ) : (() => {
            const pi = usersList;
            const pt = usersTotal;
            return (
              <>
                {usersSummary && (
                  <div className="flex flex-wrap gap-2 mb-3" data-testid="users-summary-chips">
                    <span className="px-2.5 py-1 text-xs rounded-full bg-gray-100 text-gray-700 font-medium">Всего: {usersSummary.total}</span>
                    <span className="px-2.5 py-1 text-xs rounded-full bg-indigo-50 text-indigo-700 font-medium">Заказчиков: {usersSummary.customers}</span>
                    <span className="px-2.5 py-1 text-xs rounded-full bg-teal-50 text-teal-700 font-medium">Исполнителей: {usersSummary.executors}</span>
                    <span className="px-2.5 py-1 text-xs rounded-full bg-amber-50 text-amber-700 font-medium">Новых за 7 дней: {usersSummary.new_7d}</span>
                    <span className="px-2.5 py-1 text-xs rounded-full bg-green-50 text-green-700 font-medium">
                      Email подтверждён: {usersSummary.email_verified}{usersSummary.total > 0 ? ` (${Math.round(usersSummary.email_verified / usersSummary.total * 100)}%)` : ''}
                    </span>
                  </div>
                )}
                <AdminDateRangeFilter tab="users" tabState={tabState} setDateFrom={setTSDateFrom} setDateTo={setTSDateTo} />
                <div className="flex flex-wrap justify-between items-center gap-2 mb-2">
                  <span className="text-xs text-gray-500">Найдено: {pt}</span>
                  <div className="flex items-center gap-2">
                    <button
                      data-testid="csv-export-users"
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                      onClick={() => {
                        const params = {};
                        if (usersSearchQuery) params.q = usersSearchQuery;
                        if (usersBlockedFilter) params.blocked = usersBlockedFilter;
                        if (usersRoleFilter) params.role = usersRoleFilter;
                        const state = ts('users');
                        if (state.dateFrom) params.date_from = state.dateFrom;
                        if (state.dateTo) params.date_to = state.dateTo;
                        params.sort = state.sort || 'date_desc';
                        exportCSV('/admin/users', params, 'users', CSV_COLUMNS.users, 'polzovateli');
                      }}
                    >
                      <Download size={13}/>Скачать CSV ({pt})
                    </button>
                    <select
                      value={ts('users').sort}
                      onChange={e => setTSSort('users', e.target.value)}
                      className="text-sm border border-gray-300 rounded px-3 py-1.5"
                      data-testid="users-sort-select"
                    >
                      <option value="date_desc">Сначала новые</option>
                      <option value="date_asc">Сначала старые</option>
                      <option value="last_login_desc">Недавно заходили</option>
                      <option value="name_asc">Имя А-Я</option>
                      <option value="name_desc">Имя Я-А</option>
                      <option value="rating_desc">Рейтинг: высокий</option>
                      <option value="tasks_desc">Больше заданий</option>
                      <option value="expired_desc">Больше отмен по сроку</option>
                    </select>
                  </div>
                </div>
                {pi.length === 0 ? (
                  <p className="text-gray-500">Пользователи не найдены</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr className="text-left text-gray-600">
                          <th className="px-3 py-2">Email / Имя</th>
                          <th className="px-3 py-2">Роль</th>
                          <th className="px-3 py-2">Регистрация</th>
                          <th className="px-3 py-2">Последний вход</th>
                          <th className="px-3 py-2">Email</th>
                          <th className="px-3 py-2">Верификация</th>
                          <th className="px-3 py-2">Рейтинг</th>
                          <th className="px-3 py-2">Заданий</th>
                          <th className="px-3 py-2" title="Отмен с возвратом по истечении срока исполнения (молчащий исполнитель)">Отмен по сроку</th>
                          <th className="px-3 py-2">Источник</th>
                          <th className="px-3 py-2">Статус</th>
                          <th className="px-3 py-2">Действия</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pi.map(u => (
                          <tr key={u.id} className="border-t border-gray-100" data-testid={`user-row-${u.id}`}>
                            <td className="px-3 py-2 align-top">
                              <div className="font-medium text-gray-800">{u.name}</div>
                              <div className="text-xs text-gray-500">{u.email}</div>
                              {u.phone && <div className="text-xs text-gray-400 whitespace-nowrap">{u.phone}</div>}
                            </td>
                            <td className="px-3 py-2 align-top text-gray-700 whitespace-nowrap">{USER_ROLE_LABELS[u.role] || u.role}</td>
                            <td className="px-3 py-2 align-top whitespace-nowrap" data-testid={`user-created-at-${u.id}`}>
                              {u.created_at ? (
                                <>
                                  <div className="text-gray-700">{formatMskDate(u.created_at)}</div>
                                  <div className="text-xs text-gray-400">{new Date(u.created_at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Moscow' })} MSK</div>
                                </>
                              ) : <span className="text-gray-400">—</span>}
                            </td>
                            <td className="px-3 py-2 align-top whitespace-nowrap" data-testid={`user-last-login-${u.id}`}>
                              {u.last_login_at ? (
                                <>
                                  <div className="text-gray-700">{formatMskDate(u.last_login_at)}</div>
                                  <div className="text-xs text-gray-400">входов: {u.login_count ?? 0}</div>
                                </>
                              ) : <span className="text-gray-400">—</span>}
                            </td>
                            <td className="px-3 py-2 align-top" data-testid={`user-email-verified-${u.id}`}>
                              {u.email_verified ? (
                                <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700 font-medium whitespace-nowrap">Подтверждён</span>
                              ) : (
                                <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-500 whitespace-nowrap">Нет</span>
                              )}
                            </td>
                            <td className="px-3 py-2 align-top" data-testid={`user-verification-cell-${u.id}`}>
                              {u.role === 'executor' ? (
                                <div className="flex flex-col gap-1 items-start">
                                  {u.identity_verified ? (
                                    <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700 font-medium whitespace-nowrap" data-testid={`user-identity-verified-${u.id}`}>Личность подтверждена</span>
                                  ) : (
                                    <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-500 whitespace-nowrap" data-testid={`user-identity-unverified-${u.id}`}>Без верификации</span>
                                  )}
                                  {u.is_verified_executor && (
                                    <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700 font-medium whitespace-nowrap">Проверенный исполнитель</span>
                                  )}
                                  {u.business_status && u.business_status !== 'none' && (
                                    <span className="px-2 py-0.5 text-xs rounded-full font-medium whitespace-nowrap" style={{background:'#ECFDF5', color:'#065F46', border:'1px solid #6EE7B7'}} data-testid={`user-business-status-${u.id}`}>
                                      {BUSINESS_STATUS_LABELS[u.business_status] || u.business_status}{u.business_status_warnings > 0 ? ` (жалоб: ${u.business_status_warnings})` : ''}
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </td>
                            <td className="px-3 py-2 align-top">{u.rating != null ? u.rating.toFixed(1) : '—'}</td>
                            <td className="px-3 py-2 align-top">{u.total_tasks_count ?? 0}</td>
                            <td className="px-3 py-2 align-top whitespace-nowrap" data-testid={`user-expired-cancellations-${u.id}`}>
                              {u.role === 'executor' ? (
                                (u.expired_cancellations || 0) > 0 ? (
                                  <span className="px-2 py-0.5 text-xs rounded-full font-semibold" style={{background:'#FEF2F2', color:'#B91C1C', border:'1px solid #FECACA'}}
                                    title={u.last_expired_cancellation_at ? `Последняя: ${formatMsk(u.last_expired_cancellation_at)}` : undefined}>
                                    {u.expired_cancellations}
                                  </span>
                                ) : <span className="text-gray-400">0</span>
                              ) : <span className="text-gray-300">—</span>}
                            </td>
                            <td className="px-3 py-2 align-top" data-testid={`user-source-${u.id}`}>
                              {u.reg_utm && (u.reg_utm.utm_source || u.reg_utm.utm_campaign) ? (
                                <div className="text-xs text-gray-600 max-w-[140px]">
                                  <div className="font-medium truncate" title={u.reg_utm.utm_source}>{u.reg_utm.utm_source || '—'}</div>
                                  {u.reg_utm.utm_campaign && <div className="text-gray-400 truncate" title={u.reg_utm.utm_campaign}>{u.reg_utm.utm_campaign}</div>}
                                </div>
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </td>
                            <td className="px-3 py-2 align-top">
                              {u.is_blocked ? (
                                <div>
                                  <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700 font-medium">Заблокирован</span>
                                  {u.blocked_reason && (
                                    <div className="text-xs text-gray-500 mt-1">{blockReasonsMap[u.blocked_reason] || u.blocked_reason}</div>
                                  )}
                                </div>
                              ) : (
                                <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700 font-medium">Активен</span>
                              )}
                            </td>
                            <td className="px-3 py-2 align-top">
                              {u.is_blocked ? (
                                <button onClick={() => unblockUser(u)} className="px-3 py-1 text-xs rounded bg-green-600 text-white hover:bg-green-700" data-testid={`unblock-btn-${u.id}`}>Разблокировать</button>
                              ) : (
                                <button onClick={() => setBlockModal({ user: u, reason: '', notes: '' })} className="px-3 py-1 text-xs rounded bg-red-600 text-white hover:bg-red-700" data-testid={`block-btn-${u.id}`}>Заблокировать</button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <AdminPager tabState={tabState} setPage={setTSPage} setPerPage={setTSPerPage} tab="users" total={pt} />
              </>
            );
          })()}
        </div>
      )}

      {/* Block User Modal */}
      {blockModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          data-testid="block-user-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setBlockModal(null); }}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Заблокировать пользователя</h3>
            <p className="text-sm text-gray-600 mb-4">{blockModal.user.email}</p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Причина</label>
            <select
              value={blockModal.reason}
              onChange={e => setBlockModal({ ...blockModal, reason: e.target.value })}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm mb-3"
              data-testid="block-reason-select"
            >
              <option value="">— выбрать —</option>
              {Object.entries(blockReasonsMap).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
            <label className="block text-sm font-medium text-gray-700 mb-1">Комментарий (необязательно)</label>
            <textarea
              value={blockModal.notes}
              onChange={e => setBlockModal({ ...blockModal, notes: e.target.value })}
              rows={3}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm mb-4"
              data-testid="block-notes-input"
            />
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setBlockModal(null)}
                className="px-4 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50"
              >
                Отмена
              </button>
              <button
                onClick={submitBlockUser}
                className="px-4 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                data-testid="block-confirm-btn"
              >
                Заблокировать
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Complaints Tab — запись #25 аудита */}
      {activeTab === 'complaints' && (
        <div className="bg-white p-6 rounded-lg shadow-md" data-testid="complaints-panel">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
            <h3 className="text-lg font-semibold text-pink-700">Жалобы пользователей</h3>
            <div className="flex gap-2">
              {[
                { key: 'open',    label: 'Открытые' },
                { key: 'closed',  label: 'Закрытые' },
                { key: 'blocked', label: 'С блокировкой' },
                { key: 'all',     label: 'Все' },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => { setComplaintsStatusFilter(key); setTSPage('complaints', 1); }}
                  className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                    complaintsStatusFilter === key
                      ? 'bg-pink-100 border-pink-400 text-pink-700 font-medium'
                      : 'border-gray-300 text-gray-500 hover:border-gray-400'
                  }`}
                  data-testid={`complaints-filter-${key}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {complaintsLoading ? (
            <p className="text-gray-500">Загрузка...</p>
          ) : (() => {
            const pi = complaintsList;
            const pt = complaintsTotal;
            return (
              <>
                <AdminSearchSort tabState={tabState} setSearch={setTSSearch} setSort={setTSSort} tab="complaints" placeholder="Поиск по email, заданию..." sortOptions={[{value:'date_desc',label:'Дата: новые'},{value:'date_asc',label:'Дата: старые'},{value:'status',label:'По статусу'}]} testPrefix="complaints" />
                <AdminDateRangeFilter tab="complaints" tabState={tabState} setDateFrom={setTSDateFrom} setDateTo={setTSDateTo} />
                {pi.length === 0 ? (
                  <p className="text-gray-500">Жалоб нет</p>
                ) : (
                  <div className="space-y-4">
                    {pi.map(c => {
                      const isOpen = c.complaint_status === 'open';
                      const statusBadge = {
                        open:    { label: 'Открыта',    cls: 'bg-pink-100 text-pink-700' },
                        closed:  { label: 'Закрыта',    cls: 'bg-gray-100 text-gray-700' },
                        blocked: { label: 'Блокировка', cls: 'bg-red-100 text-red-700' },
                      }[c.complaint_status] || { label: c.complaint_status, cls: 'bg-gray-100 text-gray-700' };
                      return (
                        <div key={c.id} data-testid={`complaint-card-${c.id}`} className={`border rounded-lg p-4 ${isOpen ? 'border-pink-300 bg-pink-50' : 'border-gray-200 bg-gray-50'}`}>
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="text-sm"><span className="text-gray-500">От: </span><span className="font-medium text-gray-800">{c.initiator_email}</span></p>
                              <p className="text-sm"><span className="text-gray-500">На: </span><span className="font-medium text-gray-800">{c.target_email}</span>{c.target_is_blocked && <span className="ml-2 text-xs px-2 py-0.5 rounded bg-red-100 text-red-700">Заблокирован</span>}</p>
                              {c.task_title && (<p className="text-sm"><span className="text-gray-500">Задание: </span><button onClick={() => openChatView(c.task_id, null)} className="text-blue-700 hover:underline" data-testid={`complaint-task-link-${c.id}`}>{c.task_title}</button></p>)}
                            </div>
                            <div className="text-right ml-4">
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusBadge.cls}`}>{statusBadge.label}</span>
                              <p className="text-xs text-gray-400 whitespace-nowrap mt-1">{c.created_at ? formatMsk(c.created_at) : ''}</p>
                            </div>
                          </div>
                          <p className="text-sm text-gray-700 bg-white border border-gray-200 rounded p-2 mb-3">{c.description}</p>
                          {!isOpen && c.admin_notes && (<div className="text-xs text-gray-500 mb-2 italic">Решение администратора: «{c.admin_notes}»</div>)}
                          {isOpen && (
                            <div className="flex flex-col sm:flex-row gap-2 mt-2">
                              <input type="text" placeholder="Заметка для закрытия (необязательно)" value={complaintCloseNotes[c.id] || ''} onChange={e => setComplaintCloseNotes(prev => ({ ...prev, [c.id]: e.target.value }))} className="flex-1 text-sm border border-gray-300 rounded px-2 py-1" data-testid={`complaint-notes-${c.id}`} />
                              <button onClick={() => closeComplaint(c.id)} className="px-4 py-1.5 bg-gray-600 text-white text-sm rounded hover:bg-gray-700 transition-colors whitespace-nowrap" data-testid={`complaint-close-btn-${c.id}`}>Закрыть жалобу</button>
                              <button onClick={() => setComplaintBlockModal({ complaint: c, reason: '', notes: '' })} disabled={!!c.target_is_blocked} className="px-4 py-1.5 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors whitespace-nowrap disabled:bg-gray-300 disabled:cursor-not-allowed" data-testid={`complaint-block-btn-${c.id}`}>Заблокировать пользователя</button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                <div className="flex flex-wrap items-center justify-between gap-3 mt-2">
                  <AdminPager tabState={tabState} setPage={setTSPage} setPerPage={setTSPerPage} tab="complaints" total={pt} />
                  <button
                    data-testid="csv-export-complaints"
                    onClick={() => {
                      const state = ts('complaints');
                      const params = { status: complaintsStatusFilter, sort: state.sort || 'date_desc' };
                      if (state.search) params.search = state.search;
                      if (state.dateFrom) params.date_from = state.dateFrom;
                      if (state.dateTo) params.date_to = state.dateTo;
                      exportCSV('/admin/complaints', params, 'items', CSV_COLUMNS.complaints, 'zhaloby');
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm flex-shrink-0"
                    style={{background:'rgba(13,148,136,0.08)',color:'var(--rv-teal)',border:'1px solid rgba(13,148,136,0.2)',cursor:'pointer',fontFamily:'IBM Plex Sans,sans-serif'}}
                  >
                    <Download size={13}/>Скачать CSV ({pt})
                  </button>
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* Block-from-complaint Modal */}
      {complaintBlockModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          data-testid="complaint-block-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setComplaintBlockModal(null); }}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Заблокировать пользователя по жалобе</h3>
            <p className="text-sm text-gray-600 mb-4">{complaintBlockModal.complaint.target_email}</p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Причина</label>
            <select
              value={complaintBlockModal.reason}
              onChange={e => setComplaintBlockModal({ ...complaintBlockModal, reason: e.target.value })}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm mb-3"
              data-testid="complaint-block-reason-select"
            >
              <option value="">— выбрать —</option>
              {Object.entries(blockReasonsMap).length === 0 ? (
                <>
                  <option value="leaving_platform">Уход с платформы</option>
                  <option value="fraud">Мошенничество</option>
                  <option value="abuse">Оскорбления</option>
                  <option value="spam">Спам</option>
                  <option value="other">Другое</option>
                </>
              ) : (
                Object.entries(blockReasonsMap).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))
              )}
            </select>
            <label className="block text-sm font-medium text-gray-700 mb-1">Комментарий (необязательно)</label>
            <textarea
              value={complaintBlockModal.notes}
              onChange={e => setComplaintBlockModal({ ...complaintBlockModal, notes: e.target.value })}
              rows={3}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm mb-4"
              data-testid="complaint-block-notes-input"
            />
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setComplaintBlockModal(null)}
                className="px-4 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50"
              >
                Отмена
              </button>
              <button
                onClick={submitBlockFromComplaint}
                className="px-4 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                data-testid="complaint-block-confirm-btn"
              >
                Заблокировать и закрыть
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'settings' && (
        <div>
          <h3 className="text-base font-semibold mb-4 flex items-center gap-2" style={{fontFamily:'Manrope,sans-serif'}}><Settings size={16} style={{color:'var(--rv-teal)'}}/>Настройки системы</h3>

          {/* Управление запуском: доступ к сайту и публикация заданий */}
          <div className="reversia-card p-6 mb-6">
            <h4 className="text-lg font-semibold flex items-center gap-2 mb-4" style={{fontFamily:'Manrope,sans-serif'}}><Lock size={16} style={{color:'var(--rv-teal)'}}/>Управление запуском</h4>
            {siteConfigLoading ? (
              <div className="text-center py-6">
                <div className="animate-spin rounded-full h-7 w-7 border-b-2 mx-auto" style={{borderColor:'var(--rv-teal)'}}></div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Парольная блокировка сайта */}
                <div className="p-4 rounded-lg flex items-start justify-between gap-4" style={{border:'1px solid var(--rv-border)', background:'white'}}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold" style={{color:'var(--rv-text-primary)'}}>Парольная блокировка сайта</span>
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{
                        background: siteConfig.site_gate_enabled ? 'rgba(217,119,6,0.1)' : 'rgba(16,185,129,0.1)',
                        color: siteConfig.site_gate_enabled ? '#d97706' : '#10b981',
                      }}>
                        {siteConfig.site_gate_enabled ? 'Сайт закрыт паролем' : 'Сайт публичный'}
                      </span>
                    </div>
                    <p className="text-sm mt-1" style={{color:'var(--rv-text-muted)'}}>
                      Когда включено — посетители видят форму ввода пароля (reversia2025). Используйте только при необходимости временно закрыть сайт.
                    </p>
                  </div>
                  <button
                    data-testid="toggle-site-gate"
                    onClick={() => updateSiteConfig({ site_gate_enabled: !siteConfig.site_gate_enabled })}
                    disabled={siteConfigLoading}
                    className="px-3 py-1.5 rounded text-sm font-medium flex-shrink-0"
                    style={{
                      background: siteConfig.site_gate_enabled ? '#d97706' : 'rgba(13,148,136,0.1)',
                      color: siteConfig.site_gate_enabled ? 'white' : 'var(--rv-teal)',
                      border: 'none', cursor: 'pointer',
                    }}
                  >
                    {siteConfig.site_gate_enabled ? 'Открыть сайт' : 'Закрыть паролем'}
                  </button>
                </div>

                {/* Публикация платных заданий */}
                <div className="p-4 rounded-lg flex items-start justify-between gap-4" style={{border:'1px solid var(--rv-border)', background:'white'}}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold" style={{color:'var(--rv-text-primary)'}}>Публикация платных заданий</span>
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{
                        background: siteConfig.paid_tasks_enabled ? 'rgba(16,185,129,0.1)' : 'rgba(217,119,6,0.1)',
                        color: siteConfig.paid_tasks_enabled ? '#10b981' : '#d97706',
                      }}>
                        {siteConfig.paid_tasks_enabled ? 'Открыта' : 'Закрыта (набор исполнителей)'}
                      </span>
                    </div>
                    <p className="text-sm mt-1" style={{color:'var(--rv-text-muted)'}}>
                      Пока закрыто — заказчики регистрируются, но не могут создавать задания (реальные платежи не проходят). Откройте после оформления ИП и набора исполнителей.
                    </p>
                  </div>
                  <button
                    data-testid="toggle-paid-tasks"
                    onClick={() => updateSiteConfig({ paid_tasks_enabled: !siteConfig.paid_tasks_enabled })}
                    disabled={siteConfigLoading}
                    className="px-3 py-1.5 rounded text-sm font-medium flex-shrink-0"
                    style={{
                      background: siteConfig.paid_tasks_enabled ? '#d97706' : '#10b981',
                      color: 'white', border: 'none', cursor: 'pointer',
                    }}
                  >
                    {siteConfig.paid_tasks_enabled ? 'Закрыть приём' : 'Открыть приём'}
                  </button>
                </div>

                {!siteConfig.paid_tasks_enabled && (
                  <div className="p-3 rounded-lg text-sm flex items-start gap-2" style={{background:'rgba(217,119,6,0.06)', color:'#92400e'}}>
                    <AlertTriangle size={14} className="mt-0.5 flex-shrink-0"/>
                    Напоминание: открывайте приём заданий только после оформления ИП — ручные выплаты исполнителям в статусе самозанятого попадают под ограничения по агентской деятельности.
                  </div>
                )}

                {/* AI-помощник формулировки задания */}
                <div className="p-4 rounded-lg flex items-start justify-between gap-4" style={{border:'1px solid var(--rv-border)', background:'white'}}>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold" style={{color:'var(--rv-text-primary)'}}>AI-помощник формулировки задания</span>
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{
                        background: siteConfig.ai_helper_enabled ? 'rgba(16,185,129,0.1)' : 'rgba(107,114,128,0.1)',
                        color: siteConfig.ai_helper_enabled ? '#10b981' : '#6b7280',
                      }}>
                        {siteConfig.ai_helper_enabled ? 'Включён' : 'Выключен'}
                      </span>
                    </div>
                    <p className="text-sm mt-1" style={{color:'var(--rv-text-muted)'}}>
                      Кнопка «Сформулировать задание с помощью AI» в разделе «Общие услуги». Каждая генерация расходует баланс Emergent. Отключите, если баланс низкий — кнопка пропадёт у заказчиков.
                    </p>
                  </div>
                  <button
                    data-testid="toggle-ai-helper"
                    onClick={() => updateSiteConfig({ ai_helper_enabled: !siteConfig.ai_helper_enabled })}
                    disabled={siteConfigLoading}
                    className="px-3 py-1.5 rounded text-sm font-medium flex-shrink-0"
                    style={{
                      background: siteConfig.ai_helper_enabled ? '#d97706' : '#10b981',
                      color: 'white', border: 'none', cursor: 'pointer',
                    }}
                  >
                    {siteConfig.ai_helper_enabled ? 'Отключить' : 'Включить'}
                  </button>
                </div>

                {/* AI-ассистент для споров */}
                <div className="p-4 rounded-lg flex items-start justify-between gap-4" style={{border:'1px solid var(--rv-border)', background:'white'}}>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold" style={{color:'var(--rv-text-primary)'}}>AI-ассистент для споров</span>
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{
                        background: siteConfig.ai_dispute_assistant_enabled ? 'rgba(16,185,129,0.1)' : 'rgba(107,114,128,0.1)',
                        color: siteConfig.ai_dispute_assistant_enabled ? '#10b981' : '#6b7280',
                      }}>
                        {siteConfig.ai_dispute_assistant_enabled ? 'Включён' : 'Выключен'}
                      </span>
                    </div>
                    <p className="text-sm mt-1" style={{color:'var(--rv-text-muted)'}}>
                      Кнопка «AI-анализ» на вкладке «Споры»: AI изучает переписку и детали спора и предлагает рекомендацию. Решение по-прежнему принимает администратор вручную.
                    </p>
                  </div>
                  <button
                    data-testid="toggle-ai-dispute-assistant"
                    onClick={() => updateSiteConfig({ ai_dispute_assistant_enabled: !siteConfig.ai_dispute_assistant_enabled })}
                    disabled={siteConfigLoading}
                    className="px-3 py-1.5 rounded text-sm font-medium flex-shrink-0"
                    style={{
                      background: siteConfig.ai_dispute_assistant_enabled ? '#d97706' : '#10b981',
                      color: 'white', border: 'none', cursor: 'pointer',
                    }}
                  >
                    {siteConfig.ai_dispute_assistant_enabled ? 'Отключить' : 'Включить'}
                  </button>
                </div>

                {/* TZ8-14: обезличивание push и Telegram */}
                <div data-testid="push-anonymize-block" className="p-4 rounded-lg flex items-start justify-between gap-4" style={{border:'1px solid var(--rv-border)', background:'white'}}>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold" style={{color:'var(--rv-text-primary)'}}>Обезличивание push и Telegram</span>
                      <span data-testid="push-anonymize-status" className="px-2 py-0.5 rounded-full text-xs font-medium" style={{
                        background: siteConfig.push_anonymize ? 'rgba(16,185,129,0.1)' : 'rgba(107,114,128,0.1)',
                        color: siteConfig.push_anonymize ? '#10b981' : '#6b7280',
                      }}>
                        {siteConfig.push_anonymize ? 'Включено' : 'Выключено'}
                      </span>
                    </div>
                    <p className="text-sm mt-1" style={{color:'var(--rv-text-muted)'}}>
                      Push-уведомления и Telegram приходят нейтральными фразами без названий заданий, имён, адресов и цен. Email не затрагивается.
                    </p>
                  </div>
                  <button
                    data-testid="toggle-push-anonymize"
                    onClick={() => updateSiteConfig({ push_anonymize: !siteConfig.push_anonymize })}
                    disabled={siteConfigLoading}
                    className="px-3 py-1.5 rounded text-sm font-medium flex-shrink-0"
                    style={{
                      background: siteConfig.push_anonymize ? '#d97706' : '#10b981',
                      color: 'white', border: 'none', cursor: 'pointer',
                    }}
                  >
                    {siteConfig.push_anonymize ? 'Отключить' : 'Включить'}
                  </button>
                </div>

                {/* Счётчик использования AI-генераций */}
                <div className="p-4 rounded-lg" data-testid="ai-usage-widget" style={{border:'1px solid var(--rv-border)', background:'white'}}>
                  <div className="flex items-center gap-2 mb-3">
                    <BarChart2 size={16} style={{color:'var(--rv-teal)'}}/>
                    <span className="font-semibold" style={{color:'var(--rv-text-primary)'}}>Использование AI-генераций</span>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="text-center p-3 rounded-lg" style={{background:'rgba(13,148,136,0.06)'}}>
                      <div data-testid="ai-usage-today" className="text-2xl font-bold" style={{color:'var(--rv-teal)', fontFamily:'Manrope,sans-serif'}}>
                        {aiUsage ? aiUsage.today : '—'}
                      </div>
                      <div className="text-xs mt-1" style={{color:'var(--rv-text-muted)'}}>Сегодня</div>
                    </div>
                    <div className="text-center p-3 rounded-lg" style={{background:'rgba(13,148,136,0.06)'}}>
                      <div data-testid="ai-usage-week" className="text-2xl font-bold" style={{color:'var(--rv-teal)', fontFamily:'Manrope,sans-serif'}}>
                        {aiUsage ? aiUsage.last_7_days : '—'}
                      </div>
                      <div className="text-xs mt-1" style={{color:'var(--rv-text-muted)'}}>За 7 дней</div>
                    </div>
                    <div className="text-center p-3 rounded-lg" style={{background:'rgba(13,148,136,0.06)'}}>
                      <div data-testid="ai-usage-total" className="text-2xl font-bold" style={{color:'var(--rv-teal)', fontFamily:'Manrope,sans-serif'}}>
                        {aiUsage ? aiUsage.total : '—'}
                      </div>
                      <div className="text-xs mt-1" style={{color:'var(--rv-text-muted)'}}>Всего</div>
                    </div>
                  </div>
                  <p className="text-sm mt-3" style={{color:'var(--rv-text-muted)'}}>
                    Каждая генерация расходует баланс Emergent. Лимит: {aiUsage ? aiUsage.limit_per_hour : 20} запросов в час на пользователя.{' '}
                    <a href="https://app.emergent.sh/profile" target="_blank" rel="noopener noreferrer" data-testid="ai-usage-balance-link" style={{color:'var(--rv-teal)', textDecoration:'underline'}}>
                      Проверить баланс Emergent
                    </a>
                  </p>
                </div>

                {/* Рассылка объявлений (in-app) */}
                <div className="p-4 rounded-lg" data-testid="announcement-admin-card" style={{border:'1px solid var(--rv-border)', background:'white'}}>
                  <div className="flex items-center gap-2 mb-3">
                    <MessageSquare size={16} style={{color:'var(--rv-teal)'}}/>
                    <span className="font-semibold" style={{color:'var(--rv-text-primary)'}}>Рассылка объявлений</span>
                  </div>
                  <p className="text-sm mb-3" style={{color:'var(--rv-text-muted)'}}>
                    Объявление появится баннером на главной странице у выбранной аудитории после входа. Одновременно активно только одно объявление — новое заменяет предыдущее.
                  </p>

                  {activeAnnouncement && (
                    <div className="p-3 rounded-lg mb-3" data-testid="announcement-active-block" style={{background:'rgba(13,148,136,0.06)', border:'1px solid var(--rv-border)'}}>
                      <div className="flex items-center justify-between gap-3 mb-1">
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{background:'rgba(16,185,129,0.1)', color:'#10b981'}}>Активно</span>
                        <button
                          data-testid="announcement-deactivate-btn"
                          onClick={deactivateAnnouncement}
                          disabled={announceLoading}
                          className="px-3 py-1 rounded text-sm font-medium"
                          style={{background:'#d97706', color:'white', border:'none', cursor:'pointer'}}
                        >
                          Деактивировать
                        </button>
                      </div>
                      <div className="font-semibold text-sm" style={{color:'var(--rv-text-primary)'}}>{activeAnnouncement.title}</div>
                      <div className="text-sm whitespace-pre-line" style={{color:'var(--rv-text-muted)'}}>{activeAnnouncement.message}</div>
                      <div className="text-xs mt-1" style={{color:'var(--rv-text-muted)'}}>
                        Аудитория: {activeAnnouncement.audience === 'all' ? 'Все' : activeAnnouncement.audience === 'customers' ? 'Заказчики' : activeAnnouncement.audience === 'executors' ? 'Исполнители' : `Пользователь (${activeAnnouncement.target_email || ''})`}
                      </div>
                    </div>
                  )}

                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium block mb-1" style={{color:'var(--rv-text-primary)'}}>Аудитория</label>
                      <select
                        data-testid="announcement-audience-select"
                        value={announceForm.audience}
                        onChange={(e) => setAnnounceForm({ ...announceForm, audience: e.target.value })}
                        className="w-full px-3 py-2 rounded text-sm"
                        style={{border:'1px solid var(--rv-border)', background:'white'}}
                      >
                        <option value="all">Все пользователи</option>
                        <option value="customers">Все заказчики</option>
                        <option value="executors">Все исполнители</option>
                        <option value="user">Один пользователь (по email)</option>
                      </select>
                    </div>
                    {announceForm.audience === 'user' && (
                      <div>
                        <label className="text-sm font-medium block mb-1" style={{color:'var(--rv-text-primary)'}}>Email пользователя</label>
                        <input
                          data-testid="announcement-email-input"
                          type="email"
                          value={announceForm.target_email}
                          onChange={(e) => setAnnounceForm({ ...announceForm, target_email: e.target.value })}
                          placeholder="user@example.com"
                          className="w-full px-3 py-2 rounded text-sm"
                          style={{border:'1px solid var(--rv-border)', background:'white'}}
                        />
                      </div>
                    )}
                    <div>
                      <label className="text-sm font-medium block mb-1" style={{color:'var(--rv-text-primary)'}}>Заголовок</label>
                      <input
                        data-testid="announcement-title-input"
                        type="text"
                        maxLength={120}
                        value={announceForm.title}
                        onChange={(e) => setAnnounceForm({ ...announceForm, title: e.target.value })}
                        placeholder="Например: Технические работы 15 июня"
                        className="w-full px-3 py-2 rounded text-sm"
                        style={{border:'1px solid var(--rv-border)', background:'white'}}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium block mb-1" style={{color:'var(--rv-text-primary)'}}>Текст сообщения</label>
                      <textarea
                        data-testid="announcement-message-input"
                        maxLength={2000}
                        rows={4}
                        value={announceForm.message}
                        onChange={(e) => setAnnounceForm({ ...announceForm, message: e.target.value })}
                        placeholder="Текст информационного сообщения для пользователей"
                        className="w-full px-3 py-2 rounded text-sm"
                        style={{border:'1px solid var(--rv-border)', background:'white'}}
                      />
                    </div>
                    <button
                      data-testid="announcement-send-btn"
                      onClick={sendAnnouncement}
                      disabled={announceLoading}
                      className="px-4 py-2 rounded text-sm font-medium"
                      style={{background:'var(--rv-teal)', color:'white', border:'none', cursor:'pointer'}}
                    >
                      {announceLoading ? 'Отправка...' : 'Отправить объявление'}
                    </button>
                  </div>
                </div>


                {/* Публичный счётчик социального доказательства */}
                <div className="p-4 rounded-lg" style={{border:'1px solid var(--rv-border)', background:'white'}}>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold" style={{color:'var(--rv-text-primary)'}}>Публичный счётчик «Уже с нами»</span>
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{
                          background: siteConfig.public_stats_enabled ? 'rgba(16,185,129,0.1)' : 'rgba(107,114,128,0.1)',
                          color: siteConfig.public_stats_enabled ? '#10b981' : '#6b7280',
                        }}>
                          {siteConfig.public_stats_enabled ? 'Включён' : 'Выключен'}
                        </span>
                      </div>
                      <p className="text-sm mt-1" style={{color:'var(--rv-text-muted)'}}>
                        Блок «Уже N исполнителей в M городах» на экране входа. Число исполнителей берётся из базы автоматически. Блок появляется, только когда исполнителей не меньше порога.
                      </p>
                      {liveExecutorsCount !== null && (
                        <p className="text-sm mt-2" style={{color:'var(--rv-text-secondary)'}}>
                          Сейчас в базе исполнителей: <strong style={{color:'var(--rv-teal)'}}>{liveExecutorsCount}</strong>
                        </p>
                      )}
                    </div>
                    <button
                      data-testid="toggle-public-stats"
                      onClick={() => updateSiteConfig({ public_stats_enabled: !siteConfig.public_stats_enabled })}
                      disabled={siteConfigLoading}
                      className="px-3 py-1.5 rounded text-sm font-medium flex-shrink-0"
                      style={{
                        background: siteConfig.public_stats_enabled ? '#d97706' : '#10b981',
                        color: 'white', border: 'none', cursor: 'pointer',
                      }}
                    >
                      {siteConfig.public_stats_enabled ? 'Выключить' : 'Включить'}
                    </button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                    <div>
                      <label className="block text-xs font-medium mb-1" style={{color:'var(--rv-text-secondary)'}}>Количество городов</label>
                      <input
                        data-testid="public-stats-cities-input"
                        type="number"
                        min="0"
                        value={citiesDraft}
                        onChange={(e) => setCitiesDraft(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg text-sm"
                        style={{border:'1px solid var(--rv-border)', background:'white', color:'var(--rv-text-primary)'}}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1" style={{color:'var(--rv-text-secondary)'}}>Порог показа (исполнителей)</label>
                      <input
                        data-testid="public-stats-min-input"
                        type="number"
                        min="0"
                        value={minExecutorsDraft}
                        onChange={(e) => setMinExecutorsDraft(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg text-sm"
                        style={{border:'1px solid var(--rv-border)', background:'white', color:'var(--rv-text-primary)'}}
                      />
                    </div>
                  </div>
                  <button
                    data-testid="public-stats-save-btn"
                    onClick={() => updateSiteConfig({
                      public_stats_cities: Math.max(0, parseInt(citiesDraft || '0', 10)),
                      public_stats_min_executors: Math.max(0, parseInt(minExecutorsDraft || '0', 10)),
                    })}
                    disabled={siteConfigLoading}
                    className="mt-3 px-3 py-1.5 rounded text-sm font-medium"
                    style={{background:'var(--rv-teal)', color:'white', border:'none', cursor:'pointer'}}
                  >
                    Сохранить параметры счётчика
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="reversia-card p-6">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-lg font-semibold flex items-center gap-2" style={{fontFamily:'Manrope,sans-serif'}}><Wallet size={16} style={{color:'var(--rv-teal)'}}/>Режим выплат исполнителям</h4>
              <div className="px-3 py-1 rounded-full text-sm font-medium" style={{
                background: payoutMode === 'auto' ? 'rgba(16,185,129,0.1)' : 'rgba(217,119,6,0.1)',
                color: payoutMode === 'auto' ? '#10b981' : '#d97706',
              }}>
                {payoutMode === 'auto' ? 'Автоматический' : 'Ручной'}
              </div>
            </div>

            {payoutModeLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto" style={{borderColor:'var(--rv-teal)'}}></div>
                <p className="mt-2" style={{color:'var(--rv-text-muted)'}}>Загрузка настроек...</p>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="p-4 rounded-lg" style={{background:'rgba(13,148,136,0.04)', border:'1px solid rgba(13,148,136,0.1)'}}>
                  <h5 className="font-semibold mb-2" style={{color:'var(--rv-text-primary)'}}>Текущий режим:</h5>
                  <div className="space-y-2">
                    {payoutMode === 'auto' ? (
                      <>
                        <p className="flex items-center gap-1.5" style={{color:'#10b981'}}>
                          <CheckCircle2 size={15}/><strong>Автоматические выплаты через YuKassa</strong>
                        </p>
                        <ul className="text-sm ml-5 space-y-1" style={{color:'#059669'}}>
                          <li>• Выплаты происходят автоматически после подтверждения задания</li>
                          <li>• Автоматическое формирование фискальных чеков</li>
                          <li>• Документооборот с ФНС через YuKassa</li>
                          <li>• Требует статус ИП и подключенный агент выплат</li>
                        </ul>
                      </>
                    ) : (
                      <>
                        <p className="flex items-center gap-1.5" style={{color:'#d97706'}}>
                          <AlertTriangle size={15}/><strong>Ручные выплаты через администратора</strong>
                        </p>
                        <ul className="text-sm ml-5 space-y-1" style={{color:'#b45309'}}>
                          <li>• Выплаты требуют ручного подтверждения администратором</li>
                          <li>• Выплаты через СБП или банковские переводы</li>
                          <li>• Возможен только при статусе самозанятого</li>
                          <li>• Используется как резервный режим для ИП</li>
                        </ul>
                      </>
                    )}
                  </div>
                </div>

                <div className="space-y-4">
                  <h5 className="font-semibold" style={{color:'var(--rv-text-primary)'}}>Изменить режим выплат:</h5>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg transition-all" style={{
                      border: payoutMode === 'auto' ? '2px solid #10b981' : '2px solid var(--rv-border)',
                      background: payoutMode === 'auto' ? 'rgba(16,185,129,0.04)' : 'white',
                    }}>
                      <div className="flex items-center justify-between mb-2">
                        <h6 className="font-semibold" style={{color:'var(--rv-text-primary)'}}>Автоматический</h6>
                        <button
                          onClick={() => updatePayoutMode('auto')}
                          disabled={payoutMode === 'auto' || payoutModeLoading}
                          className="px-3 py-1 rounded text-sm font-medium"
                          style={{
                            background: payoutMode === 'auto' ? 'rgba(16,185,129,0.15)' : '#10b981',
                            color: payoutMode === 'auto' ? '#10b981' : 'white',
                            border: 'none', cursor: payoutMode === 'auto' ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {payoutMode === 'auto' ? 'Активен' : 'Включить'}
                        </button>
                      </div>
                      <p className="text-sm" style={{color:'var(--rv-text-muted)'}}>
                        Выплаты через YuKassa API. Требует статус ИП и настроенный агент выплат.
                      </p>
                      {payoutMode !== 'auto' && (
                        <div className="mt-2 p-2 rounded text-xs flex items-start gap-1" style={{background:'rgba(217,119,6,0.06)', color:'#92400e'}}>
                          <AlertTriangle size={11} className="mt-0.5 flex-shrink-0"/>Убедитесь, что у вас есть статус ИП и настроен агент выплат в YuKassa
                        </div>
                      )}
                    </div>

                    <div className="p-4 rounded-lg transition-all" style={{
                      border: payoutMode === 'manual' ? '2px solid #d97706' : '2px solid var(--rv-border)',
                      background: payoutMode === 'manual' ? 'rgba(217,119,6,0.04)' : 'white',
                    }}>
                      <div className="flex items-center justify-between mb-2">
                        <h6 className="font-semibold" style={{color:'var(--rv-text-primary)'}}>Ручной</h6>
                        <button
                          onClick={() => updatePayoutMode('manual')}
                          disabled={payoutMode === 'manual' || payoutModeLoading}
                          className="px-3 py-1 rounded text-sm font-medium"
                          style={{
                            background: payoutMode === 'manual' ? 'rgba(217,119,6,0.15)' : '#d97706',
                            color: payoutMode === 'manual' ? '#d97706' : 'white',
                            border: 'none', cursor: payoutMode === 'manual' ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {payoutMode === 'manual' ? 'Активен' : 'Включить'}
                        </button>
                      </div>
                      <p className="text-sm" style={{color:'var(--rv-text-muted)'}}>
                        Ручные выплаты через админ панель. Используется как резервный режим.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-4 rounded-lg" style={{background:'rgba(217,119,6,0.06)', border:'1px solid rgba(217,119,6,0.2)'}}>
                  <h5 className="font-semibold mb-2 flex items-center gap-1.5" style={{color:'#92400e'}}><AlertTriangle size={14}/>Важные замечания</h5>
                  <ul className="text-sm space-y-1" style={{color:'#b45309'}}>
                    <li>• Изменение режима влияет только на новые задания</li>
                    <li>• Незавершённые выплаты продолжат обрабатываться в прежнем режиме</li>
                    <li>• Автоматические выплаты доступны только для ИП с подключённым агентом YuKassa</li>
                    <li>• При переходе на ручной режим уведомите исполнителей о возможных задержках</li>
                  </ul>
                </div>

                <div className="flex justify-between">
                  <button
                    onClick={fetchPayoutMode}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-md"
                    style={{background:'var(--rv-teal)', color:'white', border:'none', cursor:'pointer'}}
                  >
                    <RefreshCw size={14}/>Обновить статус
                  </button>
                  <button
                    onClick={() => setActiveTab('payouts')}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-md"
                    style={{background:'rgba(217,119,6,0.08)', color:'#d97706', border:'1px solid rgba(217,119,6,0.2)', cursor:'pointer'}}
                  >
                    <Wallet size={14}/>Перейти к выплатам
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Функция смены паролей удалена - пользователи используют "Забыли пароль" */}

      {/* Task Details Modal */}
      {taskDetailsModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center" style={{zIndex:9000}}>
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl h-[80vh] overflow-y-auto m-4">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold flex items-center gap-2" style={{fontFamily:'Manrope,sans-serif', color:'var(--rv-text-primary)'}}><Eye size={18} style={{color:'var(--rv-teal)'}}/>Детали задания</h2>
              <button
                onClick={closeTaskDetailsModal}
                className="text-xl font-light leading-none"
                style={{color:'var(--rv-text-muted)', background:'none', border:'none', cursor:'pointer'}}
              >
                <XCircle size={20}/>
              </button>
            </div>

            {taskDetailsLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto" style={{borderColor:'var(--rv-teal)'}}></div>
                <p className="mt-2" style={{color:'var(--rv-text-muted)'}}>Загрузка деталей задания...</p>
              </div>
            ) : taskDetailsModal.details ? (
              <div className="space-y-6">
                <div className="p-4 rounded-lg" style={{background:'rgba(13,148,136,0.04)', border:'1px solid rgba(13,148,136,0.1)'}}>
                  <h3 className="text-base font-semibold mb-3 flex items-center gap-2" style={{fontFamily:'Manrope,sans-serif', color:'var(--rv-text-primary)'}}><FileText size={14} style={{color:'var(--rv-teal)'}}/>Информация о задании</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div><strong>Название:</strong> {taskDetailsModal.details.task.title}</div>
                    <div><strong>Статус:</strong> <span className="px-2 py-0.5 rounded text-xs" style={{background:'rgba(13,148,136,0.1)', color:'var(--rv-teal)'}}>{taskDetailsModal.details.task.status}</span></div>
                    <div><strong>Цена:</strong> {taskDetailsModal.details.task.price}₽</div>
                    <div><strong>Тип услуги:</strong> {taskDetailsModal.details.task.service_type}</div>
                    <div><strong>Локация:</strong> {taskDetailsModal.details.task.location}</div>
                    <div><strong>Координаты:</strong> {taskDetailsModal.details.task.latitude}, {taskDetailsModal.details.task.longitude}</div>
                    <div><strong>Создано:</strong> {formatMsk(taskDetailsModal.details.task.created_at)}</div>
                    <div><strong>Дедлайн:</strong> {formatMsk(taskDetailsModal.details.task.deadline)}</div>
                    {taskDetailsModal.details.task.completed_at && (
                      <div><strong>Завершено:</strong> {formatMsk(taskDetailsModal.details.task.completed_at)}</div>
                    )}
                    {taskDetailsModal.details.task.confirmed_at && (
                      <div><strong>Подтверждено:</strong> {formatMsk(taskDetailsModal.details.task.confirmed_at)}</div>
                    )}
                    {taskDetailsModal.details.task.reopened_at && (
                      <div className="md:col-span-2 rounded px-3 py-2" style={{background:'rgba(217,119,6,0.06)', border:'1px solid rgba(217,119,6,0.2)', color:'#92400e'}}>
                        <strong>Переоткрыто:</strong> {formatMsk(taskDetailsModal.details.task.reopened_at)} — задание возвращено в пул после разрешения спора в пользу заказчика
                      </div>
                    )}
                    <div><strong>Файлов результата:</strong> {taskDetailsModal.details.task.result_files_count}</div>
                    <div><strong>Внешних ссылок:</strong> {taskDetailsModal.details.task.external_links?.length || 0}</div>
                  </div>
                  <div className="mt-3">
                    <strong>Описание:</strong>
                    <p className="mt-1 p-2 rounded border text-sm" style={{background:'white', color:'var(--rv-text-primary)', borderColor:'var(--rv-border)'}}>{taskDetailsModal.details.task.description}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 rounded-lg" style={{background:'rgba(16,185,129,0.04)', border:'1px solid rgba(16,185,129,0.15)'}}>
                    <h3 className="text-base font-semibold mb-3 flex items-center gap-2" style={{fontFamily:'Manrope,sans-serif', color:'var(--rv-text-primary)'}}><User size={14} style={{color:'#10b981'}}/>Заказчик</h3>
                    <div className="space-y-2 text-sm">
                      <div><strong>Email:</strong> {taskDetailsModal.details.customer.email}</div>
                      <div><strong>Имя:</strong> {taskDetailsModal.details.customer.name}</div>
                      <div><strong>Роль:</strong> {taskDetailsModal.details.customer.role}</div>
                      {taskDetailsModal.details.customer.created_at && (
                        <div><strong>Регистрация:</strong> {formatMsk(taskDetailsModal.details.customer.created_at)}</div>
                      )}
                    </div>
                  </div>

                  <div className="p-4 rounded-lg" style={{background:'rgba(99,102,241,0.04)', border:'1px solid rgba(99,102,241,0.15)'}}>
                    <h3 className="text-base font-semibold mb-3 flex items-center gap-2" style={{fontFamily:'Manrope,sans-serif', color:'var(--rv-text-primary)'}}><User size={14} style={{color:'#6366f1'}}/>
                      {taskDetailsModal.details.executor.email && taskDetailsModal.details.executor.email !== 'Неизвестен'
                        ? 'Текущий исполнитель' : 'Исполнитель'}
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div><strong>Email:</strong> {taskDetailsModal.details.executor.email}</div>
                      <div><strong>Имя:</strong> {taskDetailsModal.details.executor.name}</div>
                      <div><strong>Роль:</strong> {taskDetailsModal.details.executor.role}</div>
                      {taskDetailsModal.details.executor.created_at && (
                        <div><strong>Регистрация:</strong> {formatMsk(taskDetailsModal.details.executor.created_at)}</div>
                      )}
                    </div>
                  </div>
                </div>

                {taskDetailsModal.details.previous_executor && (
                  <div className="p-4 rounded-lg" style={{background:'rgba(217,119,6,0.04)', border:'1px solid rgba(217,119,6,0.2)'}}>
                    <h3 className="text-base font-semibold mb-3 flex items-center gap-2" style={{fontFamily:'Manrope,sans-serif', color:'var(--rv-text-primary)'}}><RotateCcw size={14} style={{color:'#d97706'}}/>Предыдущий исполнитель (до переоткрытия)</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                      <div><strong>Email:</strong> {taskDetailsModal.details.previous_executor.email || 'Неизвестен'}</div>
                      <div><strong>Имя:</strong> {taskDetailsModal.details.previous_executor.name || 'Неизвестен'}</div>
                      <div className="text-xs" style={{color:'#d97706'}}>Спор разрешён в пользу заказчика. Задание переоткрыто для нового исполнителя.</div>
                    </div>
                  </div>
                )}

                {/* Media Files Section */}
                {taskDetailsModal.details.task.result_files_count > 0 && (
                  <AdminMediaFilesSection 
                    taskId={taskDetailsModal.details.task.id} 
                    fileCount={taskDetailsModal.details.task.result_files_count}
                    showToast={showToast}
                  />
                )}

                {/* Archived Media Files Section (previous executor's files, saved before reopen) */}
                {taskDetailsModal.details.task.archived_media_files_count > 0 && (
                  <AdminArchivedMediaFilesSection
                    taskId={taskDetailsModal.details.task.id}
                    fileCount={taskDetailsModal.details.task.archived_media_files_count}
                    showToast={showToast}
                  />
                )}

                {/* External Links Section */}
                {taskDetailsModal.details.task.external_links && taskDetailsModal.details.task.external_links.length > 0 && (
                  <AdminExternalLinksSection 
                    taskId={taskDetailsModal.details.task.id}
                    links={taskDetailsModal.details.task.external_links}
                    showToast={showToast}
                  />
                )}

                <div className="p-4 rounded-lg" style={{background:'rgba(217,119,6,0.04)', border:'1px solid rgba(217,119,6,0.15)'}}>
                  <h3 className="text-base font-semibold mb-3 flex items-center gap-2" style={{fontFamily:'Manrope,sans-serif', color:'var(--rv-text-primary)'}}><Coins size={14} style={{color:'#d97706'}}/>Основной платёж (эскроу)</h3>
                  {taskDetailsModal.details.payment.found ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div><strong>Сумма:</strong> {taskDetailsModal.details.payment.amount != null ? `${taskDetailsModal.details.payment.amount}₽` : '—'}</div>
                    <div><strong>Статус:</strong> <span className="px-2 py-0.5 rounded text-xs" style={{background:'rgba(217,119,6,0.1)', color:'#d97706'}}>{PAYMENT_STATUS_LABELS[taskDetailsModal.details.payment.status] || taskDetailsModal.details.payment.status || 'неизвестен'}</span></div>
                    {taskDetailsModal.details.payment.yukassa_payment_id && (
                      <div className="md:col-span-2 break-all"><strong>ID платежа ЮKassa:</strong> {taskDetailsModal.details.payment.yukassa_payment_id}</div>
                    )}
                    {taskDetailsModal.details.payment.created_at && (
                      <div><strong>Создан:</strong> {formatMsk(taskDetailsModal.details.payment.created_at)}</div>
                    )}
                    {taskDetailsModal.details.payment.processed_at && (
                      <div><strong>Обработан:</strong> {formatMsk(taskDetailsModal.details.payment.processed_at)}</div>
                    )}
                    {taskDetailsModal.details.payment.processed_by && (
                      <div><strong>Обработал:</strong> {taskDetailsModal.details.payment.processed_by}</div>
                    )}
                    {taskDetailsModal.details.payment.admin_action && (
                      <div><strong>Действие админа:</strong> {taskDetailsModal.details.payment.admin_action}</div>
                    )}
                  </div>
                  ) : (
                    <p className="text-sm" style={{color:'var(--rv-text-muted)'}}>Платёж по заданию не найден (оплата не проводилась или задание от организации).</p>
                  )}
                </div>

                {taskDetailsModal.details.price_changes_history?.length > 0 && (
                  <div className="p-4 rounded-lg" style={{background:'rgba(217,119,6,0.04)', border:'1px solid rgba(217,119,6,0.15)'}}>
                    <h3 className="text-base font-semibold mb-3 flex items-center gap-2" style={{fontFamily:'Manrope,sans-serif', color:'var(--rv-text-primary)'}}><Coins size={14} style={{color:'#d97706'}}/>История изменений цены</h3>
                    <div className="space-y-2">
                      {taskDetailsModal.details.price_changes_history.map((pc, i) => (
                        <div key={i} className="rounded border px-3 py-2 text-sm grid grid-cols-2 md:grid-cols-4 gap-2" style={{background:'white', borderColor:'rgba(217,119,6,0.15)'}}>
                          <div><strong>Было:</strong> {pc.old_price}₽</div>
                          <div><strong>Стало:</strong> {pc.new_price}₽</div>
                          <div><strong>Статус:</strong> <span className="px-1 py-0.5 rounded text-xs" style={{background: pc.status === 'confirmed' ? 'rgba(16,185,129,0.1)' : pc.status === 'rejected' ? 'rgba(239,68,68,0.1)' : 'rgba(107,114,128,0.1)', color: pc.status === 'confirmed' ? '#10b981' : pc.status === 'rejected' ? '#ef4444' : 'var(--rv-text-muted)'}}>{pc.status}</span></div>
                          <div><strong>Оплата:</strong> {pc.payment_status || '—'}</div>
                          {pc.reason && <div className="col-span-2 md:col-span-4" style={{color:'var(--rv-text-muted)'}}><strong>Причина:</strong> {pc.reason}</div>}
                          {pc.created_at && <div className="col-span-2 md:col-span-4 text-xs" style={{color:'var(--rv-text-muted)'}}>{formatMsk(pc.created_at)}</div>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Price Change Payments (Доплаты) */}
                {taskDetailsModal.details.price_change_payments_history?.length > 0 && (
                  <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                    <h3 className="text-lg font-semibold text-yellow-800 mb-3">История доплат</h3>
                    <div className="space-y-2">
                      {taskDetailsModal.details.price_change_payments_history.map((pcp, i) => (
                        <div key={i} className="bg-white rounded border border-yellow-100 px-3 py-2 text-sm grid grid-cols-1 sm:grid-cols-3 gap-1.5">
                          <div><strong>Сумма:</strong> {pcp.amount}&#8381;</div>
                          <div><strong>Тип:</strong> {pcp.type === 'surcharge' ? 'Доплата' : pcp.type === 'refund' ? 'Возврат' : pcp.type}</div>
                          <div><strong>Статус:</strong> <span className={`px-1 py-0.5 rounded text-xs ${pcp.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-gray-100'}`}>{pcp.status}</span></div>
                          {pcp.created_at && <div className="col-span-1 sm:col-span-3 text-gray-400 text-xs">{formatMsk(pcp.created_at)}</div>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Dispute Information */}
                {taskDetailsModal.details.dispute.id && (
                  <div className="bg-red-50 p-4 rounded-lg">
                    <h3 className="text-base font-semibold mb-3 flex items-center gap-2" style={{fontFamily:'Manrope,sans-serif', color:'var(--rv-error)'}}><Scale size={14}/>Информация о споре</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      <div><strong>Причина:</strong> {taskDetailsModal.details.dispute.reason}</div>
                      <div><strong>Статус:</strong> <span className="px-2 py-1 bg-red-100 rounded">{taskDetailsModal.details.dispute.status}</span></div>
                      <div><strong>Файлов доказательств:</strong> {taskDetailsModal.details.dispute.evidence_files_count}</div>
                      <div><strong>Авто-разрешение:</strong> {taskDetailsModal.details.dispute.auto_resolved ? 'Да' : 'Нет'}</div>
                      {taskDetailsModal.details.dispute.created_at && (
                        <div><strong>Создан:</strong> {formatMsk(taskDetailsModal.details.dispute.created_at)}</div>
                      )}
                      {taskDetailsModal.details.dispute.resolution_reason && (
                        <div className="md:col-span-2"><strong>Причина разрешения:</strong> {taskDetailsModal.details.dispute.resolution_reason}</div>
                      )}
                    </div>
                    <div className="mt-3">
                      <strong>Описание спора:</strong>
                      <p className="mt-1 text-gray-700 bg-white p-2 rounded border">{taskDetailsModal.details.dispute.description}</p>
                    </div>
                  </div>
                )}

                {/* Communication Information */}
                <div className="bg-indigo-50 p-4 rounded-lg">
                  <h3 className="text-base font-semibold mb-3 flex items-center gap-2" style={{fontFamily:'Manrope,sans-serif', color:'#6366f1'}}><MessageSquare size={14}/>Общение</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm mb-3">
                    <div><strong>Всего сообщений:</strong> {taskDetailsModal.details.communication.messages_count}</div>
                    {taskDetailsModal.details.communication.last_message_at && (
                      <div><strong>Последнее сообщение:</strong> {formatMsk(taskDetailsModal.details.communication.last_message_at)}</div>
                    )}
                  </div>
                  
                  {taskDetailsModal.details.communication.messages_preview && taskDetailsModal.details.communication.messages_preview.length > 0 && (
                    <div>
                      <strong>Последние сообщения:</strong>
                      <div className="mt-2 space-y-2">
                        {taskDetailsModal.details.communication.messages_preview.map((message, index) => (
                          <div key={index} className="bg-white p-2 rounded" style={{borderLeft:'3px solid #6366f1'}}>
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-xs font-medium" style={{color:'#6366f1'}}>
                                {message.sender_role === 'customer' ? 'Заказчик' : 
                                 message.sender_role === 'admin' ? 'Админ' : 'Исполнитель'}
                              </span>
                              <span className="text-xs" style={{color:'var(--rv-text-muted)'}}>
                                {formatMsk(message.timestamp)}
                              </span>
                            </div>
                            <p className="text-sm" style={{color:'var(--rv-text-primary)'}}>{message.content}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-600">Не удалось загрузить детали задания</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ============== Аудит #35: Вкладка "Комиссии" ============== */}
      {activeTab === 'commissions' && (
        <div className="space-y-5" data-testid="commissions-tab-content">
          {commissionLoading && !commissionSettings ? (
            <div className="text-center py-8 text-gray-500">Загрузка...</div>
          ) : commissionSettings ? (
            <>
              <div className="bg-white p-5 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-1">Базовые ставки комиссии</h3>
                <p className="text-xs text-gray-500 mb-4">
                  Изменения применяются ко всем новым задачам мгновенно. Все изменения логируются.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {SERVICE_TYPE_OPTIONS.map(opt => (
                    <div key={opt.value}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{opt.label}</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="1"
                          max="50"
                          step="0.1"
                          data-testid={`commission-input-${opt.value}`}
                          value={commissionDraft[opt.value] ?? ''}
                          onChange={(e) => setCommissionDraft({ ...commissionDraft, [opt.value]: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        />
                        <span className="text-sm text-gray-500">%</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-5 pt-4 border-t border-gray-100">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Скидка комиссии для "Проверенного исполнителя"
                  </label>
                  <div className="flex items-center gap-2 max-w-xs">
                    <input
                      type="number"
                      min="0"
                      max="20"
                      step="0.1"
                      data-testid="commission-verified-discount-input"
                      value={commissionDiscountDraft}
                      onChange={(e) => setCommissionDiscountDraft(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                    <span className="text-sm text-gray-500">%</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    На столько уменьшается ставка для исполнителей с зелёным бейджем.
                  </p>
                </div>
                <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3">
                  <button
                    data-testid="commission-save-btn"
                    onClick={saveCommissionSettings}
                    disabled={commissionSaving}
                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-medium rounded-md"
                  >
                    {commissionSaving ? 'Сохранение...' : 'Сохранить ставки'}
                  </button>
                  {commissionSettings.updated_at && (
                    <span className="text-xs text-gray-500">
                      Обновлено: {formatMsk(commissionSettings.updated_at)}
                      {commissionSettings.updated_by ? ` (${commissionSettings.updated_by})` : ''}
                    </span>
                  )}
                </div>
              </div>

              <div className="bg-white p-5 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-1">Временные акции</h3>
                <p className="text-xs text-gray-500 mb-4">
                  Снижают комиссию на выбранный период. После окончания ставка возвращается к базовой.
                </p>

                {commissionPromotions.length === 0 ? (
                  <p className="text-sm text-gray-500 mb-4">Активных акций нет.</p>
                ) : (
                  <div className="space-y-2 mb-4">
                    {commissionPromotions.map(p => (
                      <div key={p.id} className="border border-emerald-200 bg-emerald-50/40 rounded-md p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <div>
                          <p className="font-medium text-gray-800">{p.name}</p>
                          <p className="text-xs text-gray-600">
                            {(p.service_types || []).map(st => SERVICE_TYPE_OPTIONS.find(o => o.value === st)?.label || st).join(', ')}
                          </p>
                          <p className="text-xs text-gray-500">
                            {Object.entries(p.rates || {}).map(([k, v]) => `${SERVICE_TYPE_OPTIONS.find(o => o.value === k)?.label || k}: ${(Number(v) * 100).toFixed(1)}%`).join(' · ')}
                          </p>
                          <p className="text-xs text-gray-500">
                            {p.valid_from ? formatMskDate(p.valid_from) : ''} — {p.valid_until ? formatMskDate(p.valid_until) : ''}
                          </p>
                        </div>
                        <button
                          onClick={() => cancelPromotion(p.id)}
                          className="px-3 py-1.5 text-sm bg-red-100 hover:bg-red-200 text-red-700 rounded-md whitespace-nowrap"
                          data-testid={`cancel-promotion-${p.id}`}
                        >
                          Отменить
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="border-t pt-4">
                  <p className="text-sm font-medium text-gray-700 mb-3">Создать акцию</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Название</label>
                      <input
                        type="text"
                        value={newPromotion.name}
                        onChange={(e) => setNewPromotion({ ...newPromotion, name: e.target.value })}
                        placeholder="Например: Зимняя акция"
                        data-testid="promotion-name-input"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Начало</label>
                      <input
                        type="date"
                        value={newPromotion.valid_from}
                        onChange={(e) => setNewPromotion({ ...newPromotion, valid_from: e.target.value })}
                        data-testid="promotion-from-input"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Окончание</label>
                      <input
                        type="date"
                        value={newPromotion.valid_until}
                        onChange={(e) => setNewPromotion({ ...newPromotion, valid_until: e.target.value })}
                        data-testid="promotion-until-input"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Типы услуг и акционные ставки</label>
                      <div className="space-y-2">
                        {SERVICE_TYPE_OPTIONS.map(opt => {
                          const isSelected = newPromotion.service_types.includes(opt.value);
                          return (
                            <div key={opt.value} className="flex items-center gap-2 flex-wrap">
                              <label className="flex items-center gap-2 min-w-[200px]">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={(e) => {
                                    const next = new Set(newPromotion.service_types);
                                    if (e.target.checked) next.add(opt.value); else next.delete(opt.value);
                                    setNewPromotion({ ...newPromotion, service_types: Array.from(next) });
                                  }}
                                  data-testid={`promotion-type-${opt.value}`}
                                />
                                <span className="text-sm">{opt.label}</span>
                              </label>
                              {isSelected && (
                                <div className="flex items-center gap-1">
                                  <input
                                    type="number"
                                    min="1"
                                    max="50"
                                    step="0.1"
                                    placeholder="%"
                                    value={newPromotion.rates[opt.value] || ''}
                                    onChange={(e) => setNewPromotion({
                                      ...newPromotion,
                                      rates: { ...newPromotion.rates, [opt.value]: e.target.value }
                                    })}
                                    className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                                  />
                                  <span className="text-xs text-gray-500">%</span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={createCommissionPromotion}
                    data-testid="create-promotion-btn"
                    className="mt-3 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-md"
                  >
                    Создать акцию
                  </button>
                </div>
              </div>

              <div className="bg-white p-5 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-1">История изменений комиссий</h3>
                <p className="text-xs text-gray-500 mb-4">Неизменяемый журнал — для подтверждения изменений ставок и присвоения бейджей.</p>
                <div className="flex flex-col sm:flex-row gap-2 mb-3">
                  <input
                    type="date"
                    value={auditLogFrom}
                    onChange={(e) => setAuditLogFrom(e.target.value)}
                    data-testid="audit-log-from"
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                  <input
                    type="date"
                    value={auditLogTo}
                    onChange={(e) => setAuditLogTo(e.target.value)}
                    data-testid="audit-log-to"
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                  <button
                    onClick={fetchCommissionAuditLog}
                    data-testid="audit-log-fetch-btn"
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-800 text-white text-sm rounded-md"
                  >
                    Показать
                  </button>
                </div>
                {commissionAuditLog.length === 0 ? (
                  <p className="text-sm text-gray-500">Записей нет.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-gray-500 border-b">
                          <th className="py-2 pr-2">Дата</th>
                          <th className="py-2 pr-2">Действие</th>
                          <th className="py-2 pr-2">Админ</th>
                          <th className="py-2">Подробности</th>
                        </tr>
                      </thead>
                      <tbody>
                        {commissionAuditLog.map(log => (
                          <tr key={log.id} className="border-b border-gray-100">
                            <td className="py-2 pr-2 text-xs text-gray-600 whitespace-nowrap">
                              {log.created_at ? formatMsk(log.created_at) : ''}
                            </td>
                            <td className="py-2 pr-2 text-xs font-medium text-gray-800" data-testid="audit-log-action">{AUDIT_ACTION_LABELS[log.action] || log.action}</td>
                            <td className="py-2 pr-2 text-xs text-gray-600 whitespace-nowrap">{log.admin_email}</td>
                            <td className="py-2 text-xs text-gray-600">
                              <CommissionAuditDetails details={log.details} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <div className="mt-2">
                  <AdminPager tabState={tabState} setPage={setTSPage} setPerPage={setTSPerPage} tab="commissionAudit" total={commissionAuditTotal} />
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-gray-500">Нажмите вкладку повторно для загрузки.</div>
          )}
        </div>
      )}

      {/* ============== Аудит #35: Вкладка "Проверенные исполнители" ============== */}
      {activeTab === 'verified-executors' && (
        <div className="bg-white p-5 rounded-lg shadow-md" data-testid="verified-executors-tab-content">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <h3 className="text-lg font-semibold">Проверенные исполнители</h3>
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="text"
                placeholder="Поиск по имени или email"
                value={verifiedSearch}
                onChange={(e) => setVerifiedSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { setTSPage('verifiedExecutors', 1); fetchVerifiedExecutors(); } }}
                data-testid="verified-search-input"
                className="px-3 py-2 border border-gray-300 rounded-md text-sm w-full sm:w-64"
              />
              <label className="flex items-center gap-1 text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={verifiedOnlyFilter}
                  onChange={(e) => setVerifiedOnlyFilter(e.target.checked)}
                  data-testid="verified-only-filter"
                />
                Только с бейджем
              </label>
              <button
                onClick={() => { setTSPage('verifiedExecutors', 1); fetchVerifiedExecutors(); }}
                data-testid="verified-search-btn"
                className="px-3 py-2 bg-gray-700 hover:bg-gray-800 text-white text-sm rounded-md"
              >
                Найти
              </button>
            </div>
          </div>

          {selectedExecutors.size > 0 && (
            <div className="mb-3 p-3 bg-emerald-50 border border-emerald-200 rounded-md flex flex-col sm:flex-row sm:items-center gap-2">
              <span className="text-sm text-gray-700">Выбрано: <strong>{selectedExecutors.size}</strong></span>
              <input
                type="text"
                value={bulkVerifyNotes}
                onChange={(e) => setBulkVerifyNotes(e.target.value)}
                placeholder="Комментарий (опционально)"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                data-testid="bulk-verify-notes"
              />
              <button
                onClick={bulkVerifyExecutors}
                data-testid="bulk-verify-btn"
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-md whitespace-nowrap"
              >
                Присвоить выбранным
              </button>
            </div>
          )}

          {verifiedExecutorsLoading ? (
            <div className="text-center py-6 text-gray-500">Загрузка...</div>
          ) : (() => {
            const pi = verifiedExecutors;
            const pt = verifiedExecutorsTotal;
            return (
              <>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs text-gray-500">Найдено: {pt}</span>
                  <select
                    value={ts('verifiedExecutors').sort}
                    onChange={e => setTSSort('verifiedExecutors', e.target.value)}
                    className="text-sm border border-gray-300 rounded px-3 py-1.5"
                    data-testid="verified-sort-select"
                  >
                    <option value="name_asc">Имя А-Я</option>
                    <option value="name_desc">Имя Я-А</option>
                    <option value="rating_desc">Рейтинг: высокий</option>
                    <option value="tasks_desc">Больше заданий</option>
                  </select>
                </div>
                {pi.length === 0 ? (
                  <p className="text-sm text-gray-500 py-4">Исполнители не найдены.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-gray-500 border-b">
                          <th className="py-2 w-8"></th>
                          <th className="py-2 pr-2">Имя</th>
                          <th className="py-2 pr-2">Email</th>
                          <th className="py-2 pr-2">Рейтинг</th>
                          <th className="py-2 pr-2">Задач</th>
                          <th className="py-2 pr-2">Бейдж</th>
                          <th className="py-2 pr-2">Действия</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pi.map(u => (
                          <tr key={u.id} className="border-b border-gray-100">
                            <td className="py-2">
                              <input type="checkbox" checked={selectedExecutors.has(u.id)} onChange={() => toggleExecutorSelection(u.id)} data-testid={`select-executor-${u.id}`} />
                            </td>
                            <td className="py-2 pr-2 font-medium text-gray-800">{u.name}</td>
                            <td className="py-2 pr-2 text-gray-600 text-xs">{u.email}</td>
                            <td className="py-2 pr-2 text-gray-600 text-xs">{(u.rating ?? 0).toFixed(1)}</td>
                            <td className="py-2 pr-2 text-gray-600 text-xs">{u.total_tasks ?? 0}</td>
                            <td className="py-2 pr-2 text-xs">
                              {u.is_verified_executor ? <span className="text-emerald-700 font-medium">Да</span> : <span className="text-gray-400">—</span>}
                            </td>
                            <td className="py-2 pr-2">
                              {u.is_verified_executor ? (
                                <button onClick={() => revokeVerifiedExecutor(u.id)} data-testid={`revoke-executor-${u.id}`} className="px-3 py-1 text-xs bg-red-100 hover:bg-red-200 text-red-700 rounded-md">Снять бейдж</button>
                              ) : (
                                <button onClick={() => grantVerifiedExecutor(u.id)} data-testid={`grant-executor-${u.id}`} className="px-3 py-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded-md">Присвоить</button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <AdminPager tabState={tabState} setPage={setTSPage} setPerPage={setTSPerPage} tab="verifiedExecutors" total={pt} />
              </>
            );
          })()}
        </div>
      )}

      {/* Admin Chat Viewer — просмотр переписки задания */}
      {chatView && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b">
              <div>
                <p className="font-semibold text-gray-800 truncate max-w-md">{chatView.taskTitle}</p>
                <p className="text-xs text-gray-400">Просмотр переписки — только чтение</p>
              </div>
              <button
                onClick={() => setChatView(null)}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              >
                &times;
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {chatView.messages.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-8">Сообщений нет</p>
              ) : chatView.messages.map(msg => {
                const isHighlighted = msg.id === chatView.highlightMsgId;
                return (
                  <div
                    key={msg.id}
                    className={`rounded-lg p-3 text-sm ${
                      isHighlighted
                        ? 'bg-orange-100 border-2 border-orange-400'
                        : 'bg-gray-50 border border-gray-200'
                    }`}
                  >
                    <div className="flex justify-between items-baseline mb-1">
                      <span className="font-medium text-gray-700">{msg.sender_name}</span>
                      <span className="text-xs text-gray-400">
                        {formatMsk(msg.created_at)}
                      </span>
                    </div>
                    <p className="text-gray-800">{msg.message}</p>
                    {isHighlighted && (
                      <p className="text-xs text-orange-600 font-medium mt-1">— флагированное сообщение</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Вкладка: Email-рассылка */}
      {activeTab === 'broadcast' && (
        <div className="bg-white p-6 rounded-lg shadow-md" data-testid="broadcast-panel">
          <h3 className="text-lg font-semibold mb-1 flex items-center gap-2" style={{color:'var(--rv-text-primary)', fontFamily:'Manrope,sans-serif'}}>
            <Mail size={18} style={{color:'var(--rv-teal)'}}/>Email-рассылка
          </h3>
          <p className="text-sm mb-5" style={{color:'var(--rv-text-muted)'}}>
            Отправка информационных писем на почту зарегистрированных пользователей. По умолчанию письма получают только те, кто дал согласие на рассылку при регистрации или в настройках профиля.
          </p>

          {/* Аудитория */}
          <div className="mb-5">
            <label className="block text-sm font-medium mb-2" style={{color:'var(--rv-text-primary)'}}>Аудитория</label>
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'all', label: 'Все пользователи' },
                { key: 'customers', label: 'Все заказчики' },
                { key: 'executors', label: 'Все исполнители' },
                { key: 'custom', label: 'Выбрать вручную' },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  data-testid={`broadcast-target-${key}`}
                  onClick={() => setBroadcastForm(f => ({ ...f, target: key }))}
                  className="px-3 py-1.5 rounded-full text-sm font-medium transition-colors"
                  style={{
                    background: broadcastForm.target === key ? 'var(--rv-teal)' : 'white',
                    color: broadcastForm.target === key ? 'white' : 'var(--rv-text-muted)',
                    border: `1px solid ${broadcastForm.target === key ? 'var(--rv-teal)' : 'var(--rv-border)'}`,
                    cursor: 'pointer',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Ручной выбор получателей */}
          {broadcastForm.target === 'custom' && (
            <div className="mb-5 p-4 rounded-lg" style={{border:'1px solid var(--rv-border)', background:'#FAFAF8'}}>
              <label className="block text-sm font-medium mb-2" style={{color:'var(--rv-text-primary)'}}>Поиск пользователей</label>
              <input
                type="text"
                data-testid="broadcast-user-search-input"
                value={broadcastUserSearch}
                onChange={e => searchBroadcastUsers(e.target.value)}
                placeholder="Email или имя (минимум 2 символа)..."
                className="w-full text-sm border rounded px-3 py-2 mb-2"
                style={{borderColor:'var(--rv-border)'}}
              />
              {broadcastUserSearchResults.length > 0 && (
                <div className="mb-3 rounded-lg overflow-hidden" style={{border:'1px solid var(--rv-border)', maxHeight:'220px', overflowY:'auto'}}>
                  {broadcastUserSearchResults.map(u => {
                    const selected = broadcastCustomUsers.some(p => p.id === u.id);
                    const consented = !!u.consents?.marketingEmails;
                    return (
                      <div
                        key={u.id}
                        data-testid={`broadcast-user-result-${u.id}`}
                        onClick={() => toggleBroadcastCustomUser(u)}
                        className="flex items-center justify-between px-3 py-2 text-sm cursor-pointer hover:bg-gray-50"
                        style={{borderBottom:'1px solid var(--rv-border)', background: selected ? 'rgba(13,148,136,0.08)' : 'white'}}
                      >
                        <div>
                          <span className="font-medium">{u.name}</span>
                          <span className="text-xs ml-2" style={{color:'var(--rv-text-muted)'}}>{u.email}</span>
                          {!consented && (
                            <span className="ml-2 px-1.5 py-0.5 rounded text-xs" style={{background:'rgba(217,119,6,0.1)', color:'#d97706'}}>без согласия</span>
                          )}
                        </div>
                        {selected && <CheckCircle2 size={16} style={{color:'var(--rv-teal)'}}/>}
                      </div>
                    );
                  })}
                </div>
              )}
              {broadcastCustomUsers.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {broadcastCustomUsers.map(u => (
                    <span
                      key={u.id}
                      data-testid={`broadcast-selected-chip-${u.id}`}
                      className="flex items-center gap-1 px-2 py-1 rounded-full text-xs"
                      style={{background:'var(--rv-navy)', color:'white'}}
                    >
                      {u.email}
                      <XIcon size={12} className="cursor-pointer" onClick={() => toggleBroadcastCustomUser(u)} data-testid={`broadcast-remove-chip-${u.id}`}/>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Тема и текст */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1" style={{color:'var(--rv-text-primary)'}}>Тема письма</label>
            <input
              type="text"
              data-testid="broadcast-subject-input"
              value={broadcastForm.subject}
              onChange={e => setBroadcastForm(f => ({ ...f, subject: e.target.value }))}
              placeholder="Например: Новые задания уже доступны"
              className="w-full text-sm border rounded px-3 py-2"
              style={{borderColor:'var(--rv-border)'}}
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1" style={{color:'var(--rv-text-primary)'}}>Текст сообщения</label>
            <textarea
              data-testid="broadcast-body-input"
              value={broadcastForm.body_text}
              onChange={e => setBroadcastForm(f => ({ ...f, body_text: e.target.value }))}
              rows={6}
              placeholder="Текст письма..."
              className="w-full text-sm border rounded px-3 py-2"
              style={{borderColor:'var(--rv-border)'}}
            />
          </div>

          {/* Банер */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1" style={{color:'var(--rv-text-primary)'}}>Банер (необязательно)</label>
            {broadcastForm.banner_url ? (
              <div className="flex items-center gap-3">
                <img src={broadcastForm.banner_url} alt="banner" className="h-16 rounded border" style={{borderColor:'var(--rv-border)'}}/>
                <button
                  data-testid="broadcast-remove-banner-btn"
                  onClick={() => setBroadcastForm(f => ({ ...f, banner_url: '' }))}
                  className="text-sm px-3 py-1.5 rounded border"
                  style={{borderColor:'var(--rv-border)', color:'var(--rv-text-muted)'}}
                >
                  Удалить
                </button>
              </div>
            ) : (
              <label
                className="inline-flex items-center gap-2 px-3 py-2 rounded text-sm cursor-pointer"
                style={{border:'1px dashed var(--rv-border)', color:'var(--rv-text-muted)'}}
              >
                <ImageIcon size={16}/>
                {broadcastBannerUploading ? 'Загрузка...' : 'Загрузить изображение'}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  data-testid="broadcast-banner-upload-input"
                  className="hidden"
                  onChange={e => uploadBroadcastBanner(e.target.files?.[0])}
                  disabled={broadcastBannerUploading}
                />
              </label>
            )}
          </div>

          {/* CTA кнопка */}
          <div className="mb-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1" style={{color:'var(--rv-text-primary)'}}>Текст кнопки (необязательно)</label>
              <input
                type="text"
                data-testid="broadcast-cta-text-input"
                value={broadcastForm.cta_text}
                onChange={e => setBroadcastForm(f => ({ ...f, cta_text: e.target.value }))}
                placeholder="Например: Перейти на сайт"
                className="w-full text-sm border rounded px-3 py-2"
                style={{borderColor:'var(--rv-border)'}}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{color:'var(--rv-text-primary)'}}>Ссылка кнопки</label>
              <input
                type="text"
                data-testid="broadcast-cta-url-input"
                value={broadcastForm.cta_url}
                onChange={e => setBroadcastForm(f => ({ ...f, cta_url: e.target.value }))}
                placeholder="https://reversia.ru/..."
                className="w-full text-sm border rounded px-3 py-2"
                style={{borderColor:'var(--rv-border)'}}
              />
            </div>
          </div>

          {/* Игнорировать согласие — предупреждение */}
          <div className="mb-5 p-4 rounded-lg" style={{border:'1px solid #fca5a5', background:'#fef2f2'}}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-2">
                <AlertTriangle size={18} style={{color:'#dc2626', flexShrink:0, marginTop:2}}/>
                <div>
                  <span className="font-semibold text-sm" style={{color:'#991b1b'}}>Игнорировать согласие на рассылку</span>
                  <p className="text-xs mt-1" style={{color:'#b91c1c'}}>
                    Письмо получат ВСЕ пользователи выбранной аудитории, включая тех, кто не давал согласия на информационную рассылку. Используйте только в исключительных случаях.
                  </p>
                </div>
              </div>
              <button
                data-testid="broadcast-toggle-ignore-consent"
                onClick={() => setBroadcastForm(f => ({ ...f, ignore_consent: !f.ignore_consent, ignoreConsentConfirmed: false }))}
                className="px-3 py-1.5 rounded text-sm font-medium flex-shrink-0"
                style={{ background: broadcastForm.ignore_consent ? '#dc2626' : '#e5e7eb', color: broadcastForm.ignore_consent ? 'white' : '#374151', border:'none', cursor:'pointer' }}
              >
                {broadcastForm.ignore_consent ? 'Включено' : 'Выключено'}
              </button>
            </div>
            {broadcastForm.ignore_consent && (
              <label className="flex items-center gap-2 mt-3 text-sm cursor-pointer" style={{color:'#991b1b'}}>
                <input
                  type="checkbox"
                  data-testid="broadcast-ignore-consent-confirm-checkbox"
                  checked={broadcastForm.ignoreConsentConfirmed}
                  onChange={e => setBroadcastForm(f => ({ ...f, ignoreConsentConfirmed: e.target.checked }))}
                />
                Я понимаю риск и подтверждаю отправку без учёта согласия
              </label>
            )}
          </div>

          {/* Счётчик получателей + отправка */}
          <div className="p-4 rounded-lg flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" style={{border:'1px solid var(--rv-border)', background:'rgba(13,148,136,0.06)'}}>
            <div data-testid="broadcast-recipients-count" className="text-sm" style={{color:'var(--rv-text-primary)'}}>
              Будет отправлено: <span className="font-bold">{broadcastPreview.total}</span> адресатов
              <span style={{color:'var(--rv-text-muted)'}}> (заказчиков: {broadcastPreview.customers}, исполнителей: {broadcastPreview.executors})</span>
              {broadcastForm.target === 'custom' && !broadcastForm.ignore_consent && broadcastCustomUsers.length > broadcastPreview.total && (
                <div className="text-xs mt-1" style={{color:'#d97706'}}>
                  Из выбранных {broadcastCustomUsers.length} получат письмо только {broadcastPreview.total} — остальные не давали согласия на рассылку.
                </div>
              )}
            </div>
            <button
              data-testid="broadcast-send-btn"
              onClick={sendBroadcast}
              disabled={broadcastSending || broadcastPreview.total === 0}
              className="px-5 py-2.5 rounded-lg text-sm font-semibold flex items-center gap-2 flex-shrink-0"
              style={{
                background: broadcastSending || broadcastPreview.total === 0 ? '#94a3b8' : 'var(--rv-teal)',
                color: 'white', border: 'none',
                cursor: broadcastSending || broadcastPreview.total === 0 ? 'not-allowed' : 'pointer',
              }}
            >
              <Send size={15}/>{broadcastSending ? 'Отправка...' : 'Отправить рассылку'}
            </button>
          </div>

          {/* История рассылок */}
          <div className="mt-8">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold" style={{color:'var(--rv-text-primary)'}}>История рассылок</h4>
              <button
                data-testid="broadcast-refresh-history-btn"
                onClick={fetchBroadcastHistory}
                className="text-xs hover:underline"
                style={{color:'var(--rv-teal)'}}
              >
                Обновить
              </button>
            </div>
            {broadcastHistoryLoading ? (
              <p className="text-sm" style={{color:'var(--rv-text-muted)'}}>Загрузка...</p>
            ) : broadcastHistory.length === 0 ? (
              <p className="text-sm" style={{color:'var(--rv-text-muted)'}}>Рассылок пока не было</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead style={{background:'#FAFAF8'}}>
                    <tr className="text-left" style={{color:'var(--rv-text-muted)'}}>
                      <th className="px-3 py-2">Тема</th>
                      <th className="px-3 py-2">Аудитория</th>
                      <th className="px-3 py-2">Отправлено / Ошибок</th>
                      <th className="px-3 py-2">Статус</th>
                      <th className="px-3 py-2">Дата</th>
                    </tr>
                  </thead>
                  <tbody>
                    {broadcastHistory.map(c => (
                      <tr key={c.id} className="border-t" data-testid={`broadcast-history-row-${c.id}`} style={{borderColor:'var(--rv-border)'}}>
                        <td className="px-3 py-2 align-top font-medium" style={{color:'var(--rv-text-primary)'}}>{c.subject}</td>
                        <td className="px-3 py-2 align-top" style={{color:'var(--rv-text-muted)'}}>
                          {c.target === 'all' ? 'Все' : c.target === 'customers' ? 'Заказчики' : c.target === 'executors' ? 'Исполнители' : 'Выбранные'}
                          {c.ignore_consent && <span className="ml-1 px-1.5 py-0.5 rounded text-xs" style={{background:'rgba(220,38,38,0.1)', color:'#dc2626'}}>без согласия</span>}
                        </td>
                        <td className="px-3 py-2 align-top">{c.sent_count} / {c.failed_count} <span style={{color:'var(--rv-text-muted)'}}>из {c.total_recipients}</span></td>
                        <td className="px-3 py-2 align-top">
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{
                            background: c.status === 'completed' ? 'rgba(16,185,129,0.1)' : 'rgba(217,119,6,0.1)',
                            color: c.status === 'completed' ? '#10b981' : '#d97706',
                          }}>
                            {c.status === 'completed' ? 'Завершено' : 'Отправляется'}
                          </span>
                        </td>
                        <td className="px-3 py-2 align-top" style={{color:'var(--rv-text-muted)'}}>{c.created_at ? formatMsk(c.created_at) : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Вкладка Аудит #17: Администраторы */}
      {activeTab === 'b2b' && <B2BAdminTab showToast={showToast} />}
      {activeTab === 'leads' && <LeadsAdminTab showToast={showToast} />}

      {activeTab === 'admins' && (
        <div className="bg-white p-6 rounded-lg shadow-md" data-testid="admins-panel">
          <h3 className="text-lg font-semibold text-red-700 mb-1">Управление администраторами</h3>
          <p className="text-sm text-gray-500 mb-5">
            Только вы можете выдавать и отзывать права администратора.
            Вы не можете отозвать собственные права.
          </p>

          {/* Текущие администраторы */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-gray-700">Текущие администраторы</h4>
              <button
                onClick={fetchAdmins}
                className="text-xs text-indigo-600 hover:underline"
                data-testid="refresh-admins-btn"
              >
                Обновить
              </button>
            </div>
            {adminsLoading ? (
              <p className="text-sm text-gray-500">Загрузка...</p>
            ) : adminsList.length === 0 ? (
              <p className="text-sm text-gray-500">Администраторы не найдены</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr className="text-left text-gray-600">
                      <th className="px-3 py-2">Email / Имя</th>
                      <th className="px-3 py-2">Роль</th>
                      <th className="px-3 py-2">Выдал</th>
                      <th className="px-3 py-2">Действие</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminsList.map(a => (
                      <tr key={a.id} className="border-t border-gray-100" data-testid={`admin-row-${a.id}`}>
                        <td className="px-3 py-2">
                          <div className="font-medium text-gray-800">{a.name || '—'}</div>
                          <div className="text-xs text-gray-500">{a.email}</div>
                        </td>
                        <td className="px-3 py-2 text-gray-600 capitalize">{a.role}</td>
                        <td className="px-3 py-2 text-xs text-gray-500">
                          {a.admin_granted_by
                            ? <>{a.admin_granted_by}<br />{a.admin_granted_at ? formatMskDate(a.admin_granted_at) : ''}</>
                            : 'Начальный (env)'
                          }
                        </td>
                        <td className="px-3 py-2">
                          {a.id === user?.id ? (
                            <span className="text-xs text-gray-400 italic">Вы</span>
                          ) : (
                            <button
                              onClick={() => revokeAdmin(a.id)}
                              disabled={adminActionLoading === a.id}
                              className="px-3 py-1 text-xs rounded bg-red-100 text-red-700 hover:bg-red-200 disabled:opacity-50"
                              data-testid={`revoke-admin-btn-${a.id}`}
                            >
                              {adminActionLoading === a.id ? 'Отзыв...' : 'Отозвать права'}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Выдать права новому администратору */}
          <div className="border-t pt-6">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Назначить нового администратора</h4>
            <p className="text-xs text-gray-500 mb-3">
              Найдите пользователя по email или имени. Администратором можно назначить
              пользователя с любой ролью — он сохранит свою роль (заказчик/исполнитель),
              но получит доступ к AdminPanel.
            </p>
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                placeholder="Email или имя пользователя..."
                value={adminGrantSearch}
                onChange={e => setAdminGrantSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && searchUsersForAdmin()}
                className="text-sm border border-gray-300 rounded px-3 py-1.5 flex-1 max-w-xs"
                data-testid="admin-grant-search-input"
              />
              <button
                onClick={searchUsersForAdmin}
                disabled={adminGrantSearching}
                className="px-4 py-1.5 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700 disabled:opacity-50"
                data-testid="admin-grant-search-btn"
              >
                {adminGrantSearching ? 'Поиск...' : 'Найти'}
              </button>
            </div>
            {adminGrantResults.length > 0 && (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr className="text-left text-gray-600">
                      <th className="px-3 py-2">Email / Имя</th>
                      <th className="px-3 py-2">Роль</th>
                      <th className="px-3 py-2">Действие</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminGrantResults.map(u => (
                      <tr key={u.id} className="border-t border-gray-100" data-testid={`admin-candidate-row-${u.id}`}>
                        <td className="px-3 py-2">
                          <div className="font-medium text-gray-800">{u.name || '—'}</div>
                          <div className="text-xs text-gray-500">{u.email}</div>
                        </td>
                        <td className="px-3 py-2 text-gray-600 capitalize">{u.role}</td>
                        <td className="px-3 py-2">
                          <button
                            onClick={() => grantAdmin(u.id)}
                            disabled={adminActionLoading === u.id}
                            className="px-3 py-1 text-xs rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                            data-testid={`grant-admin-btn-${u.id}`}
                          >
                            {adminActionLoading === u.id ? 'Выдача...' : 'Назначить администратором'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {adminGrantResults.length === 0 && adminGrantSearch && !adminGrantSearching && (
              <p className="text-sm text-gray-500">Пользователи не найдены или все уже являются администраторами</p>
            )}
          </div>
        </div>
      )}

    </div>
  );
};
